/**
 * @license
 * Copyright 2026 Aglyn LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import type {
  AglynOrgBilling,
  CoreOrgEntitlements,
  CoreOrgFeatureFlags,
  OrgBrandingProfile,
  OrgDiscount,
  OrgEntitlements,
  OrgFeatureFlags,
  OrgPlan,
  OrgSeatAddons,
} from '../foundation'
import type {
  PluginOrgFeatures,
  PluginOrgQuotas,
} from '../plugin-manager/plugin-entitlement-keys'
import {
  isOrgOverrideReasonCode,
  type OrgOverrideReasonCode,
} from './org-override-reason'
import type { ResolvedBrandingProfile } from './platform-brand'
import {
  PLATFORM_BRAND_NAME,
  PLATFORM_BRANDING_PROFILE,
} from './platform-brand'
import {
  listPluginFeatures,
  listPluginSeatAddons,
  pluginSeatAddon,
} from '../plugin-manager/plugin-entitlements'
import { pluginOrgCapacity } from '../plugin-manager/plugin-org-capacity'
import {
  declaredMeterReading,
  pluginCostAxes,
  pluginCostAxisFields,
} from '../plugin-manager/plugin-usage-axes'
import {
  planQuotaOf,
  pluginPlanFeatureRow,
  pluginPlanQuotaRow,
  pluginPlanQuotas,
} from '../plugin-manager/plugin-plan-entitlements'

/** Sentinel for quotas a plan does not cap; `checkQuota` always allows. */
export const UNLIMITED = Number.POSITIVE_INFINITY

/**
 * How many saved form definitions one site may hold, on every plan that can
 * build them at all.
 *
 * ⛔ **Not a tier lever, and must not become one.** The website-building field
 * does not meter form COUNT: Squarespace, HubSpot and Mailchimp publish no cap
 * whatsoever, Webflow abandoned the lever above its free tier, and of the two
 * that do meter it one is Wix (4/10/25/75) and the other is Jotform
 * (5/25/50/100), a form-first product where a form IS the billable unit. What
 * this platform meters on the forms axis is `formSubmissionsPerMonth`, which
 * is tiered, metered and part of a charged price.
 *
 * So this is an abuse ceiling wearing an entitlement's clothes. It rides
 * `formsPerHost` because that is where `checkQuota` can refuse a create inside
 * the counting transaction, not because the number is sold — and it resolves
 * to the same value on Starter through Enterprise. Only Free differs, and only
 * because the form entity rides `reusableComponents`, which Free lacks.
 *
 * The value clears every published competitor number by 5x and the largest
 * real catalog by far, and it stays STRICTLY below `FORMS_MAX_PER_HOST` — the
 * page size two listing reads use. The gap is deliberate: a catalog can sit
 * above this ceiling when a per-org override is withdrawn, and the two
 * numbers must stay tellable apart so a surface reading the window where it
 * means the ceiling is never accidentally right. `forms.spec.ts` pins both
 * halves.
 *
 * A per-org `entitlements.formsPerHost` override still resolves ahead of this,
 * so a contract can raise or lower it for one org without moving the ceiling
 * everyone else is measured against.
 */
export const FORMS_PER_HOST_CEILING = 500

/**
 * Is this quota value "no cap"? (AGL-2482; AGL-2223 is the same class.)
 *
 * WHY A PREDICATE AND NOT `x === UNLIMITED`. `UNLIMITED` is
 * `Number.POSITIVE_INFINITY`, and `JSON.stringify(Infinity)` is **`null`** —
 * so the moment an entitlement crosses a route boundary the sentinel is gone
 * and `=== UNLIMITED` is false for a plan that really is uncapped. Worse, the
 * client then reads `Number(null)`, which is `0`, and `Number.isFinite(0)` is
 * TRUE: the value sails through every guard written to reject a payload that
 * cannot state the terms, and the console renders a cap of zero for the most
 * expensive plan on the price list.
 *
 * So: `Infinity`, `null`, `undefined` and `NaN` all read as unlimited here.
 *
 * THIS IS A BACKSTOP, NOT THE FIX. `null` on the wire genuinely cannot
 * distinguish "unlimited" from "the field is missing", and answering
 * "unlimited" for a missing field is the permissive direction — the same
 * shape as `checkQuota(undefined)` resolving to Free. A route that serialises
 * a quota must therefore send a FINITE number plus an explicit boolean flag
 * (see `/api/billing/storage-overage`), and the client must rebuild the
 * sentinel from the flag with `restoreQuotaLimit` before it does arithmetic.
 * This predicate exists so that a surface which has not been given the flag
 * still renders "Unlimited" instead of `0`, `null` or `Infinity`.
 */
export function isUnlimitedQuota(limit: number | null | undefined): boolean {
  return limit == null || !Number.isFinite(limit)
}

/**
 * A quota cap, written the way every surface in the console must write it:
 * `Unlimited` for an uncapped plan, the number otherwise.
 *
 * ONE implementation on purpose. Six surfaces had grown their own copy of the
 * `limit === UNLIMITED ? 'Unlimited' : String(limit)` line and a seventh
 * category — the ones that interpolated the raw number — shipped
 * "Every site can run null registers on your current plan" and
 * "Screen limit reached (Infinity)" to customers.
 *
 * @param limit The cap. Non-finite, `null` and `undefined` are unlimited.
 * @param unit Appended to a finite number only — `MB`, `registers`. Never
 *   appended to `Unlimited`, because "Unlimited MB" reads as a typo.
 */
export function formatQuotaLimit(
  limit: number | null | undefined,
  unit?: string,
): string {
  if (isUnlimitedQuota(limit)) return 'Unlimited'
  return unit ? `${limit} ${unit}` : String(limit)
}

/**
 * Rebuild the `UNLIMITED` sentinel a JSON round-trip flattened, from the
 * explicit flag the route sent alongside the number.
 *
 * The client half of the wire contract. Once this has run, every comparison
 * downstream is ordinary arithmetic that happens to be correct: `used >
 * Infinity` is false, `Math.max(0, used - Infinity)` is `0`, and nothing has
 * to remember that this particular number might be a `null` in disguise.
 *
 * That mattered more than the display did. `1 > null` is **true**, so a site
 * on an unlimited plan was reported "1 over the limit" while the readout
 * beside it said `1/∞` — a false limit warning on a plan that has no limit.
 *
 * @param value The finite number the route sent (or the `null` it sent for a
 *   pre-fix payload).
 * @param unlimited The route's explicit flag. `undefined` means the route has
 *   not been taught to send one, and then `value` decides — non-finite reads
 *   as unlimited, which is the `isUnlimitedQuota` backstop.
 */
export function restoreQuotaLimit(
  value: number | null | undefined,
  unlimited?: boolean,
): number {
  if (unlimited === true) return UNLIMITED
  if (unlimited === false) return Number(value)
  return isUnlimitedQuota(value) ? UNLIMITED : Number(value)
}

/**
 * Campaign emails an Enterprise agreement includes per month, before the
 * contract says otherwise.
 *
 * ## Why the one Enterprise quota that is a number
 *
 * Every other Enterprise band is `UNLIMITED` because the thing behind it is
 * infrastructure we already meter and price into the deal — storage, page
 * views, seats. Email is not that. It is a per-message charge from a third
 * party, so an unbounded allowance is an unbounded liability with no meter
 * underneath it and no ceiling a negotiation ever had to name.
 *
 * `UNLIMITED` was also unrepresentable off-process. It is
 * `Number.POSITIVE_INFINITY`, `JSON.stringify(Infinity)` is `null`, and
 * `Number(null)` is `0` — so the most expensive plan on the price list
 * serialised to a cap of ZERO through any route that did not also send the
 * explicit flag `restoreQuotaLimit` rebuilds from. A finite number crosses
 * the wire as itself.
 *
 * ## Why this number
 *
 * Twice Agency's band — the rule every Enterprise fallback follows since the
 * 2026-09-07 pricing decision (see the `enterprise` row of
 * `PLAN_ENTITLEMENTS`) — and inside what the sending platform can actually
 * deliver to one workspace with room to spare. At the shipped constants a
 * workspace's share is 500 messages an hour and a projected month is 720
 * hours, so 360,000 is the ceiling nothing can exceed however it is sold.
 * 260,000 spends 520 of those 720 hours, leaving the rest for bursts, retries
 * and domain warm-up — `email-ceiling-dimensioning.spec.ts` holds that
 * relation, and holds the platform rate still so the gap cannot be closed by
 * quietly raising it.
 *
 * Selling more than that would be selling mail that cannot leave the
 * building: the hourly ceiling DEFERS rather than refuses, so the excess does
 * not bounce, it simply never sends.
 *
 * ## It is a DEFAULT, and a contract raises it
 *
 * `resolveOrgEntitlements` applies a per-org `entitlements.emailSendsPerMonth`
 * override ahead of this, so a deal that buys more email buys it on that org
 * without moving the figure every other agreement is measured against — the
 * same mechanism every numeric entitlement uses.
 */
export const ENTERPRISE_EMAIL_SENDS_PER_MONTH = 260_000

/**
 * Plan → default entitlements. Versioned with the app so pricing changes are
 * code-reviewed; per-org overrides live on `org.entitlements` and win
 * key-by-key. Tier table aligned to the Tenant Billing & SaaS Plans proposal
 * (AGL-67, 2026-07-07): storage-per-host is media storage; the published
 * total-site-size cap it used to exceed by design was retired in AGL-2133.
 * Metered costs are passed through
 * from Firebase/Vercel at cost × 1.30 separately (AGL-41).
 */
/** Legacy host-keyed dataset overrides resolved into org keys (AGL-240). */
type LegacyEntitlementKeys = 'datasetsPerHost' | 'maxDatasetsPerHost'

/**
 * Keys `OrgEntitlements` still carries so stored documents type-check, but
 * that no plan declares and nothing resolves (AGL-2133). Kept as a named type
 * rather than folded into `LegacyEntitlementKeys`: legacy keys are resolved
 * INTO their replacements, retired ones are dropped, and conflating the two
 * would eventually resolve a retired value into something.
 *
 * `RETIRED_ENTITLEMENT_KEYS` is the runtime half and must list the same keys.
 */
type RetiredEntitlementKeys = 'totalSiteSizeMb'

/**
 * Keys stored under `entitlements` that are not quotas at all (AGL-3034): the
 * staff plan comp rides the same staff-owned map, and is read through
 * `readOrgPlanComp` rather than resolved into a limit.
 */
type NonQuotaEntitlementKeys = 'planComp'

/**
 * Fully-resolved entitlements: every LIVE CORE quota present, the core
 * features complete, and whatever the plugins declared beside them.
 *
 * `Required` is over the CORE halves on purpose (AGL-3124). It is what keeps
 * a plan table honest — a plan that forgets a platform quota or a platform
 * gate does not compile — and that reasoning does not carry to a plugin's
 * key: a plugin's figure per plan comes from its own `plan-entitlements`
 * declaration (AGL-3080), whose rows are typed per plan and so cannot forget
 * one either, and requiring it here would mean every plan row in the core
 * naming every installed plugin's keys, which is the arrangement the seam
 * retires. So the plugin halves join as declared: present once a plugin
 * declares them, and optional, because core compiles without the plugin's
 * type and a workspace without the plugin has no value for them.
 *
 * They join in their MAPPED form (`PluginOrgQuotas`, `PluginOrgFeatures`)
 * because an intersection with a bare interface has no implicit string index
 * signature, and readers that hand these objects to a `Record<string, …>`
 * parameter — the quota line, the digest gates — would stop compiling for a
 * plugin half that is empty.
 */
export type ResolvedOrgEntitlements = Required<
  Omit<
    CoreOrgEntitlements,
    | 'features'
    | LegacyEntitlementKeys
    | RetiredEntitlementKeys
    | NonQuotaEntitlementKeys
  >
> &
  PluginOrgQuotas & {
    features: Required<CoreOrgFeatureFlags> & PluginOrgFeatures
  }

export const PLAN_ENTITLEMENTS: Record<OrgPlan, ResolvedOrgEntitlements> = {
  free: {
    hostLimit: 1,
    screensPerHost: 5,
    sharedLayoutsPerHost: 1,
    templatesPerHost: 10,
    storagePerHostMb: 250,
    membersPerHost: 1,
    managersPerOrg: 1,
    maxManagersPerOrg: 1,
    maxMembersPerHost: 1,
    bandwidthGb: 2,
    // No saved-form CATALOG on Free: the form entity rides
    // `reusableComponents`, which is Starter-and-above, so
    // `/api/hosts/resources` refuses the create on the entitlement before it
    // ever reaches this number. Zero is what a Free site actually gets.
    //
    // It does NOT mean a Free site has no forms. A `Form` node placed on a
    // page needs no definition to collect, and the 20 submissions above are
    // the band those replies spend.
    //
    // Every other plan carries `FORMS_PER_HOST_CEILING`. This is the one
    // number on the axis that differs, and it differs because the entitlement
    // gate above it says so, not because the catalog is sold by the tier.
    formsPerHost: 0,
    variablesPerHost: 3,
    functionsPerHost: 1,
    workflowsPerHost: 0,
    servicesPerHost: 0,
    redirectsPerHost: 0,
    contactsPerHost: 100,
    emailSendsPerMonth: 0,
    actionRunsPerMonth: 0,
    apiRequestsPerMonth: 0,
    datasetsPerOrg: 0,
    maxDatasetsPerOrg: 0,
    recordsPerDataset: 0,
    dataStorageMbPerOrg: 0,
    productsPerHost: 0,
    inventoryLocations: 1,
    posRegisters: 0,
    transactionFeePhysicalPct: 0,
    transactionFeeDigitalPct: 0,
    features: {
      abTesting: false,
      versioning: false,
      reusableComponents: false,
      customDomain: false,
      // Sending as a domain the customer owns starts at Pro, with campaign
      // email. Free sends no marketing at all — `emailSendsPerMonth` is 0 —
      // and its transactional mail leaves on the shared pool, which needs no
      // DNS from anybody.
      customSendingDomain: false,
      // The expensive shape, and the one Free must never reach by default: a
      // provider slot, three records in the platform's own zone and a place
      // in the re-verification sweep, spent per site on a tier that pays
      // nothing. The pool carries this tier's mail at no per-site cost.
      dedicatedSendingDomain: false,
      removeBranding: false,
      scheduledPublishing: false,
      // The two AI flags point opposite ways on Free, on purpose (AGL-2925).
      // `aiAssist` is the guided rung of the console assistant — page-aware
      // level-2 answers and the copy assistant — and starts at Starter; Free
      // keeps the docs-grounded level-1 chat it always had. `aiGenerative`
      // is the generative door the taste is FOR: a Free workspace may
      // generate against its 300-credit band, behind the wall and every
      // precaution the constant lists, so the landing page's "generate your
      // first page free" is a thing the product does.
      aiAssist: false,
      aiGenerative: true,
      workflows: false,
      dataStore: false,
      videoMedia: false,
      bookings: false,
      // The CRM starts at Starter (AGL-2611), whole, and Free has none of it
      // (AGL-2851): no Leads, Contacts, Companies, Deals, Tasks, Reports,
      // Fields or Settings, no CRM automation step and no CRM REST resource.
      // What Free's sites capture still counts against its 100-record band,
      // and exporting and erasing those people stay on every plan.
      // The Drive pricing decision of 2026-09-05 records why Starter and
      // not Pro: the field prices a CRM seat at $14–25 a month, so Starter
      // with the suite included is the competitive entry, and gating a
      // tier higher hands the small-business buyer to a free CRM elsewhere.
      crm: false,
      // Sequences (AGL-2974) is on NO plan, this one through Enterprise: which
      // tiers carry sequences and connected mailboxes, and at what caps, is
      // an owner decision still to be made. Until then an organization gets
      // it only through the per-org `entitlements.features.outreach`
      // override, and the Firestore rules read that override alone.
      outreach: false,
      interactions: true,
      actions: false,
      webhooks: false,
      apiAccess: false,
      siteExport: false,
      multilingual: false,
      eventCalendar: false,
      redirects: false,
      screenAnalytics: false,
      /**
       * CDN delivery and responsive image variants, on every plan (AGL-1152).
       *
       * Without this, media is addressed by its absolute
       * `firebasestorage.googleapis.com` URL: a visitor fetches the full-size
       * original from Storage egress on every request, with no shared edge
       * cache in front of it. With it, media is served over the CDN path,
       * which is edge-cacheable and returns a resized WebP variant.
       *
       * The ungated path therefore costs more to run, on both bytes per
       * request and origin requests per byte. Gating this would raise the cost
       * of the tier that pays nothing.
       */
      mediaCdn: true,
      marketingOverlays: false,
      commerce: false,
      pos: false,
      contentGating: false,
      productReviews: false,
      abandonedCart: false,
      dropshipRouting: false,
      whiteLabel: false,
      ssoEnabled: false,
    },
  },
  starter: {
    hostLimit: 1,
    screensPerHost: 25,
    sharedLayoutsPerHost: 3,
    templatesPerHost: 50,
    storagePerHostMb: 2048,
    membersPerHost: 2,
    managersPerOrg: 2,
    maxManagersPerOrg: 5,
    maxMembersPerHost: 10,
    // Sized by the same annual-price invariant as Pro's — see that band.
    bandwidthGb: 15,
    formsPerHost: FORMS_PER_HOST_CEILING,
    variablesPerHost: 25,
    functionsPerHost: 10,
    workflowsPerHost: 3,
    servicesPerHost: 1,
    redirectsPerHost: 25,
    contactsPerHost: 1000,
    // Campaign email starts at Pro. A site that may send campaigns needs its
    // own verified provider sending domain, and provisioning one is a real
    // per-site operational cost that a campaign allowance commits the platform
    // to the moment the tier includes it. Holding the allowance at 0 here
    // keeps that cost attached to the tiers that carry it.
    //
    // Zero is a REFUSAL, not a formality: `reserveCampaignEmailSends` compares
    // `used + count > limit` inside its transaction, so any campaign of one or
    // more recipients is refused before a message leaves.
    //
    // It does NOT stop transactional mail. Receipts, invites, booking
    // reminders, workflow notifications and password resets are refused by no
    // quota at any tier (see `emailSendsPerMonth` on `OrgEntitlements`), so a
    // Starter store still confirms its orders. Those sends still count on the
    // `emailSends` cost meter; they are simply not billable here, because
    // `emailSendsOverage` yields 0 against a non-positive band.
    //
    // The contacts band above is untouched: holding an audience is not sending
    // to it, and the CRM is what a Starter site builds before it upgrades.
    emailSendsPerMonth: 0,
    actionRunsPerMonth: 0,
    apiRequestsPerMonth: 0,
    datasetsPerOrg: 2,
    maxDatasetsPerOrg: 10,
    recordsPerDataset: 1000,
    dataStorageMbPerOrg: 512,
    productsPerHost: 100,
    inventoryLocations: 1,
    posRegisters: 0,
    transactionFeePhysicalPct: 2,
    // Pricing v3 (2026-07): softened 7→5 — a 7% digital fee stacked on the
    // ~2.9%+30¢ processor was ~10% all-in, far above the website-builder
    // norm (Wix/BigCommerce/Squarespace-Core = 0% platform fee). See the
    // Pricing Decision Log + Competitive Benchmark.
    transactionFeeDigitalPct: 5,
    features: {
      abTesting: false,
      versioning: false,
      reusableComponents: true,
      customDomain: true,
      // Starter's allowance is 0 campaign sends, so a verified domain of the
      // customer's own would carry receipts and nothing else — the one class
      // of mail the shared pool already delivers with no DNS work at all. The
      // capability arrives with the sends that need it.
      customSendingDomain: false,
      // Same tier as the customer-owned shape and for the stronger reason:
      // this one draws on a provider allowance that cannot be bought past a
      // certain size, so it is rationed above the tier that carries no
      // campaign sends at all.
      dedicatedSendingDomain: false,
      removeBranding: true,
      scheduledPublishing: false,
      aiAssist: true,
      aiGenerative: false,
      workflows: true,
      dataStore: true,
      videoMedia: false,
      bookings: true,
      // The whole CRM, from the first paid tier (AGL-2611). At 100% of
      // its band and its one-to-one email cap this costs $1.73 a month
      // against a $16 annual price — the Drive pricing decision of
      // 2026-09-05 has the arithmetic — and it is the upgrade motive from
      // Free, not a line on top of a plan.
      crm: true,
      outreach: false,
      interactions: true,
      actions: false,
      webhooks: false,
      apiAccess: false,
      siteExport: false,
      multilingual: false,
      eventCalendar: false,
      redirects: true,
      screenAnalytics: false,
      mediaCdn: true,
      marketingOverlays: true,
      commerce: true,
      pos: false,
      contentGating: false,
      productReviews: false,
      abandonedCart: false,
      dropshipRouting: false,
      whiteLabel: false,
      ssoEnabled: false,
    },
  },
  pro: {
    hostLimit: 3,
    screensPerHost: 100,
    sharedLayoutsPerHost: UNLIMITED,
    templatesPerHost: UNLIMITED,
    storagePerHostMb: 5120,
    membersPerHost: 3,
    managersPerOrg: 3,
    maxManagersPerOrg: 20,
    maxMembersPerHost: 25,
    // Page views are the largest term of this tier's modeled COGS. One GB is
    // 1,035 views at `ESTIMATED_PAGE_TRANSFER_BYTES`, priced where Vercel is
    // dearest because it bills in the region that serves a visitor: $0.413 of
    // weight (`perPageView` — transfer at $0.35 a decimal GB, reads at nam5)
    // and $0.224 of requests once the hosting plan's monthly allowance is
    // spent (`PAGE_VIEW_CDN_REQUEST_COST_USD` — 57 CDN requests at $3.20 per
    // million and the analytics beacon's function). So a GB of included
    // bandwidth costs $0.637, and the bandwidth band is what decides whether
    // the tier survives a customer spending the whole allowance it was sold.
    //
    // The invariant the band is sized against is the ANNUAL price ($39 a
    // month) net of Stripe's fee, with every band at 100% and the CRM seat
    // and one-to-one email terms counted — the 2026-09-07 pricing decision —
    // at that all-in gigabyte (AGL-3444), and it must cover that cost plus
    // 30%, the same markup the metered overages carry (AGL-3469). 25 GB is
    // what it carries beside the storage and campaign bands it now sits
    // with; every paid band on the ladder is sized the same way.
    // `tier-margin-floor.spec.ts` holds the model and pins the figure.
    //
    // `meteredInfraPassThrough` is true here, so traffic past the band BILLS
    // at the page-view pass-through rather than being refused or absorbed.
    bandwidthGb: 25,
    formsPerHost: FORMS_PER_HOST_CEILING,
    variablesPerHost: 100,
    functionsPerHost: 50,
    workflowsPerHost: 25,
    servicesPerHost: UNLIMITED,
    redirectsPerHost: 100,
    contactsPerHost: 10000,
    emailSendsPerMonth: 2500,
    actionRunsPerMonth: 5000,
    apiRequestsPerMonth: 0,
    datasetsPerOrg: 5,
    maxDatasetsPerOrg: 50,
    recordsPerDataset: 10000,
    dataStorageMbPerOrg: 2048,
    productsPerHost: 2500,
    inventoryLocations: 2,
    posRegisters: 1,
    transactionFeePhysicalPct: 0,
    // Pricing v3 (2026-07): softened 5→3 to smooth the digital fee ladder
    // (Starter 5 → Pro 3 → Business 2 → Scale 1 → Advanced/Agency 0).
    transactionFeeDigitalPct: 3,
    features: {
      abTesting: false,
      versioning: true,
      reusableComponents: true,
      customDomain: true,
      // The tier campaign email starts at, and therefore the tier a domain
      // whose reputation belongs to the merchant starts at: marketing may not
      // leave on the shared pool, because one merchant's complaint rate would
      // be charged against every other site's password resets.
      //
      // It costs the platform nothing in our own zone — the customer
      // publishes the records — which is what keeps the platform's domain
      // count flat as the customer base grows. The platform subdomain a site
      // is issued is the expensive half and stays an optimization on top of
      // the pool.
      customSendingDomain: true,
      // The floor for the platform subdomain too, and the tier this one is
      // sized against: a merchant who may send campaigns is a merchant whose
      // mail needs a name of its own, and Pro is where the revenue starts
      // covering a provider slot and three zone records.
      //
      // A flag rather than a tier comparison, so a single org can be granted
      // one through `entitlements.features` without being moved up a plan —
      // the support case this capability meets most often, answered without
      // repricing an account. What the grant does not do is provision: it
      // admits a REQUEST, and the provider ceiling still decides whether the
      // request is filled.
      dedicatedSendingDomain: true,
      removeBranding: true,
      scheduledPublishing: false,
      aiAssist: true,
      aiGenerative: false,
      workflows: true,
      dataStore: true,
      videoMedia: true,
      bookings: true,
      crm: true,
      outreach: false,
      interactions: true,
      actions: true,
      webhooks: false,
      apiAccess: false,
      siteExport: true,
      multilingual: false,
      eventCalendar: false,
      redirects: true,
      screenAnalytics: true,
      mediaCdn: true,
      marketingOverlays: true,
      commerce: true,
      pos: true,
      contentGating: false,
      productReviews: true,
      abandonedCart: true,
      dropshipRouting: true,
      whiteLabel: false,
      ssoEnabled: false,
    },
  },
  business: {
    hostLimit: 10,
    screensPerHost: UNLIMITED,
    sharedLayoutsPerHost: UNLIMITED,
    templatesPerHost: UNLIMITED,
    storagePerHostMb: 10240,
    membersPerHost: 5,
    managersPerOrg: 5,
    maxManagersPerOrg: 100,
    maxMembersPerHost: 100,
    // Sized by the same annual-price invariant as Pro's — see that band.
    bandwidthGb: 45,
    formsPerHost: FORMS_PER_HOST_CEILING,
    variablesPerHost: 1000,
    functionsPerHost: 250,
    workflowsPerHost: 100,
    servicesPerHost: UNLIMITED,
    redirectsPerHost: UNLIMITED,
    contactsPerHost: 30000,
    emailSendsPerMonth: 15000,
    actionRunsPerMonth: 50000,
    apiRequestsPerMonth: 1_500,
    datasetsPerOrg: 10,
    maxDatasetsPerOrg: 250,
    recordsPerDataset: 100000,
    dataStorageMbPerOrg: 10240,
    productsPerHost: 10000,
    inventoryLocations: 4,
    posRegisters: 2,
    transactionFeePhysicalPct: 0,
    transactionFeeDigitalPct: 2,
    features: {
      abTesting: true,
      versioning: true,
      reusableComponents: true,
      customDomain: true,
      customSendingDomain: true,
      dedicatedSendingDomain: true,
      removeBranding: true,
      scheduledPublishing: true,
      aiAssist: true,
      aiGenerative: false,
      workflows: true,
      dataStore: true,
      videoMedia: true,
      bookings: true,
      crm: true,
      outreach: false,
      interactions: true,
      actions: true,
      webhooks: true,
      apiAccess: true,
      siteExport: true,
      multilingual: true,
      eventCalendar: false,
      redirects: true,
      screenAnalytics: true,
      mediaCdn: true,
      marketingOverlays: true,
      commerce: true,
      pos: true,
      contentGating: true,
      productReviews: true,
      abandonedCart: true,
      dropshipRouting: true,
      whiteLabel: false,
      ssoEnabled: false,
    },
  },
  // Scale (Pricing v3, 2026-07): fills the $139→$399 gap. Feature set equals
  // Business/Advanced (all commerce features); limits interpolate between
  // Business and Advanced; digital platform fee drops to 1%.
  scale: {
    hostLimit: 15,
    screensPerHost: UNLIMITED,
    sharedLayoutsPerHost: UNLIMITED,
    templatesPerHost: UNLIMITED,
    storagePerHostMb: 15360,
    membersPerHost: 5,
    managersPerOrg: 5,
    maxManagersPerOrg: 150,
    maxMembersPerHost: 150,
    // Sized by the same annual-price invariant as Pro's — see that band.
    bandwidthGb: 70,
    formsPerHost: FORMS_PER_HOST_CEILING,
    variablesPerHost: 5000,
    functionsPerHost: 500,
    workflowsPerHost: 250,
    servicesPerHost: UNLIMITED,
    redirectsPerHost: UNLIMITED,
    contactsPerHost: 100000,
    emailSendsPerMonth: 25000,
    actionRunsPerMonth: 100000,
    apiRequestsPerMonth: 5_000,
    datasetsPerOrg: 15,
    maxDatasetsPerOrg: 500,
    recordsPerDataset: 500000,
    dataStorageMbPerOrg: 25600,
    productsPerHost: 25000,
    inventoryLocations: 6,
    posRegisters: 3,
    transactionFeePhysicalPct: 0,
    transactionFeeDigitalPct: 1,
    features: {
      abTesting: true,
      versioning: true,
      reusableComponents: true,
      customDomain: true,
      customSendingDomain: true,
      dedicatedSendingDomain: true,
      removeBranding: true,
      scheduledPublishing: true,
      aiAssist: true,
      aiGenerative: false,
      workflows: true,
      dataStore: true,
      videoMedia: true,
      bookings: true,
      crm: true,
      outreach: false,
      interactions: true,
      actions: true,
      webhooks: true,
      apiAccess: true,
      siteExport: true,
      multilingual: true,
      eventCalendar: false,
      redirects: true,
      screenAnalytics: true,
      mediaCdn: true,
      marketingOverlays: true,
      commerce: true,
      pos: true,
      contentGating: true,
      productReviews: true,
      abandonedCart: true,
      dropshipRouting: true,
      whiteLabel: false,
      ssoEnabled: false,
    },
  },
  advanced: {
    hostLimit: 25,
    screensPerHost: UNLIMITED,
    sharedLayoutsPerHost: UNLIMITED,
    templatesPerHost: UNLIMITED,
    storagePerHostMb: 20480,
    membersPerHost: 5,
    managersPerOrg: 10,
    maxManagersPerOrg: 250,
    maxMembersPerHost: 250,
    // Sized by the same annual-price invariant as Pro's — see that band.
    bandwidthGb: 80,
    formsPerHost: FORMS_PER_HOST_CEILING,
    variablesPerHost: UNLIMITED,
    functionsPerHost: 1000,
    workflowsPerHost: 500,
    servicesPerHost: UNLIMITED,
    redirectsPerHost: UNLIMITED,
    contactsPerHost: 150000,
    emailSendsPerMonth: 65000,
    actionRunsPerMonth: 250000,
    apiRequestsPerMonth: 17_000,
    datasetsPerOrg: 25,
    maxDatasetsPerOrg: 1000,
    recordsPerDataset: 1000000,
    dataStorageMbPerOrg: 51200,
    productsPerHost: UNLIMITED,
    inventoryLocations: 10,
    posRegisters: 5,
    transactionFeePhysicalPct: 0,
    transactionFeeDigitalPct: 0,
    features: {
      abTesting: true,
      versioning: true,
      reusableComponents: true,
      customDomain: true,
      customSendingDomain: true,
      dedicatedSendingDomain: true,
      removeBranding: true,
      scheduledPublishing: true,
      aiAssist: true,
      aiGenerative: false,
      workflows: true,
      dataStore: true,
      videoMedia: true,
      bookings: true,
      crm: true,
      outreach: false,
      interactions: true,
      actions: true,
      webhooks: true,
      apiAccess: true,
      siteExport: true,
      multilingual: true,
      eventCalendar: false,
      redirects: true,
      screenAnalytics: true,
      mediaCdn: true,
      marketingOverlays: true,
      commerce: true,
      pos: true,
      contentGating: true,
      productReviews: true,
      abandonedCart: true,
      dropshipRouting: true,
      whiteLabel: false,
      ssoEnabled: false,
    },
  },
  // Agency (Pricing v3, 2026-07): high-volume multi-site tier above Advanced.
  // 100 sites, 0% fees, top-of-ladder capacity. Positioned for agencies /
  // resellers — white-label, SSO, and an SLA are the follow-on product work
  // that will further differentiate it (see the Pricing Decision Log).
  agency: {
    hostLimit: 100,
    screensPerHost: UNLIMITED,
    sharedLayoutsPerHost: UNLIMITED,
    templatesPerHost: UNLIMITED,
    storagePerHostMb: 20480,
    membersPerHost: 5,
    managersPerOrg: 10,
    maxManagersPerOrg: 500,
    maxMembersPerHost: 1000,
    // Sized by the same annual-price invariant as Pro's — see that band.
    bandwidthGb: 395,
    formsPerHost: FORMS_PER_HOST_CEILING,
    variablesPerHost: UNLIMITED,
    functionsPerHost: UNLIMITED,
    workflowsPerHost: UNLIMITED,
    servicesPerHost: UNLIMITED,
    redirectsPerHost: UNLIMITED,
    contactsPerHost: 500000,
    emailSendsPerMonth: 130000,
    actionRunsPerMonth: 1000000,
    apiRequestsPerMonth: 85_000,
    datasetsPerOrg: 50,
    maxDatasetsPerOrg: 5000,
    recordsPerDataset: UNLIMITED,
    dataStorageMbPerOrg: 204800,
    productsPerHost: UNLIMITED,
    inventoryLocations: 50,
    posRegisters: 20,
    transactionFeePhysicalPct: 0,
    transactionFeeDigitalPct: 0,
    features: {
      abTesting: true,
      versioning: true,
      reusableComponents: true,
      customDomain: true,
      customSendingDomain: true,
      dedicatedSendingDomain: true,
      removeBranding: true,
      scheduledPublishing: true,
      aiAssist: true,
      aiGenerative: false,
      workflows: true,
      dataStore: true,
      videoMedia: true,
      bookings: true,
      crm: true,
      outreach: false,
      interactions: true,
      actions: true,
      webhooks: true,
      apiAccess: true,
      siteExport: true,
      multilingual: true,
      eventCalendar: false,
      redirects: true,
      screenAnalytics: true,
      mediaCdn: true,
      marketingOverlays: true,
      commerce: true,
      pos: true,
      contentGating: true,
      productReviews: true,
      abandonedCart: true,
      dropshipRouting: true,
      // White-Label Phase 1: the Agency-tier ($1,299) differentiator. Only
      // this tier and Enterprise ship white-label by default.
      whiteLabel: true,
      ssoEnabled: false,
    },
  },
  // Enterprise (AGL-1118): the top of the ladder and the ONLY custom-priced
  // tier — staff-provisioned per deal (AGL-1110), never sold self-serve, so it
  // is excluded from `SELF_SERVE_PLANS` and carries no list price in
  // `PLAN_PRICING`. White-label AND SSO ship on the plan itself rather than
  // through the per-org `entitlements` override that Enterprise-as-a-label
  // needed.
  //
  // ## Every band here is a FALLBACK, and it is FINITE (2026-09-07)
  //
  // The row used to read UNLIMITED on every axis that costs infrastructure,
  // on the theory that the deal prices the usage. It does not, unless the
  // deal says so: `meteredInfraPassThrough` is false here and every overage
  // rate is the "not for sale" sentinel, so an Enterprise org provisioned
  // without per-org figures was the one org on the platform whose cost had no
  // bound at all — the pricing soundness audit of 2026-09-07 found its worst
  // case unbounded on every axis. A default that a contract must OVERRIDE to
  // become finite is the wrong way round.
  //
  // So the rule is Agency's band × 2 on every axis Agency bounds, and
  // UNLIMITED only where Agency is already UNLIMITED (screens, layouts,
  // templates, variables, functions, workflows, services, redirects, records
  // per dataset, products). `plan-entitlements.spec.ts` holds that relation
  // key by key, so moving an Agency band without moving this one goes red.
  //
  // A contracted per-org `entitlements.*` value still wins on every one of
  // these — `resolveOrgEntitlements` honors a numeric override on every
  // axis, which is the instrument a deal that needs more is written with. A
  // fallback is what an agreement gets before anybody has written it down.
  //
  // These are CAPS, not prices: with no rate and no pass-through the quota
  // gates refuse at the line (`checkContactQuota`, `checkApiRequestQuota`,
  // `checkFormSubmissionQuota`, `mediaStorageGate` …), exactly as Free's do.
  // At twice the largest self-serve tier's bands that line is one a deal
  // reaches only by being written without its numbers. The bandwidth abuse
  // ceiling still flags and pages staff at 3× the band, and does NOT degrade
  // the render on this plan — see `bandwidthCeilingDegradesRender`.
  //
  // `formsPerHost` is the same flat ceiling every plan carries; it bounds a
  // collection, not a tier, and the 2026-08-30 decision withdrew the ladder.
  enterprise: {
    hostLimit: 200,
    screensPerHost: UNLIMITED,
    sharedLayoutsPerHost: UNLIMITED,
    templatesPerHost: UNLIMITED,
    storagePerHostMb: 40_960,
    membersPerHost: 10,
    managersPerOrg: 20,
    maxManagersPerOrg: 1_000,
    maxMembersPerHost: 2_000,
    bandwidthGb: 790,
    formsPerHost: FORMS_PER_HOST_CEILING,
    variablesPerHost: UNLIMITED,
    functionsPerHost: UNLIMITED,
    workflowsPerHost: UNLIMITED,
    servicesPerHost: UNLIMITED,
    redirectsPerHost: UNLIMITED,
    contactsPerHost: 1_000_000,
    emailSendsPerMonth: ENTERPRISE_EMAIL_SENDS_PER_MONTH,
    actionRunsPerMonth: 2_000_000,
    apiRequestsPerMonth: 170_000,
    datasetsPerOrg: 100,
    maxDatasetsPerOrg: 10_000,
    recordsPerDataset: UNLIMITED,
    dataStorageMbPerOrg: 409_600,
    productsPerHost: UNLIMITED,
    inventoryLocations: 100,
    posRegisters: 40,
    transactionFeePhysicalPct: 0,
    transactionFeeDigitalPct: 0,
    features: {
      abTesting: true,
      versioning: true,
      reusableComponents: true,
      customDomain: true,
      customSendingDomain: true,
      dedicatedSendingDomain: true,
      removeBranding: true,
      scheduledPublishing: true,
      aiAssist: true,
      aiGenerative: true,
      workflows: true,
      dataStore: true,
      videoMedia: true,
      bookings: true,
      crm: true,
      outreach: false,
      interactions: true,
      actions: true,
      webhooks: true,
      apiAccess: true,
      siteExport: true,
      multilingual: true,
      // Event Calendar stays an add-on purchase, as on every other tier.
      eventCalendar: false,
      redirects: true,
      screenAnalytics: true,
      mediaCdn: true,
      marketingOverlays: true,
      commerce: true,
      pos: true,
      contentGating: true,
      productReviews: true,
      abandonedCart: true,
      dropshipRouting: true,
      whiteLabel: true,
      // SSO (AGL-1101) is the Enterprise differentiator — the only plan that
      // carries it by default. Lower tiers still need a per-org override.
      ssoEnabled: true,
    },
  },
}

/**
 * Each plan row gains what the plugins declared for that plan (AGL-3080):
 * a plugin's own band or gate — the AI credits, the CRM's one-to-one email
 * pace, the marketplace's take rate — is written beside the reasoning that
 * sized it, in that plugin's `plan-entitlements` module, and compiled into
 * `first-party-plugins.generated.ts`. Filled here, once, as the module
 * loads, so every reader of `PLAN_ENTITLEMENTS` — the pricing tables, the
 * plan comparison, `resolveOrgEntitlements` — reads one table and cannot tell
 * which half of a row a figure came from.
 *
 * Core's keys keep their order and a plugin's follow them. A key both declare
 * keeps CORE's figure, so a declaration can never shadow what the platform
 * already charges; `plan-entitlements.spec.ts` refuses the collision outright.
 */
function fillPluginPlanEntitlements(
  rows: Record<OrgPlan, ResolvedOrgEntitlements>,
): void {
  for (const plan of Object.keys(rows) as OrgPlan[]) {
    const quotas = rows[plan] as unknown as Record<string, unknown>
    for (const [key, value] of Object.entries(pluginPlanQuotaRow(plan))) {
      if (!(key in quotas)) quotas[key] = value
    }
    const features = rows[plan].features as unknown as Record<string, boolean>
    for (const [key, value] of Object.entries(pluginPlanFeatureRow(plan))) {
      if (!(key in features)) features[key] = value
    }
  }
}

fillPluginPlanEntitlements(PLAN_ENTITLEMENTS)

/**
 * The tiers a customer can buy themselves — every plan EXCEPT `enterprise`
 * (AGL-1118), which is priced per deal and provisioned by staff (AGL-1110).
 * Any surface that offers plans for sale (the console plan-cards grid, the
 * marketing pricing table, the plan-change dialog) MUST iterate this rather
 * than `Object.keys(PLAN_ENTITLEMENTS)`, so a custom-priced tier can never
 * show a self-serve Upgrade button or a $0 headline price.
 */
export const SELF_SERVE_PLANS: OrgPlan[] = [
  'free',
  'starter',
  'pro',
  'business',
  'scale',
  'advanced',
  'agency',
]

/**
 * Whether the plan has no list price and is quoted per deal (AGL-1118).
 * `PLAN_PRICING` still carries a row for it — the record is exhaustive by
 * type — but every figure there is 0/null and means "no list price", NOT
 * "free". Revenue for such an org comes from `subscription.customMonthlyUsd`.
 */
export function isCustomPricedPlan(plan: OrgPlan | undefined | null): boolean {
  return plan === 'enterprise'
}

/** Human plan names — the ONE source every plan badge/label reads. */
export const PLAN_LABELS: Record<OrgPlan, string> = {
  free: 'Free',
  starter: 'Starter',
  pro: 'Pro',
  business: 'Business',
  scale: 'Scale',
  advanced: 'Advanced',
  agency: 'Agency',
  enterprise: 'Enterprise',
}

/**
 * Event Calendar add-on (AGL-145): first-party, Aglyn-supported, $9/mo
 * **per organization** — cost×1.3 floor honored (mostly Firestore reads).
 * Enabled via the `eventCalendar` entitlement override at purchase.
 *
 * NOT per host, despite what this comment said until AGL-1279. AGL-145
 * shipped it per host; AGL-526 then made add-ons self-serve and capped this
 * one at `return 1` in `app/api/billing/addons/route.ts`, and
 * `resolveOrgEntitlements` flips a single org-level `features.eventCalendar`
 * boolean from any quantity >= 1. One purchase covers every host in the org.
 * The marketing copy on /pricing is written against that behaviour.
 */
export const EVENT_CALENDAR_ADDON_MONTHLY_USD = 9

/**
 * POS Pro register add-on (AGL-329): $89/mo per extra register/location
 * (Shopify POS Pro parity). Purchased add-ons land as a per-org
 * `posRegisters` entitlement override, which
 * `resolveOrgEntitlements` already applies over the plan default.
 */
export const POS_REGISTER_ADDON_MONTHLY_USD = 89

/**
 * The Aglyn AI add-on's credit band (AGL-2896): the credits a month the
 * add-on ADDS to the plan's `assistCreditsPerMonth` when
 * `seatAddons.aiAddon` is set. One pool and one meter — the add-on widens
 * the band the assist meter already draws on rather than opening a second
 * one, so `resolveAssistCreditBudget`, the overage price and the hard-cap
 * switch all see one number.
 *
 * ## Sized against the add-on's own price
 *
 * A credit is `ASSIST_CREDIT_COST_USD` of provider spend, so each band is a
 * cost figure: 1,000 credits are $1.00. Every band here costs at most 50% of
 * `PlanPricing.aiAddonMonthlyUsd` at full consumption — Starter $4 of $9,
 * Pro $9 of $19, Business $19 of $39, Scale $34 of $69, Advanced $49 of
 * $99, Agency $149 of $299 — which is the same line-margin floor
 * (`ASSIST_CREDIT_MIN_MARGIN_PCT`) every retail assist rate clears.
 * `tier-margin-floor.spec.ts` holds the whole tier non-negative with the
 * add-on band at 100% and the add-on's revenue counted, and pins each band's
 * share of its price.
 *
 * ## Free and Enterprise
 *
 * Free is 0: the plan sells no add-on (`aiAddonMonthlyUsd` is null), so a
 * quantity written onto a Free org adds nothing. Enterprise is finite at
 * twice Agency's, the rule every Enterprise fallback follows since the
 * 2026-09-07 decision (AGL-2654) — never `UNLIMITED`, for the reason
 * `ENTERPRISE_ASSIST_CREDITS_PER_MONTH` gives: an unbounded band reads back
 * as zero off the wire. Enterprise carries `aiGenerative` in the agreement
 * and its price is null too, so this figure is what a contract that names
 * the add-on starts from before its own number is written.
 */
export const AI_ADDON_CREDITS_PER_MONTH: Record<OrgPlan, number> = {
  free: 0,
  starter: 4_000,
  pro: 9_000,
  business: 19_000,
  scale: 34_000,
  advanced: 49_000,
  agency: 149_000,
  enterprise: 298_000,
}

/**
 * The generative add-on's name as a person sees it (AGL-2896): the brand,
 * then "AI". Read from the platform brand by default and from the org's
 * resolved `productName` where the surface has one, for the reason every
 * rendered product name is: a self-host operator renames the product by
 * configuration, and a white-label org must not see ours.
 */
export const aiAddonName = (brand: string = PLATFORM_BRAND_NAME): string =>
  `${brand} AI`

/**
 * Purchase ceilings for the two add-on kinds that have NO per-plan hard max
 * (AGL-1738). They live here, beside the bands and the resolver they bound,
 * because they ARE the bound — and until now they were two private literals
 * in `app/api/billing/addons/route.ts`, seventeen hundred lines and one
 * project away from the `+` they cap.
 *
 * That distance is why AGL-1738 read `resolveOrgEntitlements` as unbounded
 * and proposed a `maxPosRegistersPerHost` clamp. Do not add one. Seats and
 * datasets clamp at `checkSeatQuota` / `checkDatasetQuota` because their
 * plans genuinely refuse to sell past a band (`upgradeRequired` flips and
 * the answer is a plan change). Registers and extra sites are sold flat
 * instead: `addonMax()` will sell an org up to these quantities on ANY plan
 * carrying the feature, so a band-shaped clamp in the resolver would take
 * back registers the merchant is being invoiced $89/mo each for — the
 * expensive direction of a wrong clamp, and the reason the add stays open.
 *
 * The self-serve route is the only path that writes `seatAddons` from a
 * purchase, so these are the real ceilings: a plan's band plus at most
 * `POS_REGISTERS_ADDON_MAX` registers, or plus `EXTRA_HOSTS_ADDON_MAX` sites.
 * Any console surface that windows a head-count must clear the SUM, not the
 * band (`registers-card.component.tsx` still windows at 25; AGL-1738).
 */
export const EXTRA_HOSTS_ADDON_MAX = 100

/** @see EXTRA_HOSTS_ADDON_MAX — the flat register ceiling, per org. */
export const POS_REGISTERS_ADDON_MAX = 50

export interface PlanPricing {
  /** Flat monthly base price in USD (month-to-month billing). */
  basePriceMonthlyUsd: number
  /**
   * Effective per-month price when billed annually (AGL-278): the
   * Squarespace/Shopify-parity headline number. Charged as ×12 up front.
   */
  basePriceAnnualMonthlyUsd: number
  /**
   * Monthly price per host beyond `hostLimit` (AGL-68); null when the plan
   * cannot buy extra hosts.
   *
   * ## It is not a discount ladder, because what it GRANTS is not constant
   *
   * `storagePerHostMb` and `formSubmissionsPerMonth` are per-HOST
   * entitlements, and `meteredIncludedAllowance` expands both by `hostLimit`
   * — so an extra host does not add a slot, it adds that tier's per-host
   * bands to the org's included allowance. A tier that includes more per host
   * therefore hands over more capacity for the same purchase.
   *
   * A ladder that stepped down with the tier inverted that: the tiers
   * granting the most charged the least, and the two largest ran at 24% and
   * 6% margin on a line sold as an add-on.
   *
   * ## It sits above every rung's step rate, or the rungs stop meaning anything
   *
   * The shape is a flat $20 on every plan, Starter included. A flat $8 above
   * Starter, with no cap on how many hosts a plan may add, priced the
   * marginal host below every step of the base ladder, which costs $8.57 to
   * $16 per included host on annual prices and $12 to $22 on monthly: ten
   * Business sites plus fifteen extras came to $219 against Advanced's $299,
   * and plus ninety extras to $819 against Agency's $1,049, so on host count
   * alone no plan was ever worth upgrading to. $20 clears the steepest step on
   * both intervals: extra hosts are how a plan takes a few more sites, and
   * the next plan is how it takes many more (2026-09-20).
   */
  extraHostMonthlyUsd: number | null
  /**
   * Monthly price per org-manager seat beyond `managersPerOrg`
   * (AGL-112); null when the plan cannot buy extra seats.
   */
  extraSeatMonthlyUsd: number | null
  /**
   * Monthly price per site-collaborator seat beyond `membersPerHost`
   * (AGL-112, renamed AGL-888): per-site console collaborators
   * (`hosts/{id}/members`, viewer/editor/admin) — never end-user member
   * accounts, which are unlimited on every plan. Null when the plan sells
   * no extra seats. Persisted/external keys keep the legacy "member"
   * spelling (`seatAddons.members`, `STRIPE_PRICE_*_EXTRA_MEMBER`).
   */
  extraCollaboratorMonthlyUsd: number | null
  /**
   * Monthly price per org dataset beyond `datasetsPerOrg` (AGL-132/240);
   * null when the plan cannot buy extra datasets.
   */
  extraDatasetMonthlyUsd: number | null
  /**
   * Retail overage per GB-month of dataset storage beyond
   * `dataStorageMbPerOrg` (AGL-240); null when the plan hard-blocks at the
   * included size instead of metering.
   *
   * ⛔ **NOT the metered storage pass-through.** `/pricing` publishes two
   * per-GB-month figures and they are different products: the pass-through
   * is `METERED_BILLED_RATES_USD.storagePerGbMonth` at $0.0338, media bytes
   * on GCS at cost + 30%; this is Firestore-backed DATASET bytes, an order
   * of magnitude pricier to store, sold as a retail line. Confusing the two
   * is the easiest way to corrupt a correct price, in either direction.
   *
   * Priced against `ORG_COGS_UNIT_RATES_USD.dataStoragePerGbMonth` ($0.18)
   * at the 50% retail floor. It sat at $0.25 — a 28% line margin, close
   * enough to the pass-through's 23% that the two looked like the same kind
   * of number while being sold as opposites.
   */
  extraDataGbMonthlyUsd: number | null
  /**
   * Metered overage per 1,000 customer REST API requests beyond
   * `apiRequestsPerMonth` (AGL-634). API access starts at Business and every
   * tier above it carries it, so Free, Starter and Pro are null (no API to
   * meter).
   */
  extraApiRequestsUsdPer1k: number | null
  /**
   * Retail overage per 1,000 Aglyn Assist credits beyond
   * `assistCreditsPerMonth`.
   *
   * ## The rate is a multiplier on our own cost, and reads as one
   *
   * A credit is defined as a fixed quantity of provider spend
   * (`ASSIST_CREDIT_COST_USD`), so 1,000 credits cost us exactly $1.00 and
   * this figure IS the multiplier: $3.00 per 1,000 is cost x3, $2.00 is
   * cost x2. That is the property worth having, because the alternative —
   * a rate per message — prices a question and a generated screen the same
   * when they differ by two orders of magnitude.
   *
   * ## The floor, and why the ladder stops descending
   *
   * Every retail line carries at least a 50% margin
   * (`ASSIST_CREDIT_MIN_MARGIN_PCT`), which at $1.00 of cost per 1,000 is a
   * floor of $2.00 per 1,000 — cost x2. The ladder steps down with the tier
   * the way contacts and API requests do, and since AGL-3203 it begins on
   * Starter: $3.00 on Starter and Pro together, then $2.75 -> $2.50 ->
   * $2.25, stopping at the floor on Agency rather than running past it.
   * Starter joins at Pro's figure rather than stepping above it, because the
   * ladder descends with the tier and Starter is the rung below Pro.
   *
   * ## Where it is null, and why that is not "free overage"
   *
   * Null on Free because its band is a WALL by decision (AGL-2925): the
   * 300-credit taste refuses at 100% with nothing sold past it, and a rate
   * here would be a charge on a plan that must never produce one. Null on
   * Enterprise, where every rate is the "not for sale" sentinel and the
   * terms are contractual.
   *
   * Those two are the only nulls. Every self-serve PAID plan now includes a
   * positive assist band and therefore carries a rate, Starter included —
   * a finite band with no rate beside it is usage past a bound that is
   * silently free, which `plan-entitlements.spec.ts` forbids.
   *
   * ## What the rate decides at the gate (AGL-2653)
   *
   * A plan WITH a rate sells past its band by default: `reserveAssistMessage`
   * keeps reserving and `report-usage` bills the excess here, unless the org
   * has switched on `assistOverage.hardCap` to be refused at the band
   * instead. A plan with NO rate has nothing to sell the excess at, so its
   * band stays the wall it always was, switch or no switch — see
   * `assistBandRefuses`. Unlike email, a null here strands nothing: there is
   * no unrefusable assist traffic to absorb.
   */
  extraAssistCreditsUsdPer1k: number | null
  /**
   * The Aglyn AI add-on, flat per organization per month (AGL-2896); null
   * when the plan does not sell it.
   *
   * ## What it buys
   *
   * `features.aiGenerative` — generative building and automation, the rung
   * no self-serve tier includes — plus `AI_ADDON_CREDITS_PER_MONTH[plan]`
   * added to the plan's assist band. It also switches `aiAssist` on, so on
   * Starter it brings the two rungs the tier does not carry — the copy
   * assist and the generative one — on top of the 750 credits the plan
   * already includes (AGL-3203). It ADDS to that band; it does not replace
   * it, on Starter or on any tier.
   *
   * ## Where the ladder sits
   *
   * Priced as an uplift on the plan rather than on its own cost: the field
   * charges roughly 30% of the base price for an AI tier (Zylo's 2025
   * survey puts the AI uplift at 20–37% across SaaS; aissist.io's benchmark
   * and the Framer, Webflow and HubSpot credit add-ons sit in the same
   * band), and the share DESCENDS with the tier — 36% of Starter's $25, 34%
   * of Pro's, 28% of Business's, 28% of Scale's, 25% of Advanced's, 23% of
   * Agency's — because the larger the plan the more of its price is already
   * carrying the bands the assistant builds against.
   *
   * Annual is x12 of this with no discount, as every other add-on is
   * (`orgListPriceMonthlyUsd`). The band it funds costs at most 50% of it in
   * provider spend, the `ASSIST_CREDIT_MIN_MARGIN_PCT` floor every retail
   * assist figure holds.
   *
   * Null on Free, which sells no add-on of any kind, and on Enterprise,
   * where every figure is the "not for sale" sentinel and generative
   * building is in the agreement.
   */
  aiAddonMonthlyUsd: number | null
  /**
   * Metered overage per 1,000 CRM records beyond `contactsPerHost` (AGL-890,
   * widened to contacts + companies + deals in AGL-2611): audience bands,
   * Ghost-style. Paid plans meter (a growing audience is never dropped);
   * free is null and hard-bands at the included count. The key keeps its
   * name because it is a persisted Stripe-side vocabulary and a locked
   * price; the band it prices was widened, the rate was not.
   */
  extraContactsUsdPer1k: number | null
  /**
   * Retail overage per 1,000 emails beyond `emailSendsPerMonth`.
   *
   * ## What it prices, which is not what the cap refuses
   *
   * The cap is enforced against CAMPAIGN sends alone and refuses them at the
   * band — a campaign is discretionary, so the plan is allowed to say no.
   * Transactional mail is never refused at any tier, so an org finishes the
   * month above its band whenever receipts, invites, booking reminders and
   * workflow notifications carry it there. That excess is real provider spend
   * that no gate could have stopped, and this is the rate it bills at.
   *
   * ## Retail, NOT the infrastructure pass-through
   *
   * `meteredInfraPassThrough` covers exactly three meters — storage, page
   * views, form submissions — and the published term for those is "at cost +
   * 30%", which makes the figure in `METERED_BILLED_RATES_USD` the claim
   * itself. Email is not in that set and this rate is not derived from
   * `METERED_MARKUP`: it is a price, set beside `extraContactsUsdPer1k` and
   * `extraApiRequestsUsdPer1k`, and it descends with the tier the way both of
   * those do. Describing it as "at cost" anywhere would make the pass-through
   * sentence false about three meters that really are.
   *
   * Our own per-email cost is `ORG_COGS_UNIT_RATES_USD.perEmailSend`, and it
   * is a cost-model input rather than anything a customer is quoted.
   *
   * ## The floor, and why the ladder stops descending
   *
   * Every retail line must carry at least a 50% margin, and at a cost of
   * $0.90 per 1,000 that floor is $1.80. The ladder still steps down with
   * volume the way contacts and API requests do — a larger plan pays less per
   * message — it simply stops at the floor instead of running past it, which
   * is what the contacts ladder did when it reached $0.25 against a $0.20
   * cost.
   *
   * Null wherever the plan has no email band to be over, and null on
   * enterprise, where every rate is the "not for sale" sentinel and the terms
   * are contractual. Free and Starter are the two banded at 0: campaign email
   * begins at Pro, and `emailSendsOverage` yields 0 against a non-positive
   * band, so a rate on either would advertise a fee that can never be
   * computed — the same defect as a rate beside an UNLIMITED band.
   *
   * Every tier whose band is POSITIVE carries one, Pro upward. A finite band
   * with no rate is silently free past the band, and since transactional mail
   * cannot be refused at any tier that would be unbounded absorbed spend
   * rather than "no overage".
   *
   * The Starter null therefore leaves that tier's transactional mail — the
   * only mail it can send — unbilled at any volume. Bounded in practice by
   * `hostLimit: 1` and a 1,000-contact CRM, and the alternative prices a
   * password reset, but it is the one place this axis absorbs cost by design.
   */
  extraEmailSendsUsdPer1k: number | null
  /**
   * Whether the plan carries the metered infrastructure pass-through
   * (AGL-41, turned on in AGL-1280): media storage, bandwidth (page views)
   * and form submissions BEYOND the plan's included bands, billed at
   * cost × `METERED_MARKUP`. The rates are platform-wide, not per-plan —
   * this flag only says whether a plan is subject to them at all, which is
   * why it is a boolean rather than another `extra*` rate.
   *
   * Free is `false` because there is no subscription to hang a metered item
   * on; its bands stay hard caps, which is also what stops "metered means
   * you're never cut off" from becoming unlimited free capacity.
   *
   * Enterprise is `false` because every band it has is `UNLIMITED` and its
   * price is negotiated (`isCustomPricedPlan`) — there is no published
   * pass-through to apply, and an overage can never arise anyway.
   */
  meteredInfraPassThrough: boolean
}

/**
 * Flat subscription pricing per tier (AGL-68). Kept beside the entitlements
 * so price changes ride the same review path; Stripe price ids map to plans
 * via `STRIPE_PRICE_*` env vars on the billing API routes.
 */
export const PLAN_PRICING: Record<OrgPlan, PlanPricing> = {
  free: {
    basePriceMonthlyUsd: 0,
    basePriceAnnualMonthlyUsd: 0,
    extraHostMonthlyUsd: null,
    extraSeatMonthlyUsd: null,
    extraCollaboratorMonthlyUsd: null,
    extraDatasetMonthlyUsd: null,
    extraDataGbMonthlyUsd: null,
    extraApiRequestsUsdPer1k: null,
    extraAssistCreditsUsdPer1k: null,
    aiAddonMonthlyUsd: null,
    extraContactsUsdPer1k: null,
    extraEmailSendsUsdPer1k: null,
    meteredInfraPassThrough: false,
  },
  starter: {
    basePriceMonthlyUsd: 25,
    basePriceAnnualMonthlyUsd: 16,
    extraHostMonthlyUsd: 20,
    extraSeatMonthlyUsd: 5,
    extraCollaboratorMonthlyUsd: 3,
    extraDatasetMonthlyUsd: 2,
    extraDataGbMonthlyUsd: 0.36,
    extraApiRequestsUsdPer1k: null,
    // The top of the retail ladder, which Starter JOINS rather than steps
    // above (AGL-3203): the ladder descends with the tier, and Starter is the
    // rung below Pro, not a new one over it. Moves in lockstep with the 750
    // credit band on the Starter entitlements — the band without this rate is
    // the silent free overage, and this rate without the band is a fee
    // advertised on a quantity the plan never sold.
    extraAssistCreditsUsdPer1k: 3,
    aiAddonMonthlyUsd: 9,
    extraContactsUsdPer1k: 1,
    // No email band to be "over" — see `emailSendsPerMonth` on the Starter
    // entitlements. `emailSendsOverage` returns 0 for any non-positive band,
    // so a rate here would price volume that can never be computed, which is
    // the same defect as a rate beside an UNLIMITED band.
    extraEmailSendsUsdPer1k: null,
    meteredInfraPassThrough: true,
  },
  pro: {
    basePriceMonthlyUsd: 56,
    basePriceAnnualMonthlyUsd: 39,
    extraHostMonthlyUsd: 20,
    extraSeatMonthlyUsd: 4,
    extraCollaboratorMonthlyUsd: 2,
    extraDatasetMonthlyUsd: 2,
    extraDataGbMonthlyUsd: 0.36,
    extraApiRequestsUsdPer1k: null,
    extraAssistCreditsUsdPer1k: 3,
    aiAddonMonthlyUsd: 19,
    extraContactsUsdPer1k: 0.75,
    extraEmailSendsUsdPer1k: 2.25,
    meteredInfraPassThrough: true,
  },
  business: {
    basePriceMonthlyUsd: 139,
    basePriceAnnualMonthlyUsd: 99,
    extraHostMonthlyUsd: 20,
    extraSeatMonthlyUsd: 3,
    extraCollaboratorMonthlyUsd: 1,
    extraDatasetMonthlyUsd: 1,
    extraDataGbMonthlyUsd: 0.36,
    extraApiRequestsUsdPer1k: 0.5,
    extraAssistCreditsUsdPer1k: 2.75,
    aiAddonMonthlyUsd: 39,
    extraContactsUsdPer1k: 0.5,
    extraEmailSendsUsdPer1k: 2,
    meteredInfraPassThrough: true,
  },
  scale: {
    basePriceMonthlyUsd: 249,
    basePriceAnnualMonthlyUsd: 179,
    extraHostMonthlyUsd: 20,
    extraSeatMonthlyUsd: 2,
    extraCollaboratorMonthlyUsd: 1,
    extraDatasetMonthlyUsd: 1,
    extraDataGbMonthlyUsd: 0.36,
    extraApiRequestsUsdPer1k: 0.35,
    extraAssistCreditsUsdPer1k: 2.5,
    aiAddonMonthlyUsd: 69,
    extraContactsUsdPer1k: 0.4,
    extraEmailSendsUsdPer1k: 1.9,
    meteredInfraPassThrough: true,
  },
  advanced: {
    basePriceMonthlyUsd: 399,
    basePriceAnnualMonthlyUsd: 299,
    extraHostMonthlyUsd: 20,
    extraSeatMonthlyUsd: 2,
    extraCollaboratorMonthlyUsd: 1,
    extraDatasetMonthlyUsd: 1,
    extraDataGbMonthlyUsd: 0.36,
    // FLOORED at the 50% retail margin, with Agency's: a `/v1` request costs
    // $0.117 per 1,000 at the dearest region, so $0.25 is the round figure at
    // or above $0.234 (AGL-3444).
    extraApiRequestsUsdPer1k: 0.25,
    extraAssistCreditsUsdPer1k: 2.25,
    aiAddonMonthlyUsd: 99,
    // FLOORED at the 50% retail margin, not stepped down again. The ladder
    // above it descends $1.00 → $0.75 → $0.50 → $0.40, and one more step
    // would have reached $0.25 against a `perContactMonth` cost of $0.20 per
    // 1,000 — a 20% line margin on a retail price, thinner than the 23% the
    // infrastructure pass-through earns while being sold as cost recovery.
    extraContactsUsdPer1k: 0.4,
    extraEmailSendsUsdPer1k: 1.85,
    meteredInfraPassThrough: true,
  },
  // Agency is the one tier whose PRICE moved. It included 100 hosts, 20 TB of
  // bandwidth and an unbounded form-submission band against $799, which made
  // it both the worst margin on the ladder and the most underpriced against
  // the field: Duda charges ~$1,396-1,493/mo for 100 sites, BigCommerce
  // Enterprise starts at $1,499 and Shopify Plus at $2,300. $1,299 still
  // undercuts all three for the buyer this architecture is built for.
  agency: {
    basePriceMonthlyUsd: 1299,
    // $1,049/mo is $12,588 a year, which is what the live Stripe yearly price
    // charges. Stripe prices are immutable, so the constant every surface
    // quotes has to match the one a customer is actually billed by — a
    // ratio-derived figure that no price object backs would quote one number
    // and charge another. It lands within a dollar of holding the previous
    // 18.8% annual discount across the rise, which is what stops the annual
    // interval becoming the cheap way past a repricing.
    basePriceAnnualMonthlyUsd: 1049,
    extraHostMonthlyUsd: 20,
    extraSeatMonthlyUsd: 2,
    extraCollaboratorMonthlyUsd: 1,
    extraDatasetMonthlyUsd: 1,
    extraDataGbMonthlyUsd: 0.36,
    // FLOORED at the 50% retail margin — see Advanced's.
    extraApiRequestsUsdPer1k: 0.25,
    extraAssistCreditsUsdPer1k: 2,
    aiAddonMonthlyUsd: 299,
    // $0.40, because the band it meters is now FINITE.
    //
    // This row has been wrong in both directions. It shipped $0.20 against an
    // UNLIMITED band, advertising a fee that could not be charged —
    // `checkContactQuota` computes `Math.max(0, used - Infinity)`, which is 0
    // at every usage level (AGL-2439) — and was corrected to `null` to stop
    // publishing a claim the code could not honour. Bounding the band inverts
    // the problem: a finite band with no rate is usage past a bound that is
    // silently free, and the bound achieves nothing.
    //
    // $0.40 rather than a further step down the ladder because that is the
    // 50% retail floor against a `perContactMonth` cost of $0.20 per 1,000 —
    // the same figure Scale and Advanced carry, and the same floor that moved
    // Advanced off $0.25.
    //
    // It METERS rather than walls: a rate is what makes `checkContactQuota`
    // return `allowed: true` past the band, so a growing audience is billed
    // and never dropped. That is what makes bounding this band safe here and
    // NOT safe on enterprise, whose every rate is the "not for sale"
    // sentinel.
    extraContactsUsdPer1k: 0.4,
    extraEmailSendsUsdPer1k: 1.8,
    meteredInfraPassThrough: true,
  },
  // Enterprise (AGL-1118) has NO list price — every figure here is the
  // "not for sale" sentinel, not a $0 offer (`isCustomPricedPlan`). The org's
  // real price is the negotiated `subscription.customMonthlyUsd` the
  // enterprise-billing flow writes (AGL-1110), which `orgListPriceMonthlyUsd`
  // already prefers over the plan default; a comped enterprise org genuinely
  // bills $0, and that is what this row then reports. Nulls also mean the
  // plan sells no self-serve add-on seats — enterprise capacity is unlimited,
  // so there is nothing to buy.
  enterprise: {
    basePriceMonthlyUsd: 0,
    basePriceAnnualMonthlyUsd: 0,
    extraHostMonthlyUsd: null,
    extraSeatMonthlyUsd: null,
    extraCollaboratorMonthlyUsd: null,
    extraDatasetMonthlyUsd: null,
    extraDataGbMonthlyUsd: null,
    extraApiRequestsUsdPer1k: null,
    extraAssistCreditsUsdPer1k: null,
    aiAddonMonthlyUsd: null,
    extraContactsUsdPer1k: null,
    extraEmailSendsUsdPer1k: null,
    meteredInfraPassThrough: false,
  },
}

/**
 * The price list a plan's row becomes when a staff COMP is what grants it
 * (AGL-3034): the list prices kept, and nothing for sale.
 *
 * Every other figure on a row prices something sold on top of a subscription —
 * an add-on item, or usage past a band billed onto that subscription's next
 * invoice. A comp has no subscription to add an item to or to invoice, so
 * every one of them is `null` and the pass-through is off. That is `free`'s
 * reasoning (`meteredInfraPassThrough`: "there is no subscription to hang a
 * metered item on"), and it turns each band into a wall rather than a line
 * past which usage is sold to nobody.
 *
 * It is also what keeps a comp away from Stripe without a check anyone could
 * forget: the AI overage charge claims only what `assistMonthOverage` prices,
 * the usage sweep bills only what these rates price, and a `null` rate prices
 * zero. A comp that needs more of something gets a quota override beside it,
 * or is uncapped outright (AGL-3049) — and an uncapped comp reads THIS row
 * too, so lifting every band sells nothing past any of them.
 *
 * Derived from the row rather than written out, so a rate added to
 * `PlanPricing` later is withheld from a comp on the day it ships.
 */
function compPricing(pricing: PlanPricing): PlanPricing {
  const withheld: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(pricing)) {
    if (key === 'basePriceMonthlyUsd' || key === 'basePriceAnnualMonthlyUsd') {
      withheld[key] = value
    } else if (typeof value === 'boolean') {
      withheld[key] = false
    } else {
      withheld[key] = null
    }
  }
  return withheld as unknown as PlanPricing
}

const PLAN_COMP_PRICING = Object.fromEntries(
  Object.entries(PLAN_PRICING).map(([plan, pricing]) => [plan, compPricing(pricing)]),
) as Record<OrgPlan, PlanPricing>

/**
 * The prices the org's usage and add-ons are sold at (AGL-3034): its
 * effective plan's row, or that row with nothing for sale when a staff comp
 * is what grants the plan — see `compPricing`.
 *
 * EVERY reader that decides whether something past a band is sold, or what a
 * purchasable seat costs, reads this rather than indexing `PLAN_PRICING` by
 * the effective plan. Indexing it directly is how a comp would be quoted a
 * rate nobody can be invoiced at.
 */
export function resolvePlanPricing(
  org: Partial<AglynOrgBilling> | null | undefined,
): PlanPricing {
  const plan = resolveEffectivePlan(org)
  return resolvePlanComp(org) ? PLAN_COMP_PRICING[plan] : PLAN_PRICING[plan]
}

/**
 * Subscription states that stop paying for the plan (AGL-247). `past_due`
 * keeps working as a dunning grace period; these do not.
 *
 * `incomplete_expired` is the state a signup reaches when its first payment
 * never completes — an abandoned SCA challenge, a card that never
 * authenticated — and Stripe stamps it about a day later. It has charged
 * nothing and never will, so it belongs here beside `incomplete`, which is the
 * same subscription an hour earlier. `LIVE_SUBSCRIPTION_STATUSES` in
 * `org-billing-doc.ts` already excludes it and `DEAD_STATUSES` in
 * `utils/subscription-period-notice.ts` already names it; this set is the one
 * that decides both what a workspace GETS and whether we count it as revenue,
 * so it is the one place the omission cost money in both directions at once.
 *
 * THE DENYLIST FORM IS THE HAZARD, and it is why the omission was silent: an
 * allowlist of live statuses cannot rot when Stripe adds a status, whereas a
 * denylist of dead ones silently grants the plan — and books the MRR — for
 * every status nobody has enumerated yet. The three readers below keep the
 * denylist anyway, because the direction they fail in is not the same as the
 * live-side question's: an unrecognized status here must not revoke a paying
 * workspace's features, while `isLiveSubscriptionStatus` refusing to call an
 * unrecognized status live only ever declines to sell a second subscription.
 * A new Stripe status therefore has to be added HERE by hand, which is a
 * standing obligation rather than a property of the code.
 */
const DEAD_SUBSCRIPTION_STATUSES = new Set([
  'canceled',
  'unpaid',
  'incomplete',
  'incomplete_expired',
])

/**
 * The subscription status entitlement resolution runs on (AGL-1028).
 *
 * `subscription` moved to `orgs/{orgId}/billing/stripe`, behind
 * `canManageOrg()` — but the status word is an ENTITLEMENT input, not a
 * commercial secret: `resolveEffectivePlan` downgrades a paid plan to free on a
 * dead subscription, and `resolvePurchasedAddons` stops counting add-ons on
 * one. Both resolve in the tenant runtime and in console components belonging
 * to members who cannot read a manager-gated doc, so the webhook mirrors the
 * bare status string back onto the org doc as `billingStatus`.
 *
 * Reads the mirror first, then the inline `subscription` — the fallback is what
 * keeps entitlements correct for orgs the backfill has not reached yet, and for
 * any caller still passing a whole billing doc rather than an org doc.
 */
function subscriptionStatusOf(
  org: Partial<AglynOrgBilling> | null | undefined,
): string | undefined {
  const mirrored = (org as { billingStatus?: unknown } | null | undefined)
    ?.billingStatus
  if (typeof mirrored === 'string' && mirrored) return mirrored
  return org?.subscription?.status
}

/**
 * What the org's subscription contributes to deciding its plan (AGL-3034).
 *
 * - `live` — a status is mirrored and it is not one this module reads as
 *   dead. The subscription decides the plan. `past_due` is live (dunning
 *   grace), and so is a status this build does not recognize, for the reason
 *   `DEAD_SUBSCRIPTION_STATUSES` gives: an unrecognized status must not take
 *   away what a paying workspace bought, and handing the plan to a comp would
 *   do exactly that.
 * - `dead` — the status is in `DEAD_SUBSCRIPTION_STATUSES`. A stored paid
 *   plan grants nothing (AGL-247).
 * - `none` — no status at all: the workspace has never been subscribed, or
 *   predates the mirror.
 *
 * `dead` and `none` are the only states a staff comp applies in.
 */
export type OrgSubscriptionState = 'live' | 'dead' | 'none'

export function orgSubscriptionState(
  org: Partial<AglynOrgBilling> | null | undefined,
): OrgSubscriptionState {
  const status = subscriptionStatusOf(org)
  if (!status) return 'none'
  return DEAD_SUBSCRIPTION_STATUSES.has(status) ? 'dead' : 'live'
}

/** A staff plan comp, as every reader receives it (AGL-3034). */
export interface ResolvedOrgPlanComp {
  /** The plan granted — always a paid plan this build knows. */
  plan: OrgPlan
  /**
   * Whether the comp lifts every cap (AGL-3049) — see `isUncappedPlanComp`.
   * `false` for anything but a stored `true`.
   */
  uncapped: boolean
  /** `null` when the stored code is not one of the override set's. */
  reason: OrgOverrideReasonCode | null
  note: string | null
  grantedBy: string | null
  /** ISO instant, or `null` when the stored stamp does not read back. */
  grantedAt: string | null
}

/**
 * An instant off a stored value in whichever form it reached this reader: a
 * Firestore `Timestamp` from either SDK, the `{ seconds }`/`{ _seconds }`
 * shape a timestamp takes after crossing JSON, a `Date`, or an ISO string.
 */
function instantOf(value: unknown): string | null {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value.toISOString()
  }
  if (typeof value === 'string') {
    const millis = Date.parse(value)
    return Number.isFinite(millis) ? new Date(millis).toISOString() : null
  }
  if (!value || typeof value !== 'object') return null
  const toDate = (value as { toDate?: unknown }).toDate
  if (typeof toDate === 'function') {
    const date = toDate.call(value)
    return date instanceof Date && !Number.isNaN(date.getTime())
      ? date.toISOString()
      : null
  }
  const record = value as { seconds?: unknown; _seconds?: unknown }
  const seconds = Number(record.seconds ?? record._seconds)
  if (!Number.isFinite(seconds)) return null
  // Out of `Date`'s range is Invalid Date, whose `toISOString` throws.
  const date = new Date(seconds * 1000)
  return Number.isNaN(date.getTime()) ? null : date.toISOString()
}

function trimmedOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null
}

/**
 * The org's STORED comp (`entitlements.planComp`), whether or not it is in
 * force, or `null` when there is none (AGL-3034).
 *
 * Strict about the one field that grants anything: `plan` must be a paid plan
 * this build knows. A comp of `free` grants nothing and an unknown plan could
 * grant anything, so both read as no comp at all — the org then resolves
 * exactly as it would without one, which is the direction a malformed grant
 * must fail in. The descriptive fields are narrowed and never block the plan:
 * a comp whose note or stamp did not read back is still the comp staff wrote.
 *
 * `uncapped` is strict in the same direction (AGL-3049): it lifts every cap,
 * so only a literal `true` does. A `"true"`, a `1` or a map written by hand
 * reads as a capped comp — the plan still stands, and its bands stay walls.
 */
export function readOrgPlanComp(
  org: Partial<AglynOrgBilling> | null | undefined,
): ResolvedOrgPlanComp | null {
  const entitlements = org?.entitlements as { planComp?: unknown } | null | undefined
  const raw =
    entitlements && typeof entitlements === 'object' ? entitlements.planComp : undefined
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const record = raw as Record<string, unknown>
  const plan = record['plan']
  if (
    typeof plan !== 'string' ||
    plan === 'free' ||
    !Object.prototype.hasOwnProperty.call(PLAN_ENTITLEMENTS, plan)
  ) {
    return null
  }
  const reason = record['reason']
  return {
    plan: plan as OrgPlan,
    uncapped: record['uncapped'] === true,
    reason: isOrgOverrideReasonCode(reason) ? reason : null,
    note: trimmedOrNull(record['note']),
    grantedBy: trimmedOrNull(record['grantedBy']),
    grantedAt: instantOf(record['grantedAt']),
  }
}

/**
 * The comp IN FORCE: the stored comp while no subscription decides the plan,
 * and `null` otherwise (AGL-3034).
 *
 * A live subscription always wins. The comp is not removed by one starting —
 * nothing but the staff override writes it — so it waits, dormant, and
 * applies again if that subscription ends. Staff views say so.
 */
export function resolvePlanComp(
  org: Partial<AglynOrgBilling> | null | undefined,
): ResolvedOrgPlanComp | null {
  if (orgSubscriptionState(org) === 'live') return null
  return readOrgPlanComp(org)
}

/**
 * Whether an UNCAPPED comp is in force (AGL-3049): every band and quota the
 * org resolves reads as `UNLIMITED`, whatever its plan row and its per-org
 * overrides say — see `withEveryCapLifted`.
 *
 * In force only, like the comp itself. A live subscription decides the plan
 * and its bands, so a dormant uncapped comp lifts nothing, and a canceled one
 * lifts every cap again the moment the subscription ends.
 *
 * What it does NOT lift is anything that is not a plan band: the fee
 * percentages (`PRICE_ENTITLEMENT_KEYS`) are prices, and the platform's own
 * guards live outside the entitlements map and are not the plan's to give
 * away — the form-submission and bandwidth abuse ceilings (which read an
 * unlimited band as `FORM_ABUSE_CEILING_UNLIMITED` and
 * `BANDWIDTH_ABUSE_CEILING_UNLIMITED`), the flat per-site caps on webhooks,
 * actions and collection entries, the email send pacing, the assist runaway
 * message cap, an operator's explicit spend ceiling, and staff pauses and
 * lockdowns. It never sells anything either: the pricing stays the comp's,
 * with every rate withheld, so an unbounded band is still billed nothing.
 *
 * The lifted value is `UNLIMITED` in process and nowhere else. Every route
 * that sends a band off-process sends a finite number with an explicit flag,
 * or `null` with a documented meaning, because `JSON.stringify(Infinity)` is
 * `null` — and the comp itself is stored and sent as a boolean.
 */
export function isUncappedPlanComp(
  org: Partial<AglynOrgBilling> | null | undefined,
): boolean {
  return resolvePlanComp(org)?.uncapped === true
}

/**
 * What decided the org's effective plan (AGL-3034):
 *
 * - `subscription` — a live subscription; the stored plan is its mirror.
 * - `comp`         — a staff comp, on a dead or absent subscription.
 * - `lapsed`       — a dead subscription and no comp: Free.
 * - `stored-plan`  — no subscription and no comp, and a paid plan stored
 *                    directly, the way staff granted plans before comps.
 * - `no-plan`      — no subscription, no comp, no paid plan: Free.
 */
export type OrgPlanDecidedBy =
  | 'subscription'
  | 'comp'
  | 'lapsed'
  | 'stored-plan'
  | 'no-plan'

/** Everything a staff surface needs to say how an org's plan resolves. */
export interface OrgPlanDescription {
  effectivePlan: OrgPlan
  /** `org.plan` when it names a plan this build knows, else `null`. */
  storedPlan: OrgPlan | null
  subscriptionStatus: string | null
  subscription: OrgSubscriptionState
  /** The STORED comp — in force or dormant; see `compInForce`. */
  comp: ResolvedOrgPlanComp | null
  compInForce: boolean
  /**
   * An uncapped comp is IN FORCE, so every band reads as unlimited
   * (AGL-3049). A dormant uncapped comp is `false` here and `comp.uncapped`.
   */
  uncapped: boolean
  decidedBy: OrgPlanDecidedBy
}

/**
 * How a staff chip names a stored comp (AGL-3049): the plan, then whether it
 * is uncapped and whether a live subscription holds it dormant —
 * `Enterprise (uncapped)`, `Pro (dormant)`, `Agency (uncapped, dormant)`.
 *
 * One reading of the terms, so the orgs list, the org summary and the
 * override dialog cannot describe the same grant differently. `name` is the
 * plan as the surface already writes plans: the label by default, the stored
 * key on the surfaces whose other chips show keys.
 */
export function planCompLabel(
  comp: Pick<ResolvedOrgPlanComp, 'plan' | 'uncapped'>,
  inForce: boolean,
  name: string = PLAN_LABELS[comp.plan],
): string {
  const terms = [comp.uncapped ? 'uncapped' : null, inForce ? null : 'dormant']
    .filter(Boolean)
    .join(', ')
  return terms ? `${name} (${terms})` : name
}

/**
 * A stored comp as a sentence names it (AGL-3049): `uncapped Enterprise
 * comp`, `Pro comp`. Shared by the plan description and the override route's
 * account of what it changed.
 */
export function planCompPhrase(
  comp: Pick<ResolvedOrgPlanComp, 'plan' | 'uncapped'>,
): string {
  return `${comp.uncapped ? 'uncapped ' : ''}${PLAN_LABELS[comp.plan]} comp`
}

/** "A Pro comp", "An uncapped Enterprise comp": the article a phrase takes. */
function withArticle(phrase: string): string {
  return `${/^[aeiou]/i.test(phrase) ? 'An' : 'A'} ${phrase}`
}

/**
 * The org's plan, described rather than only resolved (AGL-3034) — the ONE
 * reading the staff override route answers with and the staff override
 * dialog renders, so the response and the dialog cannot tell staff two
 * different stories about the same document.
 */
export function describeOrgPlan(
  org: Partial<AglynOrgBilling> | null | undefined,
): OrgPlanDescription {
  const subscription = orgSubscriptionState(org)
  const status = subscriptionStatusOf(org)
  const stored = org?.plan
  const storedPlan =
    typeof stored === 'string' &&
    Object.prototype.hasOwnProperty.call(PLAN_ENTITLEMENTS, stored)
      ? (stored as OrgPlan)
      : null
  const comp = readOrgPlanComp(org)
  const compInForce = comp !== null && subscription !== 'live'
  const decidedBy: OrgPlanDecidedBy =
    subscription === 'live'
      ? 'subscription'
      : compInForce
        ? 'comp'
        : subscription === 'dead'
          ? 'lapsed'
          : storedPlan && storedPlan !== 'free'
            ? 'stored-plan'
            : 'no-plan'
  return {
    effectivePlan: resolveEffectivePlan(org),
    storedPlan,
    subscriptionStatus: typeof status === 'string' && status ? status : null,
    subscription,
    comp,
    compInForce,
    uncapped: compInForce && comp?.uncapped === true,
    decidedBy,
  }
}

/**
 * One sentence for staff saying what decides the plan and what a plan set in
 * the override dialog would do (AGL-3034). Shared by the route's response and
 * the dialog, for the reason `describeOrgPlan` is.
 *
 * A comp in force also says whether its bands are walls or lifted
 * (AGL-3049), because that is the question a comped workspace refused at a
 * band brings to staff.
 */
export function orgPlanDescriptionSentence(
  description: OrgPlanDescription,
): string {
  const label = (plan: OrgPlan | null) => (plan ? PLAN_LABELS[plan] : 'no plan')
  const status = description.subscriptionStatus ?? 'none'
  const dormant =
    description.comp && !description.compInForce
      ? ` ${withArticle(planCompPhrase(description.comp))} is stored and ` +
        'dormant: it takes effect if the subscription ends.'
      : ''
  switch (description.decidedBy) {
    case 'subscription':
      return (
        `The live subscription (${status}) decides the plan: ` +
        `${label(description.effectivePlan)}. Stripe's next subscription ` +
        `event rewrites a plan set here.${dormant}`
      )
    case 'comp':
      return (
        `${label(description.effectivePlan)} is in force as ` +
        `${description.uncapped ? 'an uncapped' : 'a'} staff comp, ` +
        (description.subscription === 'dead'
          ? `because the subscription is ${status}. `
          : 'because no subscription pays for this workspace. ') +
        (description.uncapped
          ? 'It bills nothing, every band and quota reads as unlimited, '
          : 'It bills nothing, every band is a hard limit that a quota ' +
            'override can raise, ') +
        'and it lasts until it is removed or a live subscription starts.'
      )
    case 'lapsed':
      return (
        `The subscription is ${status}, so this workspace resolves as ` +
        `${label('free')}` +
        (description.storedPlan && description.storedPlan !== 'free'
          ? ` — the stored ${label(description.storedPlan)} grants nothing`
          : '') +
        '. A plan set here is written as a staff comp.'
      )
    case 'stored-plan':
      return (
        `No subscription: the plan stored directly, ` +
        `${label(description.storedPlan)}, decides it. A plan set here is ` +
        'written as a staff comp.'
      )
    default:
      return (
        `No subscription and no paid plan, so this workspace resolves as ` +
        `${label('free')}. A plan set here is written as a staff comp.`
      )
  }
}

/**
 * Whether the org is actually being charged for its plan right now.
 *
 * This is deliberately stricter than `resolveEffectivePlan`: entitlement
 * asks "what does this org get", revenue asks "who is paying us". A staff
 * plan override (`/admin/orgs`) never writes `subscription` — before AGL-3034
 * it wrote `plan`, and now it writes a comp — so a comped or dark-launched
 * org resolves to a paid plan while billing nothing, and counting it inflates
 * MRR (AGL-925). No subscription mirror means no Stripe subscription means no
 * revenue.
 *
 * A comp never reaches this answer: it reads the stored `plan` and the
 * status, never `entitlements.planComp`, and a comp is only ever in force
 * where the status is dead or absent — both of which answer false here.
 *
 * `past_due` counts: Stripe is still retrying and the plan is still owed.
 */
export function isBillingSubscription(
  org: Partial<AglynOrgBilling> | null | undefined,
): boolean {
  const plan = org?.plan
  if (!plan || plan === 'free' || !(plan in PLAN_ENTITLEMENTS)) return false
  const status = subscriptionStatusOf(org)
  if (!status) return false
  return !DEAD_SUBSCRIPTION_STATUSES.has(status)
}

/**
 * The org's monthly LIST price in USD — plan base plus every purchased add-on
 * (manager/collaborator seats, datasets, extra hosts, POS registers, Event
 * Calendar), BEFORE any applied discount, and 0 for anyone not actually
 * billing. This is the sticker price the plan + add-ons would bill at.
 *
 * Annual subscriptions contribute their per-month equivalent
 * (`basePriceAnnualMonthlyUsd`), not the month-to-month price, so a mixed
 * book does not read high by the size of the annual discount. Add-ons bill
 * per-month (annual variants are ×12 with no discount), so each contributes
 * its monthly price on either interval.
 *
 * Pricing-v3 fix (2026-07): previously omitted the host, POS-register, and
 * Event-Calendar add-ons, understating the total for every org carrying them.
 *
 * AGL-1105: split out of `orgMonthlyRevenueUsd`, which now subtracts an
 * applied per-org discount on top of this. Callers wanting the pre-discount
 * sticker (the margin guardrail, a "what would this bill at" quote) use this;
 * callers wanting the actual money collected use `orgMonthlyRevenueUsd`.
 */
export function orgListPriceMonthlyUsd(
  org: Partial<AglynOrgBilling> | null | undefined,
): number {
  if (!isBillingSubscription(org)) return 0
  // Custom enterprise price (AGL-1110): a negotiated amount is the list price
  // outright — it already reflects the whole deal, so it overrides the plan
  // default AND the seat/host add-on math (those are folded into the quote).
  // Stored monthly-normalized, so it is used as-is.
  const custom = org?.subscription?.customMonthlyUsd
  if (typeof custom === 'number' && custom > 0) {
    return Math.round(custom * 100) / 100
  }
  const pricing = PLAN_PRICING[org?.plan as OrgPlan]
  if (!pricing) return 0
  const annual = org?.subscription?.interval === 'year'
  const addons = org?.seatAddons ?? {}
  const seats = (quantity: number | undefined, price: number | null) =>
    Math.max(0, quantity ?? 0) * (price ?? 0)
  return (
    (annual ? pricing.basePriceAnnualMonthlyUsd : pricing.basePriceMonthlyUsd) +
    seats(addons.managers, pricing.extraSeatMonthlyUsd) +
    seats(addons.members, pricing.extraCollaboratorMonthlyUsd) +
    seats(addons.datasets, pricing.extraDatasetMonthlyUsd) +
    seats(addons.hosts, pricing.extraHostMonthlyUsd) +
    seats(addons.posRegisters, POS_REGISTER_ADDON_MONTHLY_USD) +
    seats(addons.eventCalendar, EVENT_CALENDAR_ADDON_MONTHLY_USD) +
    // A toggle, not a quantity: one purchase covers the org, so a stored
    // quantity above one bills once.
    seats(aiAddonUnits(addons), pricing.aiAddonMonthlyUsd)
  )
}

/**
 * How many Aglyn AI add-ons a purchase map counts as: 0 or 1 (AGL-2896).
 * The add-on is org-wide like Event Calendar, so a quantity above one — a
 * hand edit, a doubled webhook item — is one purchase, and a non-number,
 * `NaN` or a negative is none.
 */
export function aiAddonUnits(addons: OrgSeatAddons | null | undefined): 0 | 1 {
  const quantity = Number(addons?.aiAddon ?? 0)
  return Number.isFinite(quantity) && quantity >= 1 ? 1 : 0
}

/**
 * A list price net of a discount (AGL-1105). Percentage discounts scale the
 * whole bill; fixed discounts subtract a flat USD amount. Never below 0, and
 * a percentage is clamped to 0–100. Prefers `percentOff` when both are
 * somehow set (Stripe coupons carry only one). Rounds to whole cents.
 */
export function applyDiscountUsd(
  listPriceUsd: number,
  discount:
    | Pick<OrgDiscount, 'percentOff' | 'amountOffUsd'>
    | null
    | undefined,
): number {
  if (!(listPriceUsd > 0) || !discount) return Math.max(0, listPriceUsd)
  if (typeof discount.percentOff === 'number') {
    const pct = Math.min(100, Math.max(0, discount.percentOff))
    return Math.round(listPriceUsd * (1 - pct / 100) * 100) / 100
  }
  if (typeof discount.amountOffUsd === 'number') {
    return Math.max(0, Math.round((listPriceUsd - discount.amountOffUsd) * 100) / 100)
  }
  return listPriceUsd
}

/**
 * Whether an org should read as **"Enterprise"** everywhere the plan is shown.
 * Three ways to qualify:
 *  - **`plan === 'enterprise'`** — the real plan (AGL-1118). This is the path
 *    every new enterprise org takes, and the only one that also grants
 *    Enterprise *entitlements*; the two below are display-only overlays on a
 *    lower base plan;
 *  - a billing org on a **negotiated custom price** (`customMonthlyUsd > 0`) —
 *    a paying enterprise deal still parked on its pre-AGL-1118 base plan; or
 *  - an explicit **`org.enterprise`** marker — a *comped* enterprise account,
 *    100%-discounted so it collects $0 while infra cost is still metered.
 *
 * ⚠️ This function answers ONE question — "does this org read as Enterprise" —
 * and it is not the same question as "what does this org get". Only the first
 * bullet grants anything: `resolveEffectivePlan` reads `org.plan` and nothing
 * else, so `org.enterprise` and `customMonthlyUsd` are read NOWHERE but here.
 * A comped or custom-priced org resolves its BASE plan's entitlements, and
 * gets Enterprise capability through a per-org `entitlements` override — see
 * `ssoEnabled`, whose docblock calls that "how enterprise orgs provisioned
 * before that plan existed still get it".
 *
 * This paragraph replaces a parenthetical claiming the comped marker carried
 * "full Enterprise capability + SSO" (AGL-2297). It never did, and the billing
 * page believed it: the Enterprise card ticked five entitlements against any
 * org this function marked, so a comped org whose overrides granted SSO but
 * not white-label was shown a green tick for full white-label while the
 * branding card correctly refused it.
 *
 * The last two are kept for orgs provisioned before `enterprise` was a plan;
 * migrating such an org to `plan: 'enterprise'` is the clean end state and
 * changes nothing about how it reads here. List-priced orgs with none of the
 * three are not enterprise.
 *
 * A staff comp in force names the plan instead of the stored one (AGL-3034),
 * exactly as it does for entitlements: a workspace comped to Enterprise reads
 * as Enterprise, and one comped to a lower tier does not read as Enterprise
 * because of a plan stored before the comp. The explicit marker still counts.
 */
export function isEnterpriseOrg(
  org: Partial<AglynOrgBilling> | null | undefined,
): boolean {
  const comp = resolvePlanComp(org)
  if (comp) return comp.plan === 'enterprise' || org?.enterprise === true
  if (org?.plan === 'enterprise') return true
  if (org?.enterprise === true) return true
  return (
    isBillingSubscription(org) &&
    (org?.subscription?.customMonthlyUsd ?? 0) > 0
  )
}

/**
 * Human label shown for `enterprise` in plan badges. Kept as a named export
 * for the surfaces that label a custom-priced org whose stored `plan` is still
 * a lower tier; a real `plan: 'enterprise'` org gets the same string from
 * `PLAN_LABELS`.
 */
export const ENTERPRISE_PLAN_LABEL = PLAN_LABELS.enterprise

/**
 * The org's monthly recurring revenue in USD — its LIST price
 * (`orgListPriceMonthlyUsd`) net of any applied per-org discount
 * (`org.discount`, AGL-1105), and 0 for anyone who is not actually billing. A
 * comped enterprise deal genuinely collects less, so the discount belongs in
 * MRR; the pre-discount sticker stays available as `orgListPriceMonthlyUsd`.
 */
export function orgMonthlyRevenueUsd(
  org: Partial<AglynOrgBilling> | null | undefined,
): number {
  const listPrice = orgListPriceMonthlyUsd(org)
  if (listPrice <= 0) return 0
  return applyDiscountUsd(listPrice, org?.discount)
}

/**
 * Stripe's standard processing fee on Aglyn's OWN subscription charges
 * (AGL-1108) — 2.9% + $0.30 per charge. This is the fee Aglyn pays Stripe to
 * collect a subscription; it is DISTINCT from the storefront platform fee
 * (`transactionFee*Pct`, the Connect `application_fee`) that a seller's own
 * Stripe pays on their storefront sales. Revenue/margin should be reasoned on
 * the NET figure, not gross.
 */
export const STRIPE_PROCESSOR_FEE_PCT = 0.029
export const STRIPE_PROCESSOR_FEE_FIXED_USD = 0.3

/**
 * A gross monthly USD amount net of Stripe's processing fee. Annual
 * subscriptions are charged once per year, so the fixed 30¢ amortizes to
 * $0.30/12 per month; monthly subscriptions pay it every month. Never
 * returns below 0.
 */
export function netOfProcessorFee(
  grossMonthlyUsd: number,
  annual = false,
): number {
  if (!(grossMonthlyUsd > 0)) return 0
  const fixedPerMonth = annual
    ? STRIPE_PROCESSOR_FEE_FIXED_USD / 12
    : STRIPE_PROCESSOR_FEE_FIXED_USD
  const net = grossMonthlyUsd * (1 - STRIPE_PROCESSOR_FEE_PCT) - fixedPerMonth
  return Math.max(0, Math.round(net * 100) / 100)
}

/**
 * The org's monthly recurring revenue NET of Stripe's processing fee
 * (AGL-1108) — what Aglyn actually keeps before infra COGS, and the correct
 * base for margin/discount reasoning. Gross is `orgMonthlyRevenueUsd`; this
 * subtracts ~2.9%+30¢, interval-aware. 0 for anyone not actually billing.
 */
export function orgNetMonthlyRevenueUsd(
  org: Partial<AglynOrgBilling> | null | undefined,
): number {
  const gross = orgMonthlyRevenueUsd(org)
  if (gross <= 0) return 0
  return netOfProcessorFee(gross, org?.subscription?.interval === 'year')
}

/**
 * Net-margin floor for discount underwriting (AGL-1105). Mirrors the
 * Enterprise-Pricing-Calculator's ~75% gross-margin target: a discounted
 * subscription should still keep ≥75% of its net (post-processor-fee) revenue
 * after infrastructure COGS. Below the floor a discount is warned; well below
 * it is blocked without explicit sign-off.
 *
 * WHAT THIS IS A FLOOR ON (AGL-1930): net revenue less INFRASTRUCTURE COGS,
 * and nothing else. It is a contribution margin — see `MARGIN_SCOPE_NOTE`,
 * which is the sentence every staff surface quoting this number has to print
 * beside it.
 *
 * Read as a dollar guard it says: net revenue must be at least
 * `cogs / (1 − floor)` = **4 × COGS**, and blocks below
 * `cogs / (1 − floor + band)` ≈ **2.86 × COGS**. That restatement is the point
 * of AGL-1930 — the percentage is scale-invariant, so the only thing that
 * moves the real guard line in dollars is the COGS figure fed into it, and on
 * production today that figure is `INFRA_COGS_PER_SITE_USD × sites` for
 * **every** org (14 of 14 usage rollups across all 6 orgs, measured
 * 2026-08-24). Re-tuning this constant therefore re-tunes a multiplier on the
 * flat floor, not on anything measured. Calibrate the floor first.
 */
export const NET_MARGIN_FLOOR_PCT = 0.75

/**
 * How far under the floor a discount may sit and still only WARN (AGL-1105).
 * Within `[floor − band, floor)` staff see a caution; below `floor − band`
 * the guardrail blocks unless overridden. 10 points, matching the
 * calculator's soft/hard split.
 */
export const NET_MARGIN_WARN_BAND_PCT = 0.1

/**
 * What every "net margin" figure in the staff console actually measures
 * (AGL-1930).
 *
 * `checkDiscountMargin`, the enterprise quote card and the coupon badge all
 * compute `(net revenue − infrastructure COGS) / net revenue`. Support,
 * acquisition and overhead are not in the model anywhere — no meter records
 * them and no constant estimates them — so "net margin 78%" is a CONTRIBUTION
 * margin, and a staff member signing a deal on it is reading a ceiling, not a
 * profit.
 *
 * One exported string rather than three literals: the three surfaces that
 * print a margin already drifted apart once (AGL-1134), and a caveat that is
 * true on one screen and absent on the next is worse than none.
 */
export const MARGIN_SCOPE_NOTE =
  'Contribution margin: net revenue less infrastructure COGS only. ' +
  'Support, customer acquisition (CAC) and overhead are not in this figure.'

/**
 * The `ok` / `warn` / `block` band a cost-recovery margin falls in (AGL-1930).
 *
 * Extracted so `NET_MARGIN_FLOOR_PCT` and `NET_MARGIN_WARN_BAND_PCT` have ONE
 * reader. The staff org page's enterprise quote card had `>= 0.75` and
 * `>= 0.65` written into its JSX, so re-tuning either constant — the whole
 * subject of AGL-1930 — would have moved the discount guardrail and left that
 * card rating deals against the old numbers, silently and with money on the
 * line.
 */
export function netMarginRating(marginPct: number): DiscountMarginRating {
  return marginPct >= NET_MARGIN_FLOOR_PCT
    ? 'ok'
    : marginPct >= NET_MARGIN_FLOOR_PCT - NET_MARGIN_WARN_BAND_PCT
      ? 'warn'
      : 'block'
}

/**
 * Per-site monthly infrastructure COGS in USD (AGL-1105). The
 * Enterprise-Pricing-Calculator's ~$2/site figure — Firebase + Vercel at
 * cost — charged against every host the org runs. The margin guardrail
 * subtracts this from net revenue before rating a discount.
 */
export const INFRA_COGS_PER_SITE_USD = 2

/**
 * Platform markup on passed-through infrastructure costs (AGL-41).
 *
 * DEFINED HERE, not in `apps/console/utils/usage-metering.ts`, and re-exported
 * from there — the same move AGL-2155 made for `pageViewsFromBandwidthGb`, and
 * for the same reason (AGL-2194). The published `/pricing` pass-through table
 * states both a cost column and a "you pay (+30%)" column, and
 * `tools/marketing/build-pricing-tables.mts` must generate both from code so
 * `npm run check:pricing-tables` can fail on drift. That generator resolves no
 * `@aglyn/*` path alias, so it can import `plan-entitlements.ts` and nothing
 * under `apps/console`. A constant the published page depends on must live
 * where the page's generator can read it.
 *
 * The console keeps importing it from `usage-metering` unchanged.
 *
 * It is the margin KEPT: cost + 30% after Stripe's processor fee, not before
 * it (AGL-3476). A price of cost × 1.3 kept cost × 1.26 once 2.9% came off,
 * so the published rates are set by {@link METERED_PRICE_MULTIPLE}, and this
 * stays the figure every full-utilization rule is held to.
 */
export const METERED_MARKUP = 1.3

/**
 * What a passed-through cost is multiplied by to reach its price, so that
 * cost + 30% survives Stripe's percentage fee: 1.3 ÷ 0.971 ≈ 1.3388
 * (AGL-3476). The fixed 30¢ is paid by the subscription charge the overage
 * rides on, so only the percentage is grossed up.
 */
export const METERED_PRICE_MULTIPLE = METERED_MARKUP / (1 - STRIPE_PROCESSOR_FEE_PCT)

/**
 * A published overage price: `costPerQuotedUnitUsd` × {@link
 * METERED_PRICE_MULTIPLE}, rounded UP at the `decimals` it is quoted in, so
 * the price the page prints and the invoice bills is the same round figure
 * and never less than the rule. The product is settled to six places before
 * the ceiling so a figure that is exact in decimal — or a pinned cost carried
 * to eleven places, like the page-view pair — is not lifted a step by its
 * tail.
 */
export function publishedMeteredPrice(costPerQuotedUnitUsd: number, decimals: number): number {
  const scale = 10 ** decimals
  return (
    Math.ceil(Number((costPerQuotedUnitUsd * METERED_PRICE_MULTIPLE * scale).toFixed(6))) / scale
  )
}

/**
 * What the REQUESTS behind ONE page view cost once the platform is past its
 * hosting plan's included request allowance, at the DEAREST region the CDN
 * and functions bill, in USD per view (AGL-1879, AGL-3444): the CDN requests
 * a cold load makes, and the one of them that reaches a function — the
 * analytics beacon. A page view billed past a plan's band is priced at
 * `(perPageView + this) × METERED_MARKUP` — $0.80 per 1,000 — and every
 * `bandwidthGb` band is sized with it counted.
 *
 * ## Why a page view needs a second cost term
 *
 * `perPageView` prices what a view WEIGHS: the bytes it transfers, plus the
 * Firestore reads behind the render. A view also costs per REQUEST, which is
 * not proportional to weight: Vercel Pro includes 10,000,000 CDN requests a
 * month, then bills $2.00 to $3.20 per million by region, and a cold load of
 * `aglyn.com/` makes 57 first-party requests to settle
 * (`tools/tenant-page-budget.json`, `wireCalibration.measuredAgainst`) — so
 * the allowance is about 175,000 views a month, platform-wide, and runs out
 * long before the transfer allowance. One of those requests is the analytics
 * beacon (`apps/tenant/app/api/analytics/collect`), which runs a function on
 * every view and writes two counter documents.
 *
 * ## Why the dearest region
 *
 * The CDN bills a request, and the bytes it transfers, in the region that
 * SERVES it — the one nearest the visitor — so a site whose audience is in
 * São Paulo or Cape Town costs what those regions cost, and nothing about a
 * plan can choose otherwise. The platform's rule is that no plan is under
 * water at any utilization and that "at cost + 30%" is true of every billed
 * unit, so every Vercel-billed term of a page view is priced where Vercel is
 * dearest.
 *
 * ## The figure
 *
 * Per view, past the allowance, at the dearest region:
 *
 * |                                                        |              |
 * |--------------------------------------------------------|--------------|
 * | 57 CDN requests at $3.20 per million                   | $0.00018240  |
 * | the beacon's function: 0.4 s billed as active CPU at   | $0.00002922  |
 * |   $0.221/hr, 2 GB at $0.0183/GB-hr, $0.60 per million  |              |
 * | the beacon's two counter writes at nam5's $0.18/100k   | $0.00000360  |
 * | the beacon's ~1 KB through the function at $0.43/GB    | $0.00000044  |
 * | **requests, measured**                                 | $0.00021566  |
 *
 * ($0.399028 weight + $0.215662) × 1.3 is $0.7991 per 1,000, rounded up to
 * $0.80, and this term is pinned so ($0.399028 + $0.216357) × 1.3 is exactly
 * $0.80 — "Our cost $0.615385 / You pay $0.80" — with the basis set ABOVE the
 * measurement as the weight basis is. "At cost + 30%" holds of the cost a
 * billed view incurs in the dearest region, and so in every other. The 0.4 s
 * is the one function duration on record (the form-submission route), billed
 * whole as CPU because nothing measured splits it from I/O wait.
 *
 * ## ⛔ Not in {@link ORG_COGS_UNIT_RATES_USD}, deliberately
 *
 * `perPageView` is half of a pair with `ESTIMATED_PAGE_TRANSFER_BYTES` — one
 * page weight in dollars and in bytes — and `npm run check:page-view-rate`
 * recovers that weight from the rate. A per-request charge is not
 * proportional to weight, so folding it into `perPageView` would make the
 * gate read a page nobody serves.
 *
 * ## The INCLUDED bands carry it too
 *
 * A view inside a plan's band makes the same requests as one past it, and
 * once the platform is past the allowance they cost the same either way, so
 * the `bandwidthGb` bands are sized with this term counted: $0.224 per
 * included GB on top of $0.413 of weight. `tier-margin-floor.spec.ts`
 * multiplies it in and pins every tier at 100% of every band.
 *
 * Lives HERE, beside the markup, for the reason the markup does: the
 * `/pricing` generator publishes the cost column from it and cannot import
 * anything under `apps/console`.
 */
export const PAGE_VIEW_CDN_REQUEST_COST_USD = 0.00021635711

/**
 * Monthly unit costs for the meters the rollup already records (AGL-1134).
 *
 * These are OUR costs, not prices — the org is billed at cost × 1.30
 * (`METERED_MARKUP`, AGL-41). Storage/page-view/form rates match
 * `METERED_UNIT_RATES_USD` in the metering module on purpose: two files
 * disagreeing about what a page view costs is exactly the drift this function
 * exists to remove.
 *
 * The three added here are operator-tuned estimates of the same quality as
 * the existing three — Firestore-backed dataset storage is priced well above
 * object storage because it is, and API requests and contacts are priced off
 * the reads behind them. Treat every operator-tuned figure here as
 * provisional until validated against a real Firebase + Vercel invoice month;
 * AGL-1134 says so out loud rather than implying the numbers are derived.
 * `perEmailSend` is the exception and carries its own basis below.
 *
 * ## Three of these are shared with the billed table. The rest are NOT.
 *
 * `storagePerGbMonth`, `perPageView` and `perFormSubmission` are the metered
 * infrastructure pass-through, and the customer is charged those figures
 * times `METERED_MARKUP`. They therefore appear in `METERED_UNIT_RATES_USD`
 * as well, with identical values, and must be changed in both places at once.
 * A page view billed past its band also carries
 * {@link PAGE_VIEW_CDN_REQUEST_COST_USD}, which neither table holds: it is
 * the per-request CDN charge past the hosting plan's allowance, and this
 * table's `perPageView` is the weight-proportional half of what a band is
 * sized against — the request term is the other.
 *
 * ## Every Vercel-billed input is priced at Vercel's dearest region
 *
 * The CDN and functions bill by region, from $0.15 to $0.35 per GB of
 * transfer (a decimal GB), $2.00 to $3.20 per million requests, and $0.128 to
 * $0.221 per hour of active CPU (memory $0.0106 to $0.0183 per GB-hour), and
 * a plan cannot choose where its visitors are. So the Vercel share of every
 * rate here is priced at the dearest region (AGL-3444): the transfer inside
 * `perPageView`, the function, CDN request and transfer inside
 * `perFormSubmission` and `perApiRequest`, and the compute inside `perRun`.
 *
 * The GCP shares are priced at the database's own location. Production's
 * Firestore is `nam5` (read from the Admin API 2026-10-01), at $0.06 per
 * 100,000 reads, $0.18 per 100,000 writes and $0.18 per GiB-month — twice
 * the single-region figures — and every Firestore operation in this table is
 * priced there. Object storage is GCS US multi-region, `storagePerGbMonth`.
 *
 * Everything else here is a cost-model input ONLY. `dataStoragePerGbMonth`,
 * `perApiRequest`, `perContactMonth` and `perEmailSend` price what an org
 * costs us; what an org is CHARGED on those axes is a retail rate on
 * `PLAN_PRICING` that descends with the tier and is not derived from cost.
 * Adding one of them to `METERED_UNIT_RATES_USD` would put it inside the
 * "at cost + 30%" claim, which is true of exactly three meters.
 *
 * The first three were corrected 2026-08-09 (AGL-1280) — storage $0.03 →
 * $0.026 and form submissions $0.0005 → $0.00005 — `perPageView` was
 * re-pegged 2026-09-09 (AGL-2711), from $0.0001 to $0.00016153846, and the
 * last two were re-priced at Vercel's dearest region and Firestore's `nam5`
 * on 2026-10-01 (AGL-3444): page views $0.00039902751, form submissions
 * $0.000061538462.
 * All three MUST be changed here and in `METERED_UNIT_RATES_USD` together. See that
 * table for each figure's basis; it is the one a customer is billed against,
 * so it is the one that carries the working.
 *
 * The page-view basis is no longer an open gap. The rate priced a 627 KB page
 * against one that measured far more for weeks; the weight reduction that was
 * preferred over a re-peg has now shipped, the page was re-measured at
 * 976.1 KB, and the rate is pegged to a 1012.8 KB basis that sits ABOVE it.
 * `npm run check:page-view-rate` holds that peg, and now fails if the basis
 * ever drops back below what a published page measures.
 *
 * Raising the two here raises measured COGS, which changes no guardrail
 * verdict: `INFRA_COGS_PER_SITE_USD × sites` is the floor, and it still
 * outruns measured cost by orders of magnitude at real traffic.
 */
export const ORG_COGS_UNIT_RATES_USD = {
  storagePerGbMonth: 0.026,
  /**
   * ⛔ HALF OF A PAIR — see ESTIMATED_PAGE_TRANSFER_BYTES, which states the
   * same page in bytes as this states it in dollars. Both are calibrated
   * against a 1012.8 KB page and neither may move alone: their quotient is
   * the cost of a gigabyte's weight, and that — with the request term — is
   * what every bandwidthGb band on the ladder is sized against.
   */
  perPageView: 0.00039902751,
  perFormSubmission: 0.000061538462,
  /**
   * Firestore-backed dataset bytes — nam5's $0.18 per GiB-month, an order
   * pricier than object storage.
   */
  dataStoragePerGbMonth: 0.18,
  /**
   * One `/v1` request — $117 per million (AGL-3444).
   *
   * Every request is a function invocation behind a CDN request, and it reads
   * before it answers (`apps/console/utils/api-v1.ts`). The ledger for a
   * default list page — 25 records, read as 26 — at Vercel's dearest region
   * and nam5's list prices:
   *
   *   - reads: the key, the rate-limit window, the org, the month's quota
   *     counter, the site the resource sits under, and the page — 31, at
   *     $0.06 per 100,000 = $0.0000186
   *   - writes: the rate-limit window and the usage counter — 2, at $0.18 per
   *     100,000 = $0.0000036
   *   - the function, 0.4 s billed as active CPU at $0.221/hr, 2 GB at
   *     $0.0183/GB-hr, $0.60 per million invocations = $0.0000292
   *   - the CDN request: $3.20 per million = $0.0000032
   *   - the response, 25 records at 3 KB — the largest mean document size
   *     measured on production across the collections `/v1` lists (leads,
   *     3.0 KB; records, contacts, media and the rest 1.0–1.5 KB) — plus 2 KB
   *     of request and envelope, through Fast Data Transfer ($0.35 a GB) and
   *     Fast Origin Transfer ($0.43 a GB), decimal GB = $0.0000615
   *
   * $0.0001161, carried up to $0.000117. A page of 100 — the most a client
   * may ask for — costs about three times that, so the figure is the default
   * request rather than the ceiling; see the 2026-10-01 decision entry.
   */
  perApiRequest: 0.000117,
  /**
   * One CRM record held for a month. An operator-tuned estimate (AGL-1134),
   * not built from a per-operation price: at `nam5`'s $0.06 per 100,000 it
   * covers about 333 reads a record a month, plus the record's storage at
   * $0.18 per GiB-month — well beyond what a record is read.
   */
  perContactMonth: 0.0002,
  /**
   * One delivered email, whatever produced it — $0.90 per 1,000.
   *
   * Unlike its five neighbours this is not an operator-tuned estimate. It is
   * the provider's own blended rate at the tier above the account's current
   * one ($90 / 100,000) and also the marginal overage rate on both that tier
   * and the current one, so it does not fall as volume grows and is the
   * conservative figure at any usage the platform can reach.
   *
   * It prices EVERY send on the cost meter — campaigns, receipts, invites,
   * password resets — because every one of them is a message the provider
   * charges for. The band on the plan and the retail overage rate beside it
   * both concern a smaller quantity; this is the whole bill.
   */
  perEmailSend: 0.0009,
  /**
   * One workflow or action run — $13 per million.
   *
   * Derived from the run paths at list prices (2026-09-07), because nothing
   * measurable exists yet: runs execute inside the Vercel request that fired
   * the event, not in a function of their own, so Cloud Logging carries no
   * per-run entry to count, and Firestore's operation counts are platform-wide
   * and cannot be attributed to a run. Over the 14 days to 2026-09-07 the
   * project logged no run at all.
   *
   * The ledger, for an event that executes one workflow on a site holding a
   * handful of functions, variables and workflows (`runEventWorkflows`,
   * `runEventActions` and the inbound-webhook run are the same shape):
   *
   *   - reads: the triggered query, the workflow map, the host → org lookup,
   *     the run counter, the functions and the variables — about 12, at
   *     nam5's $0.06 per 100,000 = $0.0000072
   *   - writes: the activity row and the counter increment — 2, at $0.18 per
   *     100,000 = $0.0000036
   *   - compute: a few milliseconds of active CPU and memory on Fluid compute
   *     for the expression evaluation, about $0.000001 in the cheapest
   *     region and $0.0000017 in the dearest ($0.221 against $0.128 an
   *     hour of active CPU), which is where it is priced
   *
   * $0.0000125, carried as $0.000013. It scales with the catalog a site holds
   * — every `limit(100)` read above is a query that costs one read per
   * document returned — and a step that writes a dataset record or calls a
   * webhook adds to it, so this is the typical figure, not the ceiling.
   *
   * It is a COST, not a price: no plan bills runs, and none refuses past the
   * band on a metered plan. It exists so a tier's run bands reach
   * `orgMonthlyCogsUsd` and `tier-margin-floor.spec.ts` — Agency's 3,000,000
   * runs are $36 a month that read as nothing while this was absent.
   */
  perRun: 0.000013,
}

/**
 * The rollup fields `orgMonthlyCogsUsd` prices. All optional and all
 * absent-safe.
 *
 * The named fields are the platform's own meters. A plugin's meter rides the
 * index signature under the rollup field its declaration names
 * (`plugin-usage-axes.ts`): the AI plugin's provider spend, the CRM's
 * records, the forms plugin's submissions, the workflows plugin's runs.
 */
export interface OrgUsageRollupInput {
  hostCount?: number | null
  storageGb?: number | null
  pageViews?: number | null
  dataStorageMb?: number | null
  apiRequests?: number | null
  /**
   * Every email the org sent this month, campaigns and transactional alike —
   * the `emailSends` cost meter, not the campaign meter.
   *
   * The cost meter is the right input precisely because the campaign meter is
   * the one with a cap on it. What this org cost us is every message the
   * provider charged for, and most of them are messages no quota could have
   * refused.
   */
  emailSends?: number | null
  /**
   * A plugin's meter, by the rollup field its declaration names. `unknown`
   * because a rollup document carries its month and its audit fields beside
   * the meters, and a caller may hand the document over whole; every meter
   * is read through `declaredMeterReading`, which prices only a positive
   * finite number.
   */
  [field: string]: unknown
}

export interface OrgCogsResult {
  /** What the guardrail should charge against revenue. */
  cogsUsd: number
  /** Which arm produced it — `floor` means the meters came in under the flat estimate. */
  basis: 'measured' | 'floor'
  /** Cost from the meters alone, before the floor is applied. */
  measuredUsd: number
  /** `INFRA_COGS_PER_SITE_USD` × sites. */
  floorUsd: number
  /** Per-meter contributions, for showing the working in staff UI. */
  breakdown: Record<string, number>
}

/**
 * The unit rate a declared cost axis names, or 1 where it names none (the
 * rollup already records dollars).
 *
 * ⚑ A rate key core does not carry THROWS. Pricing an unknown axis at zero
 * would make an org cost less than it does, which is the direction that
 * approves a discount; `plugin-usage-axes.spec.ts` holds every declared key
 * to a rate that exists, so this is reached only by a tree that skipped it.
 */
function cogsRateOf(axis: { id: string; rate?: string }): number {
  if (axis.rate === undefined) return 1
  const rate = (ORG_COGS_UNIT_RATES_USD as Record<string, number>)[axis.rate]
  if (typeof rate !== 'number' || !Number.isFinite(rate)) {
    throw new Error(
      `cost axis "${axis.id}" names the rate "${axis.rate}", which ORG_COGS_UNIT_RATES_USD does not carry`,
    )
  }
  return rate
}

/**
 * One org's monthly infrastructure COGS — the single cost model (AGL-1134).
 *
 * Replaces two things that had drifted apart: the discount guardrail read the
 * rollup's `costUsd`, and the staff MRR views read the same field separately,
 * so neither could be changed without silently changing the other's meaning.
 *
 * `costUsd` was also incomplete. The rollup records `dataStorageMb`,
 * `apiRequests` and `contactsCount`, and `estimateMonthlyUsageCost` prices
 * none of them — it sums storage, page views and form submissions only. So
 * the "measured" cost omitted three meters the platform was already at the
 * trouble of recording.
 *
 * The flat per-site figure stays a FLOOR rather than a fallback: measured
 * cost wins only when it exceeds the estimate. A rollup that has not run, or
 * an org with genuinely tiny usage, must not make the guardrail more generous
 * — that is the one direction that costs money.
 *
 * Measured against production 2026-07-30, that floor is doing all the work:
 * the largest real org's `costUsd` was **$0.0000054** against a $4.00 floor
 * for two sites. Five orders of magnitude. So this function changes no verdict
 * today, and will not until there is real traffic — which is the honest
 * reason to build it now, while nothing depends on the answer.
 */
export function orgMonthlyCogsUsd(
  rollup: OrgUsageRollupInput | null | undefined,
  siteCount: number,
): OrgCogsResult {
  const num = (value: unknown) => {
    const parsed = Number(value ?? 0)
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0
  }
  const rates = ORG_COGS_UNIT_RATES_USD
  // The platform's own axes, at the positions the plugins' declared axes
  // interleave with (`order`, a multiple of 10 here). The generator refuses a
  // declaration that takes one of these ids or positions.
  const core: Array<{ id: string; order: number; usd: number }> = [
    { id: 'storage', order: 10, usd: num(rollup?.storageGb) * rates.storagePerGbMonth },
    { id: 'pageViews', order: 20, usd: num(rollup?.pageViews) * rates.perPageView },
    // Megabytes on the doc, gigabytes in the rate — the unit mismatch is in
    // the stored field name, so convert here rather than in each caller.
    {
      id: 'dataStorage',
      order: 40,
      usd: (num(rollup?.dataStorageMb) / 1024) * rates.dataStoragePerGbMonth,
    },
    { id: 'apiRequests', order: 50, usd: num(rollup?.apiRequests) * rates.perApiRequest },
    // Every send, not the overage — the provider bills the first message of
    // the month as much as the last, so a cost model that started counting
    // at the plan's included band would report an org that stayed inside its
    // allowance as free.
    { id: 'emailSends', order: 70, usd: num(rollup?.emailSends) * rates.perEmailSend },
  ]
  // Each plugin's meter, priced at the core rate its declaration names — or
  // at ×1 where the rollup already records dollars, as the AI plugin's
  // provider spend does.
  const declared = pluginCostAxes().map((axis) => ({
    id: axis.id,
    order: axis.order,
    usd: declaredMeterReading(rollup, axis) * cogsRateOf(axis),
  }))
  const breakdown: Record<string, number> = Object.fromEntries(
    [...core, ...declared]
      .sort((a, b) => a.order - b.order)
      .map((axis) => [axis.id, axis.usd]),
  )
  const measuredUsd = Object.values(breakdown).reduce((sum, x) => sum + x, 0)
  const floorUsd = Math.max(0, siteCount) * INFRA_COGS_PER_SITE_USD
  return {
    cogsUsd: Math.max(measuredUsd, floorUsd),
    basis: measuredUsd > floorUsd ? 'measured' : 'floor',
    measuredUsd,
    floorUsd,
    breakdown,
  }
}

/**
 * Pluck the fields `orgMonthlyCogsUsd` prices out of a raw usage rollup
 * (AGL-1134) — a Firestore `snapshot.data()`, or the JSON a staff endpoint
 * served from one. ONE list of field names, in one place.
 *
 * The hazard this closes is silent. Every caller previously hand-listed the
 * six fields at its own call site — the discount route, the anomaly detector,
 * the staff org page — and `/api/admin/org-usage` projected only three of
 * them. A projection that drops a priced field does not error; the model just
 * returns a smaller number, and a smaller COGS is the direction that APPROVES
 * a discount. Same shape as the API projection that starved a predicate in
 * AGL-1355, and the two-units usage figure that read 20-45% wrong for years
 * in AGL-1402.
 *
 * UNITS, because the field names do not all say: `storageGb` and
 * `dataStorageMb` are gigabytes and MEGABYTES respectively (the conversion
 * lives in `orgMonthlyCogsUsd`, with its own test); `pageViews`,
 * `apiRequests` and `emailSends` are counts FOR THE MONTH. A plugin's fields
 * carry the unit its cost-axis declaration states — a monthly count, a
 * point-in-time level, or dollars.
 */
export function orgCogsInputFrom(
  source: Record<string, unknown> | null | undefined,
): OrgUsageRollupInput {
  const read = (field: string) => {
    const value = source?.[field]
    return value == null ? undefined : (value as number)
  }
  const input: OrgUsageRollupInput = {
    hostCount: read('hostCount'),
    storageGb: read('storageGb'),
    pageViews: read('pageViews'),
    dataStorageMb: read('dataStorageMb'),
    apiRequests: read('apiRequests'),
    emailSends: read('emailSends'),
  }
  // Every field a plugin's cost axis reads or records — forwarded for the
  // reason the six above are: a projection that drops a priced field prices
  // the meter at nothing, which is the direction that approves a discount.
  for (const field of pluginCostAxisFields()) input[field] = read(field)
  return input
}

/**
 * A cost figure, or the explicit admission that the rollup has not loaded
 * (AGL-1134).
 *
 * `pending` is not "no usage" and not "$0" — it is "do not answer yet".
 */
export type OrgCogsPreview =
  | { status: 'pending' }
  | { status: 'ready'; cogs: OrgCogsResult }

/**
 * `orgMonthlyCogsUsd` behind an explicit readiness flag (AGL-1134).
 *
 * The staff org page loads its rollup asynchronously, and an unfinished read
 * and an absent document are the same `undefined`. Priced directly, both
 * yield `basis: 'floor'` — so the enterprise pricing card printed "per-site
 * floor — no usage recorded yet" during the loading window, a
 * measurement-shaped sentence about an org whose measurements had simply not
 * arrived. That is the AGL-1380 / AGL-1422 defect exactly: a question
 * answered before its data is ready, and answered in the cheap direction.
 *
 * Returning a discriminated union rather than a nullable number is what makes
 * the window unrepresentable — a caller cannot reach a cost without first
 * handling `pending`, so the next call site cannot re-introduce this by
 * forgetting a flag it was handed.
 */
export function orgCogsPreview(
  ready: boolean,
  rollup: OrgUsageRollupInput | null | undefined,
  siteCount: number,
): OrgCogsPreview {
  // Gated on the caller's flag alone. Deriving readiness from `rollup` being
  // truthy would re-create the bug: the org with no rollup yet is precisely
  // the one whose value stays falsy after the read completes.
  if (!ready) return { status: 'pending' }
  return { status: 'ready', cogs: orgMonthlyCogsUsd(rollup, siteCount) }
}

/**
 * Where a cost figure came from, in a sentence a staff member can act on
 * (AGL-1930).
 *
 * The staff quote card said `per-site floor — no usage recorded yet` for
 * EVERY `basis: 'floor'` org. Measured against production 2026-08-24 that
 * sentence is false on most of them: all 14 usage rollups across all 6 orgs
 * price under the floor, and 9 of the 14 carry genuinely non-zero usage — the
 * largest real org measured **$0.22** against its $2.00 floor. "No usage
 * recorded" and "usage recorded, and it came in under the floor" are opposite
 * facts about the cost model, and only the second one is evidence the meters
 * are working at all.
 *
 * Same failure family as AGL-1380 / AGL-1422 — a measurement-shaped sentence
 * printed over a measurement that was never consulted — one layer further in:
 * here the measurement exists and the copy denies it.
 */
export function orgCogsBasisSummary(
  cogs: OrgCogsResult,
  month?: string | null,
): string {
  const when = month ?? 'the latest'
  if (cogs.basis === 'measured') return `measured from ${when} usage`
  // Absent and zero are NOT the same claim, and with `strictNullChecks` off an
  // absent meter reads as 0 all the way down — so this leans on the caller's
  // `orgCogsPreview` readiness gate for "has a rollup arrived", and reports
  // only what the priced total says.
  if (!(cogs.measuredUsd > 0)) return 'per-site floor — no usage recorded yet'
  // Cents are the wrong precision for these figures and rounding to them
  // prints `$0.00`, which reads as the "no usage" claim this function exists
  // to stop making. Every real org's measured month is under a cent.
  const measured =
    cogs.measuredUsd < 0.01
      ? cogs.measuredUsd.toFixed(4)
      : cogs.measuredUsd.toFixed(2)
  return (
    `per-site floor — ${when} usage measured ` +
    `$${measured}, under the $${cogs.floorUsd.toFixed(2)} floor`
  )
}

/**
 * Percent-off at or above which creating a coupon needs explicit staff
 * sign-off (AGL-1105). A ≥40%-off coupon is a real revenue commitment, so the
 * creation route rejects it unless the caller passes a confirm flag; the UI
 * surfaces a checkbox at this threshold.
 */
export const DISCOUNT_APPROVAL_THRESHOLD_PCT = 40

/**
 * Discount DEPTH bands (AGL-1120) — how much of list price is being given
 * away, which is a different question from whether the remainder covers
 * infrastructure.
 *
 * The original guardrail asked only the second question, so a **93% off**
 * coupon rated "OK — net margin 78.1% vs a 75% floor": $9.15 left against $2
 * of infra really is a 78% contribution margin. The ratio is scale-invariant,
 * so as long as the leftover dwarfs $2 the verdict stays green no matter how
 * little is left. Depth never entered the verdict at all.
 *
 * Warn at the threshold that already requires sign-off, so the badge and the
 * approval checkbox stop disagreeing with each other; block at 70%, where a
 * deal is being given away rather than discounted.
 */
export const DISCOUNT_DEPTH_WARN_PCT = DISCOUNT_APPROVAL_THRESHOLD_PCT
export const DISCOUNT_DEPTH_BLOCK_PCT = 70

/** How many sites (hosts) the org runs — at least 1 for a billing org. */
export function orgSiteCount(
  org: Partial<AglynOrgBilling> | null | undefined,
): number {
  return Math.max(1, Object.keys(org?.hosts ?? {}).length)
}

/** The margin-guardrail verdict for a proposed or applied discount. */
export type DiscountMarginRating = 'ok' | 'warn' | 'block'

export interface DiscountMarginResult {
  /** Pre-discount list price (`orgListPriceMonthlyUsd`). */
  grossUsd: number
  /** List price net of the proposed discount. */
  discountedUsd: number
  /** Discounted price net of Stripe's processing fee — what Aglyn keeps. */
  netUsd: number
  /**
   * Monthly COGS charged against this org: the org's MEASURED cost when the
   * caller supplies one, else the flat `$/site × sites` estimate. The flat
   * figure is a floor, never a ceiling — a measured cost below it is treated
   * as the estimate, since a rollup that has not caught up must not make a
   * deal look cheaper than the placeholder did.
   */
  infraCogsUsd: number
  /** True when `infraCogsUsd` came from measured usage, not the flat rate. */
  cogsMeasured: boolean
  /**
   * Cost recovery = (net − COGS) / net, as a fraction. 1 = COGS-free; -1 is
   * the clamp for a fully-underwater discount (net ≤ 0), which always blocks.
   *
   * NOT a deal-quality number on its own — see `depthPct`. Kept under its
   * original name because the staff UI reports it verbatim.
   */
  marginPct: number
  /** Share of list price given away, as a fraction (0.93 = 93% off). */
  depthPct: number
  /** The worse of the depth and cost-recovery verdicts. */
  rating: DiscountMarginRating
  /**
   * Which test produced `rating`, so the UI can say why rather than showing a
   * margin number that looks fine next to a red badge.
   */
  reason: 'none' | 'depth' | 'cogs' | 'underwater'
  /** The cost-recovery floor this was rated against (`NET_MARGIN_FLOOR_PCT`). */
  floorPct: number
}

export interface DiscountMarginOptions {
  /**
   * The org's measured monthly cost, when the caller has it — the `costUsd`
   * rollup at `orgs/{id}/usage/{month}`. Server callers and the staff org
   * page can supply it; coupon creation cannot, because no org is chosen yet.
   */
  measuredCogsUsd?: number | null
}

/**
 * Rate a proposed discount (AGL-1105, re-shaped in AGL-1120) — the guardrail
 * behind staff coupon creation and per-org application.
 *
 * Two independent questions, and the verdict is the WORSE of them:
 *
 *  1. **How deep is it?** `depthPct` against `DISCOUNT_DEPTH_WARN_PCT` /
 *     `DISCOUNT_DEPTH_BLOCK_PCT`. This is the one that was missing. Cost
 *     recovery alone is scale-invariant — 93% off $139 leaves $9.15 against
 *     $2 of infra, a genuine 78% contribution margin — so the old verdict
 *     rated a give-away "OK" and only warned at 94%.
 *  2. **Does the remainder cover the cost of serving them?** `marginPct`
 *     against `NET_MARGIN_FLOOR_PCT`, with `NET_MARGIN_WARN_BAND_PCT` below
 *     it, and an automatic block once the discount drives net ≤ 0.
 *
 * Neither subsumes the other: a shallow discount on an org running twenty
 * sites can still fail cost recovery, and a deep one on a cheap org can still
 * clear it. `reason` reports which test bound, so the UI never shows a
 * healthy-looking margin beside a red badge without explanation.
 *
 * `discount` is the PROPOSED change, independent of any discount already on
 * the org — `grossUsd` is always the list price (`orgListPriceMonthlyUsd`),
 * so this never double-counts an existing `org.discount`. A non-billing org
 * (list price 0) has no revenue to protect and rates `ok`.
 */
export function checkDiscountMargin(
  org: Partial<AglynOrgBilling> | null | undefined,
  discount: Pick<OrgDiscount, 'percentOff' | 'amountOffUsd'>,
  options: DiscountMarginOptions = {},
): DiscountMarginResult {
  const floorPct = NET_MARGIN_FLOOR_PCT
  const grossUsd = orgListPriceMonthlyUsd(org)
  const flatCogsUsd =
    Math.round(INFRA_COGS_PER_SITE_USD * orgSiteCount(org) * 100) / 100
  // Measured cost wins only when it EXCEEDS the flat estimate. A rollup that
  // has not run yet reads as a low number, and a guardrail that got looser
  // because the meter was behind would fail in the one direction that costs
  // money.
  const measured = Number(options.measuredCogsUsd ?? 0)
  const cogsMeasured = Number.isFinite(measured) && measured > flatCogsUsd
  const infraCogsUsd = cogsMeasured
    ? Math.round(measured * 100) / 100
    : flatCogsUsd
  if (grossUsd <= 0) {
    return {
      grossUsd: 0,
      discountedUsd: 0,
      netUsd: 0,
      infraCogsUsd,
      cogsMeasured,
      marginPct: 1,
      depthPct: 0,
      rating: 'ok',
      reason: 'none',
      floorPct,
    }
  }
  const discountedUsd = applyDiscountUsd(grossUsd, discount)
  const annual = org?.subscription?.interval === 'year'
  const netUsd = netOfProcessorFee(discountedUsd, annual)
  const depthPct =
    Math.round(((grossUsd - discountedUsd) / grossUsd) * 10000) / 10000
  const marginPct =
    netUsd > 0
      ? Math.round(((netUsd - infraCogsUsd) / netUsd) * 10000) / 10000
      : -1

  const depthRating: DiscountMarginRating =
    depthPct * 100 >= DISCOUNT_DEPTH_BLOCK_PCT
      ? 'block'
      : depthPct * 100 >= DISCOUNT_DEPTH_WARN_PCT
        ? 'warn'
        : 'ok'
  // Same bands the staff quote card renders — one reader for the two
  // constants (AGL-1930), so a re-tune cannot reach the guardrail and miss
  // the UI.
  const cogsRating: DiscountMarginRating = netMarginRating(marginPct)

  const severity = { ok: 0, warn: 1, block: 2 } as const
  const rating =
    severity[depthRating] >= severity[cogsRating] ? depthRating : cogsRating
  const reason: DiscountMarginResult['reason'] =
    rating === 'ok'
      ? 'none'
      : netUsd <= 0
        ? 'underwater'
        : severity[depthRating] >= severity[cogsRating]
          ? 'depth'
          : 'cogs'

  return {
    grossUsd,
    discountedUsd,
    netUsd,
    infraCogsUsd,
    cogsMeasured,
    marginPct,
    depthPct,
    rating,
    reason,
    floorPct,
  }
}

/**
 * The plan the org actually gets (AGL-247): missing/unknown plans resolve
 * as `free`, and a paid plan whose subscription is canceled/unpaid/
 * incomplete downgrades to `free` until the webhook restores it — plan
 * fields alone are not entitlement.
 *
 * A STAFF COMP comes first (AGL-3034), and only where no subscription decides
 * the plan: `resolvePlanComp` answers `null` while one is live, so a paying
 * workspace's plan is always its subscription's. The comp is its own marker,
 * never the stored `plan` — a canceled customer whose `plan` still names what
 * they paid for resolves as Free exactly as before, because nothing about
 * their document is a comp.
 */
export function resolveEffectivePlan(
  org: Partial<AglynOrgBilling> | null | undefined,
): OrgPlan {
  const comp = resolvePlanComp(org)
  if (comp) return comp.plan
  const plan = org?.plan
  if (!plan || !(plan in PLAN_ENTITLEMENTS)) return 'free'
  const status = subscriptionStatusOf(org)
  if (plan !== 'free' && status && DEAD_SUBSCRIPTION_STATUSES.has(status)) {
    return 'free'
  }
  return plan
}

function resolvePlan(org: Partial<AglynOrgBilling> | null | undefined) {
  return resolveEffectivePlan(org)
}

/**
 * Purchased add-on quantities that currently apply (AGL-524): add-ons
 * bill as items on the org's Stripe subscription, so a dead subscription
 * (the `resolveEffectivePlan` set) stops them counting until the webhook
 * restores it. Orgs with no subscription state keep staff-set quantities
 * (comped add-ons predating self-serve billing).
 */
function resolvePurchasedAddons(
  org: Partial<AglynOrgBilling> | null | undefined,
): OrgSeatAddons {
  const status = subscriptionStatusOf(org)
  if (status && DEAD_SUBSCRIPTION_STATUSES.has(status)) return {}
  return org?.seatAddons ?? {}
}

/**
 * Whether the org's Aglyn AI add-on currently applies (AGL-2896): bought,
 * and on a subscription that is still paying for it. The ONE reading of
 * `seatAddons.aiAddon` — the entitlement fold and the assist overage rate
 * both ask this, so a dead subscription drops the band and the rate
 * together rather than leaving a rate on a band that has gone.
 */
export function hasAiAddon(
  org: Partial<AglynOrgBilling> | null | undefined,
): boolean {
  return aiAddonUnits(resolvePurchasedAddons(org)) === 1
}

/**
 * Numeric entitlement keys that were REMOVED from `PLAN_ENTITLEMENTS` and
 * must never be resolved again from a stored override (AGL-2133).
 *
 * `totalSiteSizeMb` is the first. It carried a value on all 8 plans and was
 * enforced by nothing — no gate, no meter, no alert. AGL-1107 added it and
 * AGL-1370 deleted its meter and its alert on measurement rather than taste:
 * `measure-node-map.ts` refuses any node map over 900 KB (AGL-678), so the
 * measurable org total tops out at 2.3–20.9% of the cap depending on plan.
 * What survived was a WRITABLE staff override field for a number nothing
 * read, so a support engineer resolving a "this site is too big" ticket
 * raised it, got a success and an audit row, and changed nothing.
 *
 * WIRING IT WAS THE OTHER OPTION AND IT IS WORSE. A cap the measurement can
 * reach a fifth of is a gate that refuses nobody — the same "reports success,
 * does nothing" shape, moved from the staff dialog to the enforcement point,
 * plus a new hard wall introduced days before launch. The per-site size
 * ceiling that actually binds is AGL-678's 900 KB node map, and it is real.
 *
 * The rollup's `siteSizeMb` MEASUREMENT stays — host-usage and the usage
 * audit read it, and it is a genuine internal signal. Only the entitlement
 * goes.
 */
export const RETIRED_ENTITLEMENT_KEYS: ReadonlySet<string> = new Set([
  'totalSiteSizeMb',
])

/** Each seat band as `[included, max]`: the max purchases may reach. */
const SEAT_BANDS = [
  ['managersPerOrg', 'maxManagersPerOrg'],
  ['membersPerHost', 'maxMembersPerHost'],
  ['datasetsPerOrg', 'maxDatasetsPerOrg'],
] as const satisfies ReadonlyArray<
  readonly [
    keyof Omit<ResolvedOrgEntitlements, 'features'>,
    keyof Omit<ResolvedOrgEntitlements, 'features'>,
  ]
>

/**
 * The numeric entitlements that are PRICES rather than caps (AGL-3049): the
 * platform's percentage of a storefront or marketplace sale.
 *
 * Every other numeric key is a band — a count, a size, a monthly or daily
 * allowance — and an uncapped comp lifts it to `UNLIMITED`. These three it
 * must never touch. `transactionFee*Pct` becomes Stripe's
 * `application_fee_amount` at checkout, so an unlimited one is a fee larger
 * than the charge and every sale on the storefront fails; and "no cap" has
 * no meaning for a price in the first place. An uncapped workspace pays its
 * plan's percentages, or whatever a per-org override sets them to.
 *
 * Named rather than derived from the `Pct` suffix, so a new numeric key is a
 * band unless someone decides here that it is a price. `plan-comp.spec.ts`
 * pins these three by name and proves every other numeric key is lifted.
 */
export const PRICE_ENTITLEMENT_KEYS: ReadonlySet<string> = new Set([
  'transactionFeePhysicalPct',
  'transactionFeeDigitalPct',
  // A plugin's percentage of a sale says so in its declaration.
  ...pluginPlanQuotas()
    .filter((declared) => declared.price)
    .map((declared) => declared.key),
])

/**
 * The entitlements with every band lifted (AGL-3049): `UNLIMITED` on each
 * numeric key but the prices, features untouched. What an uncapped comp in
 * force resolves to — see `isUncappedPlanComp`.
 *
 * Applied LAST, over the plan row, the per-org overrides and the folded
 * add-ons alike: "every band reads as unlimited" means an override written
 * before the comp was uncapped does not quietly cap it again. The overrides
 * stay stored, and apply once more if the comp is capped or removed.
 */
function withEveryCapLifted(
  entitlements: ResolvedOrgEntitlements,
): ResolvedOrgEntitlements {
  const lifted: Record<string, unknown> = { ...entitlements }
  for (const [key, value] of Object.entries(entitlements)) {
    if (typeof value === 'number' && !PRICE_ENTITLEMENT_KEYS.has(key)) {
      lifted[key] = UNLIMITED
    }
  }
  return lifted as unknown as ResolvedOrgEntitlements
}

/**
 * Effective entitlements for an org: plan defaults with the org doc's
 * per-key overrides applied (features merge key-by-key too), then
 * purchased add-ons stacked on top (AGL-524): `seatAddons.hosts` raises
 * `hostLimit`, `seatAddons.eventCalendar` switches the `eventCalendar`
 * feature on, and every seat add-on a plugin declared through
 * `registerPluginEntitlements` (AGL-2940) widens the quota it names by its
 * per-plan figure per unit and switches its features on — the Aglyn AI
 * add-on (AGL-2896) is the first such declaration. (Seat/dataset add-ons
 * instead fold in at `checkSeatQuota` / `checkDatasetQuota`, where the
 * per-plan hard max clamps them.)
 * Missing or unknown plans resolve as `free`.
 *
 * EVERY NUMERIC KEY of `OrgEntitlements` is an override axis, not a chosen
 * few: the loop below copies any number it finds under the org's
 * `entitlements`, so a contracted `bandwidthGb`, `apiRequestsPerMonth` or
 * `workflowRunsPerMonth` resolves ahead of the plan default exactly as the
 * long-documented `emailSendsPerMonth`, `formsPerHost` and
 * `assistCreditsPerMonth` overrides do. Since 2026-09-07 this is what makes
 * Enterprise's finite fallbacks safe to hold: the row in `PLAN_ENTITLEMENTS`
 * is what an agreement gets before its numbers are written down, and the
 * override is where they are written. `plan-entitlements.spec.ts` proves
 * the precedence on every axis, so a resolver that started honoring a list
 * would fail by the name of the axis it dropped.
 *
 * `hostLimit` is DELIBERATELY unclamped here (AGL-1738): there is no
 * `maxHostsPerOrg` to clamp against, because extra sites are sold flat rather
 * than up to a band. Their bound is the purchase ceiling,
 * `EXTRA_HOSTS_ADDON_MAX`, and a band-shaped `Math.min` added here would
 * silently disable sites the org is invoiced for. See that constant before
 * "fixing" this add.
 *
 * `posRegisters` IS NOT ADDED HERE, and this is the fix, not an omission
 * (AGL-1775). The register add-on is priced "per
 * extra register/location" and enforced PER SITE, so folding the org-wide
 * purchase into an org-level value every site inherits sold one register and
 * delivered `hostLimit` of them. `seatAddons.posRegisters` is now a POOL and
 * `org.registerAllocations` says which site holds each seat. The number this
 * function returns for `posRegisters` is therefore **the plan's cap alone** —
 * exactly what an unallocated site must resolve to. Per-site capacity comes
 * from `resolveHostRegisterCap(org, hostId)`; nothing else may add the pool
 * back in.
 *
 * (`eventCalendar` is still org-wide on purpose: it is documented and priced
 * as "one purchase covers every host in the org". The register add-on never
 * was, which is the whole difference.)
 *
 * AN UNCAPPED COMP IN FORCE (AGL-3049) resolves every band as `UNLIMITED`
 * on top of all of the above — `withEveryCapLifted` — and leaves the
 * features and the fee percentages exactly as resolved.
 */
export function resolveOrgEntitlements(
  org: Partial<AglynOrgBilling> | null | undefined,
): ResolvedOrgEntitlements {
  const resolved = resolveCappedOrgEntitlements(org)
  return isUncappedPlanComp(org) ? withEveryCapLifted(resolved) : resolved
}

/** `resolveOrgEntitlements` before an uncapped comp lifts anything. */
function resolveCappedOrgEntitlements(
  org: Partial<AglynOrgBilling> | null | undefined,
): ResolvedOrgEntitlements {
  const defaults = withPluginFeatureDefaults(
    PLAN_ENTITLEMENTS[resolvePlan(org)],
    resolvePlan(org),
  )
  const overrides = org?.entitlements
  let resolved = defaults
  if (overrides) {
    const {
      features: featureOverrides,
      datasetsPerHost: legacyDatasets,
      maxDatasetsPerHost: legacyMaxDatasets,
      ...quotaOverrides
    } = overrides
    const merged = { ...defaults }
    for (const [key, value] of Object.entries(quotaOverrides)) {
      // A RETIRED quota's stored override is dropped, not merged (AGL-2133).
      // This loop copies any numeric key it finds, so removing a key from
      // `PLAN_ENTITLEMENTS` alone would leave the value that staff already
      // wrote onto live org documents still appearing on the resolved
      // entitlements — a number with no default behind it and no reader in
      // front of it, which is a worse artefact than the field that produced
      // it. `tools/scripts/strip-retired-entitlements.mjs` clears them from
      // Firestore; this makes the resolver correct before it has run, and
      // stays correct if a document is restored from an old backup.
      if (RETIRED_ENTITLEMENT_KEYS.has(key)) continue
      if (typeof value === 'number') (merged as any)[key] = value
    }
    // Pre-AGL-240 override docs keyed datasets per host; resolve them into
    // the org keys unless an org-keyed override is present.
    if (typeof legacyDatasets === 'number' && overrides.datasetsPerOrg == null) {
      merged.datasetsPerOrg = legacyDatasets
    }
    if (
      typeof legacyMaxDatasets === 'number' &&
      overrides.maxDatasetsPerOrg == null
    ) {
      merged.maxDatasetsPerOrg = legacyMaxDatasets
    }
    // A seat band's max never sits below its included count. The max is the
    // ceiling PURCHASES stop at, and `checkSeatQuota` clamps the limit to it —
    // so a staff override of Team seats to 2 on Free (max 1) resolved back to
    // 1 everywhere: the members card, the invite gate and the acceptance
    // transaction all refused the second manager the override granted. An
    // override of the included count is a grant; the band moves with it.
    for (const [included, max] of SEAT_BANDS) {
      merged[max] = Math.max(merged[max], merged[included])
    }
    resolved = {
      ...merged,
      features: { ...defaults.features, ...featureOverrides },
    }
  }
  const purchased = resolvePurchasedAddons(org)
  const extraHosts = Math.max(0, purchased.hosts ?? 0)
  const eventCalendar = (purchased.eventCalendar ?? 0) >= 1
  // A declared add-on's quota is added AFTER the per-org override, so a
  // contracted figure and a purchased add-on stack the way a raised
  // `hostLimit` and purchased sites do. The per-unit figure is the PLAN's,
  // read off the effective plan rather than the stored one, so a dead
  // subscription that resolved to free adds free's figure.
  const folded = foldPluginSeatAddons(resolved, purchased, resolvePlan(org))
  if (!extraHosts && !eventCalendar && folded === resolved) return resolved
  return {
    ...folded,
    hostLimit: folded.hostLimit + extraHosts,
    features: {
      ...folded.features,
      ...(eventCalendar ? { eventCalendar: true } : {}),
    },
  }
}

/**
 * How many units of a plugin-declared seat add-on a purchase map counts as
 * (AGL-2940): the stored quantity, capped at the declaration's `maxUnits`
 * — 1 for an org-wide add-on, where a doubled webhook item or a hand edit
 * above one is still one purchase. A non-number, `NaN`, a negative, or a
 * key nothing declared is none.
 */
export function pluginSeatAddonUnits(
  addons: OrgSeatAddons | null | undefined,
  key: string,
): number {
  const declared = pluginSeatAddon(key)
  if (!declared) return 0
  const quantity = Number((addons as Record<string, unknown> | null)?.[key] ?? 0)
  if (!Number.isFinite(quantity) || quantity < 1) return 0
  const units = Math.floor(quantity)
  return declared.maxUnits != null ? Math.min(units, declared.maxUnits) : units
}

/**
 * Whether a plugin-declared seat add-on currently applies to the org: bought,
 * and on a subscription that is still paying for it — the one reading every
 * surface that shows or charges for the add-on goes through, so a dead
 * subscription drops the quota and the feature together.
 */
export function hasPluginSeatAddon(
  org: Partial<AglynOrgBilling> | null | undefined,
  key: string,
): boolean {
  return pluginSeatAddonUnits(resolvePurchasedAddons(org), key) > 0
}

/**
 * Every declared seat add-on the org holds, folded in catalog order: the
 * quota each names gains its per-plan figure per unit, and its features
 * switch on. Returns the input object untouched when nothing applies, so a
 * caller can tell "nothing folded" from "folded to the same numbers".
 */
function foldPluginSeatAddons(
  resolved: ResolvedOrgEntitlements,
  purchased: OrgSeatAddons,
  plan: OrgPlan,
): ResolvedOrgEntitlements {
  let out = resolved
  for (const addon of listPluginSeatAddons()) {
    const units = pluginSeatAddonUnits(purchased, addon.key)
    if (!units) continue
    const next: ResolvedOrgEntitlements = { ...out, features: { ...out.features } }
    if (addon.quota) {
      const perUnit = addon.quota.perUnitByPlan[plan] ?? 0
      const bag = next as unknown as Record<string, unknown>
      const current = Number(bag[addon.quota.key] ?? 0)
      bag[addon.quota.key] = (Number.isFinite(current) ? current : 0) + perUnit * units
    }
    const features = next.features as unknown as Record<string, boolean>
    for (const feature of addon.features ?? []) features[feature] = true
    out = next
  }
  return out
}

/**
 * The plan row with every plugin-declared feature's default filled in
 * (AGL-2940). A feature the plan tables already carry keeps the table's
 * answer; a feature only a declaration knows reads its `defaultByPlan`, or
 * `false`. Returns the row itself when no declaration adds anything.
 */
function withPluginFeatureDefaults(
  row: ResolvedOrgEntitlements,
  plan: OrgPlan,
): ResolvedOrgEntitlements {
  const features = row.features as unknown as Record<string, boolean | undefined>
  let out: Record<string, boolean | undefined> | undefined
  for (const declared of listPluginFeatures()) {
    if (features[declared.key] !== undefined) continue
    out = out ?? { ...features }
    out[declared.key] = declared.defaultByPlan?.[plan] ?? false
  }
  return out
    ? { ...row, features: out as unknown as ResolvedOrgEntitlements['features'] }
    : row
}

/**
 * The org's POS register pool (AGL-1775): how many seats were purchased, how
 * many are assigned to a site, and how many are left to assign.
 *
 * `purchased` runs through `resolvePurchasedAddons`, so a dead subscription
 * empties the pool exactly as it empties every other add-on — the seats stop
 * counting when they stop billing.
 *
 * `byHost` is the SANITISED allocation, and it never sums past `purchased`.
 * The write path validates before it stores, so an over-allocated map means
 * the data is stale or corrupt (a purchase reduced without the allocation
 * being trimmed, a partial write, a hand edit). Both directions of getting
 * that wrong cost money, so it is resolved rather than trusted: hosts are
 * walked in sorted-id order and granted what remains of the pool, which is
 * deterministic — the same host gets the same answer on every reader — and
 * cannot hand out capacity nobody paid for.
 *
 * Every value is coerced: a non-number, `NaN`, `Infinity` or a negative reads
 * as zero seats. An `Infinity` in this map would otherwise become an
 * unbounded per-site register cap, which is the one outcome the guard in
 * `resolveHostRegisterCap` exists to make impossible.
 */
export function resolveRegisterSeatPool(
  org: Partial<AglynOrgBilling> | null | undefined,
): {
  purchased: number
  allocated: number
  available: number
  byHost: Record<string, number>
} {
  const rawPurchased = resolvePurchasedAddons(org).posRegisters
  const purchased =
    Number.isFinite(rawPurchased) && (rawPurchased as number) > 0
      ? Math.floor(rawPurchased as number)
      : 0
  const raw = org?.registerAllocations
  const byHost: Record<string, number> = {}
  let allocated = 0
  if (raw && typeof raw === 'object') {
    for (const hostId of Object.keys(raw).sort()) {
      if (allocated >= purchased) break
      const value = Number((raw as Record<string, unknown>)[hostId])
      if (!Number.isFinite(value) || value <= 0) continue
      const seats = Math.min(Math.floor(value), purchased - allocated)
      if (seats <= 0) continue
      byHost[hostId] = seats
      allocated += seats
    }
  }
  return { purchased, allocated, available: purchased - allocated, byHost }
}

/**
 * How many POS registers one SITE may run (AGL-1775): the plan's per-site cap
 * plus whatever the org has assigned to that site out of the pool.
 *
 * THE GUARD, and the reason this is a function rather than a lookup. An
 * unallocated host — no entry in `registerAllocations`, a missing map, a
 * missing org, an org still loading — resolves to `resolveOrgEntitlements(org)
 * .posRegisters`, which since AGL-1775 is the PLAN's cap and nothing else.
 * Never the pooled total, and never `Infinity` unless the plan itself is
 * `UNLIMITED` (enterprise, where an unbounded cap is what was sold).
 *
 * `checkQuota(undefined)` resolving to the Free tier is the lesson this
 * inverts: an absent input there quietly answered a question in the
 * PERMISSIVE direction on the day it mattered. Here the absent input answers
 * in the direction of the plan, which is the conservative one — a site that
 * has been assigned seats and whose allocation failed to load runs fewer
 * registers than it paid for, which the console can say out loud, rather than
 * more than it paid for, which nothing would ever notice.
 *
 * There is deliberately no separate read: the allocation lives on the org doc
 * beside the entitlements, so a caller holding one holds the other.
 */
export function resolveHostRegisterCap(
  org: Partial<AglynOrgBilling> | null | undefined,
  hostId: string | null | undefined,
): number {
  const planCap = resolveOrgEntitlements(org).posRegisters
  if (!hostId) return planCap
  // An UNLIMITED plan cap plus anything is still UNLIMITED; short-circuit so
  // `Infinity + n` never has to be reasoned about downstream.
  if (planCap === UNLIMITED) return UNLIMITED
  return planCap + (resolveRegisterSeatPool(org).byHost[hostId] ?? 0)
}

/**
 * The org's COLLABORATOR seat pool (AGL-2439): how many per-site collaborator
 * seats were purchased, how many are assigned to a site, and how many are
 * left to assign.
 *
 * The AGL-1775 register mechanism, applied to the key it was never applied to.
 * `seatAddons.members` is bought once, org-wide; `membersPerHost` is enforced
 * PER SITE. Adding one to the other handed the whole purchase to every site,
 * so an org with 20 sites that bought ONE extra collaborator seat received 20.
 * The quantity is now the size of an org-level pool and
 * `org.collaboratorAllocations` says which site holds each seat.
 *
 * Everything about the resolution is deliberately identical to
 * `resolveRegisterSeatPool` — the same clamp, the same sorted-id determinism,
 * the same coercion of a non-number / `NaN` / `Infinity` / negative to zero.
 * This is ONE mechanism used twice, not a second mechanism that happens to
 * rhyme: a reader who has understood the register pool has understood this,
 * and a fix to one is a fix to both.
 *
 * `purchased` runs through `resolvePurchasedAddons`, so a dead subscription
 * empties the pool exactly as it empties every other add-on.
 */
export function resolveCollaboratorSeatPool(
  org: Partial<AglynOrgBilling> | null | undefined,
): {
  purchased: number
  allocated: number
  available: number
  byHost: Record<string, number>
} {
  const rawPurchased = resolvePurchasedAddons(org).members
  const purchased =
    Number.isFinite(rawPurchased) && (rawPurchased as number) > 0
      ? Math.floor(rawPurchased as number)
      : 0
  const raw = org?.collaboratorAllocations
  const byHost: Record<string, number> = {}
  let allocated = 0
  if (raw && typeof raw === 'object') {
    for (const hostId of Object.keys(raw).sort()) {
      if (allocated >= purchased) break
      const value = Number((raw as Record<string, unknown>)[hostId])
      if (!Number.isFinite(value) || value <= 0) continue
      const seats = Math.min(Math.floor(value), purchased - allocated)
      if (seats <= 0) continue
      byHost[hostId] = seats
      allocated += seats
    }
  }
  return { purchased, allocated, available: purchased - allocated, byHost }
}

/**
 * How many console COLLABORATORS one SITE may hold (AGL-2439): the plan's
 * per-site allowance plus whatever the org has assigned to that site out of
 * the pool, clamped to the plan's hard `maxMembersPerHost`.
 *
 * THE GUARD, and the reason this is a function rather than a lookup. An
 * unallocated host — no entry in `collaboratorAllocations`, a missing map, a
 * missing org, an org still loading — resolves to the PLAN's `membersPerHost`
 * and nothing else. Never the pooled total, and never `Infinity` unless the
 * plan itself is `UNLIMITED` (enterprise, where an unbounded cap is what was
 * sold). `checkQuota(undefined)` resolving to the Free tier is the lesson
 * this inverts: the absent input answers in the direction of the plan, which
 * is the conservative one.
 *
 * The clamp is what `checkSeatQuota` always did and is kept: collaborator
 * seats are sold up to a BAND (`maxMembersPerHost`), unlike registers, which
 * are sold flat. Assigning more pool seats to a site than the band allows
 * cannot lift it past the band — the org must upgrade the plan.
 *
 * There is deliberately no separate read: the allocation lives on the org doc
 * beside the entitlements, so a caller holding one holds the other.
 */
export function resolveHostCollaboratorCap(
  org: Partial<AglynOrgBilling> | null | undefined,
  hostId: string | null | undefined,
): number {
  const entitlements = resolveOrgEntitlements(org)
  const included = entitlements.membersPerHost
  const maxSeats = entitlements.maxMembersPerHost
  if (!hostId) return Math.min(included, maxSeats)
  // An UNLIMITED plan cap plus anything is still UNLIMITED; short-circuit so
  // `Infinity + n` never has to be reasoned about downstream.
  if (included === UNLIMITED) return UNLIMITED
  const assigned = resolveCollaboratorSeatPool(org).byHost[hostId] ?? 0
  return Math.min(included + assigned, maxSeats)
}

/**
 * Quota answer for a site's console COLLABORATORS (AGL-2439) — the
 * host-scoped `checkSeatQuota`, in the same `SeatQuotaResult` shape so the
 * existing call sites and refusal copy read identically.
 *
 * `checkSeatQuota(org, 'members', …)` is WRONG for a site now and this exists
 * so nobody has to remember why: since AGL-2439 that helper no longer folds
 * the org-wide purchase into a per-site number, so it answers the PLAN's cap
 * and would refuse a site the seats it holds.
 *
 * ## THE GRANDFATHER BOUNDARY — explicit, and this field is it
 *
 * The cap is corrected, and no org already above it is evicted or locked
 * out.
 *
 * The boundary is drawn between two different questions, and they are
 * different lines of code rather than one number used for two purposes:
 *
 *   `allowed`           — may this site take on ANOTHER collaborator? Bound
 *                         by `limit`, the corrected cap. This is the ONLY
 *                         thing the cap decides.
 *   `retainedOverCap`   — how many seats this site holds ABOVE `limit`. They
 *                         are RETAINED. Nothing in this repo revokes a
 *                         collaborator for being over a cap, and nothing may
 *                         be added that does: the cap binds ALLOCATION, never
 *                         ACCESS.
 *
 * So an over-cap site keeps every collaborator it has, cannot add another,
 * and returns to normal by attrition, by buying pool seats and assigning them
 * here, or by upgrading — at which point `limit` recomputes from the new plan
 * or allocation and the retention lapses on its own. That is what "they keep
 * what they have until they next change plan or seat count" means, resolved
 * rather than stored: a stored floor would need a backfill over live orgs and
 * would be a defaulted field on a `merge`-written document, which is the
 * converter hazard that destroys the real value it was meant to protect.
 *
 * `retainedOverCap` is non-zero ONLY when the site is already over. It is not
 * headroom and cannot be spent: it is a count the console says out loud so
 * the customer sees why the next Add is refused while nobody loses access.
 */
export function checkHostCollaboratorQuota(
  org: Partial<AglynOrgBilling> | null | undefined,
  hostId: string | null | undefined,
  currentUsage: number,
): SeatQuotaResult & {
  /** Seats held ABOVE `limit`, retained and never revoked (the grandfather). */
  retainedOverCap: number
  /** Pool seats the org has assigned to THIS site. */
  assignedSeats: number
} {
  const entitlements = resolveOrgEntitlements(org)
  const pricing = resolvePlanPricing(org)
  const included = entitlements.membersPerHost
  const maxSeats = entitlements.maxMembersPerHost
  const addonPriceUsd = pricing.extraCollaboratorMonthlyUsd
  const assignedSeats = resolveCollaboratorSeatPool(org).byHost[hostId ?? ''] ?? 0
  const limit = resolveHostCollaboratorCap(org, hostId)
  return {
    allowed: currentUsage < limit,
    limit,
    remaining: Math.max(0, limit - currentUsage),
    included,
    purchased: assignedSeats,
    maxSeats,
    upgradeRequired: addonPriceUsd === null || limit >= maxSeats,
    addonPriceUsd,
    // THE GRANDFATHER. `Math.max(0, …)` so a site under its cap reports zero
    // rather than a negative that a caller could add and turn into headroom.
    retainedOverCap: Math.max(0, currentUsage - limit),
    assignedSeats,
  }
}

/**
 * Quota answer for a site's POS registers (AGL-1775) — the register-shaped
 * `checkQuota`, in the same `{ allowed, limit, remaining }` shape so callers
 * read identically.
 *
 * `checkQuota(org, 'posRegisters', used)` is WRONG for registers now and this
 * exists so nobody has to remember why: that helper resolves the org-level
 * value, which is the plan cap with no pool in it, so it would refuse a site
 * the seats it holds. Everything that gates a register creation calls this.
 */
export function checkHostRegisterQuota(
  org: Partial<AglynOrgBilling> | null | undefined,
  hostId: string | null | undefined,
  currentUsage: number,
): { allowed: boolean; limit: number; remaining: number } {
  const limit = resolveHostRegisterCap(org, hostId)
  return {
    allowed: currentUsage < limit,
    limit,
    remaining: Math.max(0, limit - currentUsage),
  }
}

/**
 * Platform transaction fee % for a storefront sale (AGL-278): resolved
 * from the effective plan (with per-org overrides) by product type.
 * Digital and service sales use the digital rate; AGL-307 turns this into
 * the Stripe Connect `application_fee_amount` at charge time.
 */
export function resolveTransactionFeePct(
  org: Partial<AglynOrgBilling> | null | undefined,
  productType: 'physical' | 'digital' | 'service',
): number {
  const entitlements = resolveOrgEntitlements(org)
  const key =
    productType === 'physical'
      ? 'transactionFeePhysicalPct'
      : 'transactionFeeDigitalPct'
  // A MALFORMED value falls back to the PLAN'S OWN rate, never to zero
  // (AGL-2114). `resolveOrgEntitlements` merges any numeric override
  // indistinguishably from the default, and `strictNullChecks` is off
  // repo-wide, so a `NaN`, an `Infinity`, a stringly-typed number or a
  // negative can reach here from a hand-edited or partially-written org doc.
  // This used to answer 0 for every one of them — silently zeroing the
  // platform's cut on a PAYING merchant's storefront, which on a destination
  // charge is not "no take rate" but a loss (see below).
  //
  // The sibling `resolveMarketplaceFeePct` has forbidden exactly this since
  // AGL-1543, in as many words: "A malformed override must never zero the
  // platform's cut." This is the storefront half of the same rule. The plan
  // table is a literal and cannot itself be malformed, so it is the honest
  // floor — and it preserves the tiers whose 0% is a DELIBERATE, advertised
  // rate rather than a data fault.
  const raw = entitlements[key]
  const pct =
    typeof raw === 'number' && Number.isFinite(raw) && raw >= 0
      ? raw
      : PLAN_ENTITLEMENTS[resolvePlan(org)][key]
  if (Number.isFinite(pct) && pct > 0) return pct
  // The free plan's 0% prices a plan that CANNOT SELL: `features.commerce` is
  // false there, so on an ordinary free org this rate is never consulted. It
  // IS consulted for the one org shape that can reach a storefront on the free
  // entitlement set — a per-org `features.commerce` override — and 0% on a
  // DESTINATION charge is not "no take rate", it is a loss: Stripe's
  // processing fee (2.9% + 30¢) and any $15 dispute fee are debited from the
  // PLATFORM's balance, so every sale costs Aglyn money (AGL-2071).
  //
  // `marketplaceFeePct: 30` on the free row is the same decision already made
  // for marketplace selling, and its comment says so in as many words: the
  // rate exists to price "an org GRANTED selling via a per-org feature
  // override". This is the missing equivalent for storefront sales, expressed
  // here rather than in the table because the plan CARDS render the table —
  // `billing-plan-cards.component.tsx:444` — and a Free card advertising a
  // transaction fee for selling it does not permit would be a pricing lie.
  //
  // A staff fee override wins even at 0: `resolveOrgEntitlements` merges any
  // numeric override indistinguishably from the default, so the raw doc is
  // what separates "nobody set a rate" from a deliberately comped one. That
  // decision has a name and a reason attached (`org-override-reason.ts`), and
  // this floor must not silently overrule it.
  if (
    resolvePlan(org) === 'free' &&
    entitlements.features.commerce &&
    typeof org?.entitlements?.[key] !== 'number'
  ) {
    return PLAN_ENTITLEMENTS.starter[key]
  }
  return 0
}

/**
 * Stripe's processing cost on ONE destination-charge sale, in cents
 * (AGL-2152) — a storefront order, a booking deposit — the dearest enabled
 * method's percentage plus the fixed 30¢, rounded UP on the percentage so the
 * answer is never a cent short of what Stripe actually debits.
 *
 * WHO PAYS THIS. Every storefront charge is a DESTINATION charge:
 * `payment_intent_data[transfer_data][destination]` names the merchant's
 * connected account and no `transfer_data[amount]` is sent, so Stripe moves
 * the whole charge to the merchant and debits its processing fee from the
 * PLATFORM's balance. Aglyn's net on an order is therefore
 *
 *     application_fee_amount − (processing% × charge + 30¢)
 *
 * and with `application_fee_amount` at 0 — which is what
 * `transactionFeePhysicalPct: 0` produced on Pro, Business, Scale, Advanced,
 * Agency and Enterprise — that net is NEGATIVE on every single sale. A flat
 * percentage cannot repair it either: Starter's 2% is below even the cheapest
 * processing rate, so Starter lost money at every order size too, and any rate
 * above it still loses until the order clears `30¢ ÷ (take − processing)`. The
 * fixed 30¢ is why a percentage alone can never reach break-even on small
 * orders, and why this returns a CENTS amount rather than a rate.
 *
 * WHY THE DEAREST METHOD AND NOT 2.9%. The shopper picks how to pay AFTER the
 * session is created, and `application_fee_amount` is a fixed number Stripe
 * will not let us revise once the charge settles. No storefront path pins
 * `payment_method_types`, so the platform's payment method configuration
 * decides the set — and per AGL-2343 that configuration has `klarna` and
 * `affirm` on beside the card family. Pricing this at the card rate would be
 * wrong exactly on the orders where it cost the most, which is the same
 * reasoning the marketplace listing floor applies; both are defined from the
 * SAME processing constants so the two cannot drift.
 *
 * THE ONE-LINE LEVER. On a card-family order this over-recovers by the spread
 * between the two rates. Pinning `payment_method_types` on the storefront
 * sessions to the card family would make 2.9% the true binding rate, and
 * `SALE_PROCESSING_PERCENT` below is then the single identifier to
 * repoint. That is a product decision about which payment methods a storefront
 * offers, not this one; it is recorded on AGL-2152 and in the Pricing Decision
 * Log so it can be taken deliberately.
 */
// `SALE_PROCESSING_PERCENT` and `SALE_PROCESSING_FIXED_CENTS` are declared
// beside the processing constants they are defined from, further down this
// file — a `const` cannot be read before its declaration is evaluated, and
// this function is only ever CALLED, never evaluated at import.
export function saleProcessingCostCents(chargeCents: number): number {
  if (!Number.isFinite(chargeCents) || chargeCents <= 0) return 0
  return (
    Math.ceil((chargeCents * SALE_PROCESSING_PERCENT) / 100) +
    SALE_PROCESSING_FIXED_CENTS
  )
}

/**
 * The Stripe Connect `application_fee_amount` for ONE storefront sale
 * (AGL-2152) — the platform's advertised take PLUS Stripe's processing cost,
 * passed through at cost.
 *
 *     fee = take%(feeBaseCents) + processing%(chargeCents) + 30¢
 *
 * so Aglyn's net per order is AT LEAST the advertised take, at every order
 * size down to Stripe's own 50¢ charge minimum, and never negative. The
 * September-1 price lock was lifted for this one change; see the Pricing
 * Decision Log entry of 2026-08-19. No advertised PLATFORM rate moved — a 0% tier is still 0%
 * platform take — what changed is that the card cost stops being absorbed
 * silently by Aglyn.
 *
 * `feeBaseCents` is the GOODS the take is a cut of (discounts already applied,
 * tax and shipping excluded — that has always been the basis, AGL-2317).
 * `chargeCents` is what Stripe will actually run the card for, which is goods
 * + tax + shipping, and defaults to `feeBaseCents` for the paths where they
 * are the same number. Passing the goods figure for a charge that also carries
 * tax would under-recover by the processing rate on the difference.
 *
 * Clamped to `chargeCents` because Stripe rejects an application fee larger
 * than the charge. That clamp can only bind below ~32¢, under Stripe's own 50¢
 * charge minimum, so it never trims a real order's fee.
 */
export function resolveTransactionFeeCents(
  org: Partial<AglynOrgBilling> | null | undefined,
  productType: 'physical' | 'digital' | 'service',
  feeBaseCents: number,
  chargeCents: number = feeBaseCents,
): number {
  const charge =
    Number.isFinite(chargeCents) && chargeCents > 0 ? Math.round(chargeCents) : 0
  if (charge <= 0) return 0
  const base =
    Number.isFinite(feeBaseCents) && feeBaseCents > 0
      ? Math.round(feeBaseCents)
      : 0
  const pct = resolveTransactionFeePct(org, productType)
  // The `Math.max(1, …)` every door already applied: a rate above zero on
  // goods the shopper pays for is a fee however small it rounds to.
  const take = pct > 0 && base > 0 ? Math.max(1, Math.round((base * pct) / 100)) : 0
  return Math.min(charge, take + saleProcessingCostCents(charge))
}

/**
 * Stripe's processing cost on a RECURRING charge of `amountCents`, as the
 * percent a Subscription's `application_fee_percent` can carry (AGL-2655).
 *
 * A Stripe Subscription accepts no `application_fee_amount` — only
 * `application_fee_percent`, a rate with at most two decimal places — so the
 * fixed 30¢ that `saleProcessingCostCents` adds in cents has to be
 * folded into the rate. `(rate × amount + fixed) ÷ amount` is, with the
 * amount cancelled,
 *
 *     percent = rate + fixed ÷ amount
 *
 * which is why a smaller recurring price carries a HIGHER percent: the 30¢
 * is a bigger share of it. At 6% + 30¢ a $10 price carries 9%, $25 carries
 * 7.2% and $100 carries 6.3%.
 *
 * Computed in hundredths of a percent on integers and rounded UP to the next
 * hundredth. Up, for the reason the one-time helper rounds its percentage
 * up: rounding to nearest would leave the recovery a fraction of a cent
 * short of what Stripe debits half the time, and Stripe rejects a third
 * decimal outright, so the only honest direction is over. Integers, because
 * `0.09 * 100` is not `9` in floating point and a percent one ulp above a
 * whole hundredth would round up to the next one. The same two constants as
 * the one-time path, so repointing `SALE_PROCESSING_PERCENT` moves
 * both surfaces together. Zero for a charge that is not a charge.
 */
export function saleProcessingPassThroughPercent(
  amountCents: number,
): number {
  const amount =
    Number.isFinite(amountCents) && amountCents > 0 ? Math.round(amountCents) : 0
  if (amount <= 0) return 0
  const rateHundredths = Math.round(SALE_PROCESSING_PERCENT * 100)
  // 30¢ ÷ amount is a fraction; × 100 makes it a percent, × 100 again makes
  // it hundredths of one.
  const fixedHundredths = (SALE_PROCESSING_FIXED_CENTS * 10_000) / amount
  return Math.min(100, Math.ceil(rateHundredths + fixedHundredths) / 100)
}

/**
 * The `application_fee_percent` for ONE storefront subscription (AGL-2655):
 * the plan's advertised take PLUS Stripe's processing cost as a percent —
 * the recurring twin of `resolveTransactionFeeCents`, under the same rule.
 * Every tier carries the pass-through, a 0% tier included, because the
 * tier's 0% is a platform take and the pass-through is not one; it is what
 * Stripe charges to take the payment, which on a destination charge is
 * debited from the platform's balance whatever the tier.
 *
 * `amountCents` is the recurring items the percent will be applied to —
 * the goods after any discount, tax and shipping excluded, the AGL-2317
 * basis. Stripe applies the percent to the whole invoice and the paid-invoice
 * correction scales it back to items, so sizing on items is what makes the
 * recovery come out at cost rather than under.
 *
 * Composed in hundredths so two decimals stay two decimals (`2 + 6.34` is
 * not always `8.34` in floating point), and capped at 100, Stripe's ceiling
 * for the parameter — the recurring twin of the clamp to the charge.
 */
export function resolveSubscriptionFeePercent(
  org: Partial<AglynOrgBilling> | null | undefined,
  productType: 'physical' | 'digital' | 'service',
  amountCents: number,
): number {
  const amount =
    Number.isFinite(amountCents) && amountCents > 0 ? Math.round(amountCents) : 0
  if (amount <= 0) return 0
  const take = resolveTransactionFeePct(org, productType)
  const passThrough = saleProcessingPassThroughPercent(amount)
  return Math.min(
    100,
    (Math.round(take * 100) + Math.round(passThrough * 100)) / 100,
  )
}

/**
 * Platform take rate % for a MARKETPLACE listing sale (AGL-1543), priced
 * off the SELLER org. Resolved through `resolveOrgEntitlements` — so a
 * dead subscription prices as free-plan (30%) even while the stale `plan`
 * field still names a paid tier, and a negotiated per-org
 * `entitlements.marketplaceFeePct` override wins over the plan default.
 * Resolve per request from the org doc; never cache the answer.
 *
 * An out-of-range or missing value falls back to the FREE-plan rate — the
 * conservative direction. A malformed override must never zero the
 * platform's cut.
 */
export function resolveMarketplaceFeePct(
  org: Partial<AglynOrgBilling> | null | undefined,
): number {
  const pct = takeRatePctOf(resolveOrgEntitlements(org))
  if (pct !== null) return pct
  const fallback = takeRatePctOf(PLAN_ENTITLEMENTS.free)
  if (fallback !== null) return fallback
  // No plan carries a take rate at all: the marketplace plugin's declaration
  // did not compile in. Pricing a sale at 0% would hand the whole charge to
  // the seller, so the sale is refused instead.
  throw new Error(
    `no plan declares "${TAKE_RATE_KEY}" — the marketplace take rate ` +
      'is declared by the marketplace plugin (plugins.config.json)',
  )
}

/**
 * The marketplace take rate's key. Declared by the marketplace plugin
 * (AGL-3080); read here by key because the storefront and the revenue report
 * price a marketplace sale from core.
 */
const TAKE_RATE_KEY = 'marketplaceFeePct'

/** A row's take rate when it is a usable percentage, else `null`. */
function takeRatePctOf(entitlements: object): number | null {
  const pct = (entitlements as Record<string, unknown>)[TAKE_RATE_KEY]
  return typeof pct === 'number' && Number.isFinite(pct) && pct >= 0 && pct <= 100
    ? pct
    : null
}

/**
 * The marketplace take rate that breaks even LATEST, derived from the plan
 * table rather than copied from it (AGL-2343).
 *
 * The lowest rate is the binding one: a smaller cut of the same price against
 * the same processing cost needs a dearer listing to cover it, so advice quoted
 * at this band holds for every publisher on every plan. Derived on each call
 * because a hand-written second copy of a rate is exactly the artifact that
 * decays into wrong money copy shown to a publisher — the same reason
 * `planGrantingFeature` reads the table instead of a map.
 */
export function bindingMarketplaceFeePct(): number {
  return Math.min(
    ...Object.values(PLAN_ENTITLEMENTS)
      .map((plan) => takeRatePctOf(plan) ?? 0)
      .filter((pct) => Number.isFinite(pct) && pct > 0),
  )
}

/**
 * What one destination-charge sale costs the PLATFORM to process (AGL-2343):
 * a storefront order, a booking deposit, a marketplace listing. On a
 * destination charge Stripe debits its fee from the PLATFORM's balance, so
 * every surface that sells through the platform's account prices its take
 * against these.
 *
 * NONE OF THESE IS AN AGLYN PRICE. They are Stripe's published US rates, and
 * they exist only as inputs to a platform take and to figures a seller reads;
 * changing one never changes a price list.
 *
 * THE BNPL RATE IS THE ONE THAT BINDS, and it is not hypothetical: the live
 * default payment method configuration has `klarna` and `affirm` enabled
 * alongside `card`, `cashapp`, `link`, `amazon_pay` and `apple_pay`, and no
 * checkout session pins `payment_method_types`, so a buyer may pay by either.
 * A cost computed from the cheapest method would be wrong exactly when it
 * mattered.
 */
export const CARD_PROCESSING_PERCENT = 2.9
/** Klarna/Affirm, roughly. See the note above — this is the binding one. */
export const BNPL_PROCESSING_PERCENT = 6
/** Stripe's per-transaction fixed component, in cents. */
export const PROCESSING_FIXED_CENTS = 30

/**
 * The processing rate a storefront or booking destination charge is priced
 * against (AGL-2152), and the one identifier to repoint if those sessions'
 * payment method set changes.
 *
 * Deliberately its OWN name rather than a direct use of the binding rate,
 * while being defined as it: every seller shares Stripe's published rates and
 * today shares the platform's payment method configuration, but each surface
 * is a separate product decision. A future choice to pin
 * `payment_method_types` on storefront sessions to the card family — and so
 * to recover at `CARD_PROCESSING_PERCENT` — must not silently lower the
 * marketplace listing floor, which is derived from the binding rate for a
 * different flow.
 */
export const SALE_PROCESSING_PERCENT = BNPL_PROCESSING_PERCENT
/** Stripe's per-transaction fixed component, in cents. Not rate-dependent. */
export const SALE_PROCESSING_FIXED_CENTS = PROCESSING_FIXED_CENTS

/**
 * The cheapest plan whose BASE features include `feature`, in ladder order —
 * the answer to "which plan do I need for this?", which is the one thing an
 * upsell has to say and the one thing no gate can hard-code without drifting
 * from the table it is quoting.
 *
 * Returns `undefined` when NO plan carries it on the base tier. That is not
 * an error: `eventCalendar` is an add-on purchased through
 * `resolveOrgEntitlements`' override path and is false on all eight tiers, so
 * "upgrade to X" is the wrong sentence for it. Callers must handle undefined
 * rather than defaulting to a tier name.
 *
 * Derived from PLAN_ENTITLEMENTS on every call rather than from a
 * hand-written map: a table of "feature → plan" is exactly the artifact that
 * decays silently when a tier's flags are re-cut, and it would decay into
 * pricing copy shown to a customer.
 *
 * Ladder order is `SELF_SERVE_PLANS` then `enterprise` — the same order the
 * plan grid renders — so an enterprise-only feature (`ssoEnabled`) reports
 * Enterprise rather than nothing.
 */
export function planGrantingFeature(
  feature: keyof OrgFeatureFlags,
): OrgPlan | undefined {
  return [...SELF_SERVE_PLANS, 'enterprise' as OrgPlan].find((plan) =>
    Boolean(PLAN_ENTITLEMENTS[plan].features[feature]),
  )
}

/**
 * The plan name an upsell should name, ready to interpolate — "Business",
 * or undefined when {@link planGrantingFeature} has no answer.
 */
export function planLabelGrantingFeature(
  feature: keyof OrgFeatureFlags,
): string | undefined {
  const plan = planGrantingFeature(feature)
  return plan ? PLAN_LABELS[plan] : undefined
}

/**
 * True when the org's plan (or overrides) enables the boolean feature. A
 * key outside `OrgFeatureFlags` is one a plugin declared (AGL-2940) and is
 * answered the same way.
 */
export function checkEntitlement(
  org: Partial<AglynOrgBilling> | null | undefined,
  feature: keyof OrgFeatureFlags | (string & {}),
): boolean {
  const features = resolveOrgEntitlements(org).features as unknown as Record<
    string,
    boolean | undefined
  >
  return Boolean(features[feature])
}

/**
 * The branding profile shape and the platform's own fallback, re-exported
 * for every caller that reads them alongside the entitlement gate.
 *
 * They are DEFINED in `platform-brand.ts`, beside the environment-driven
 * literals they are assembled from. A published tenant page renders the
 * attribution badge from `PLATFORM_BRANDING_PROFILE` and needs nothing else
 * in this module; importing it from here would put the whole plan, quota and
 * entitlement table into every anonymous visitor's first load.
 */
export type { ResolvedBrandingProfile } from './platform-brand'
export { PLATFORM_BRANDING_PROFILE } from './platform-brand'

/** A non-empty trimmed string, else undefined — blanks never override a default. */
function cleanBrandString(value: string | undefined): string | undefined {
  const trimmed = typeof value === 'string' ? value.trim() : ''
  return trimmed.length ? trimmed : undefined
}

/**
 * The effective brand for an org (White-Label Phase 1). EVERY branded
 * surface — published-site branding, transactional email, console chrome —
 * MUST read the brand through this one resolver so a white-label org can
 * never partly-render as Aglyn (the multi-surface drift that dogged
 * `removeBranding`, which each surface re-derived on its own).
 *
 * When the org carries the `whiteLabel` entitlement (Agency tier, or an
 * Enterprise per-org override), the org's `brandingProfile` values win and
 * any field it leaves blank falls back to the Aglyn default. Without the
 * entitlement the stored profile is ignored entirely and the full Aglyn
 * brand is returned — so an org that white-labeled and then downgraded
 * reverts cleanly.
 *
 * An org WITH the entitlement that has stored no brand value at all gets the
 * full Aglyn brand too (AGL-3440). The entitlement is the right to a brand,
 * not a brand: until the org sets one, every name it shows is the platform's
 * anyway, so the white-label profile's blanks — no logo, no support link, no
 * home — conceal nothing and only strip the platform's own header and footer
 * from mail whose every line already names the platform. Every Enterprise
 * org carries the entitlement, Aglyn's own included. One field set, and the
 * rules below apply.
 */
export function resolveBrandingProfile(
  org: Partial<AglynOrgBilling> | null | undefined,
): ResolvedBrandingProfile {
  if (!checkEntitlement(org, 'whiteLabel')) return PLATFORM_BRANDING_PROFILE
  const profile = (org?.brandingProfile ?? {}) as OrgBrandingProfile
  if (
    !Object.values(profile).some(
      (value) => cleanBrandString(value as string | undefined) !== undefined,
    )
  ) {
    return PLATFORM_BRANDING_PROFILE
  }
  return {
    productName:
      cleanBrandString(profile.productName) ??
      PLATFORM_BRANDING_PROFILE.productName,
    // NO FALLBACK — and this became load-bearing the moment
    // `PLATFORM_BRANDING_PROFILE.logoUrl` stopped being null. Inheriting it
    // would stamp the PLATFORM's mark on a white-label org's published sites,
    // which is precisely the disclosure white-label is sold to prevent. A
    // white-label org that set no logo shows none.
    logoUrl: cleanBrandString(profile.logoUrl) ?? null,
    // The same no-fallback rule, and no fallback to the light logo either:
    // a reader wanting "the logo for a dark ground" asks for
    // `logoDarkUrl ?? logoUrl` itself, so null here keeps "the org set a
    // dark variant" answerable.
    logoDarkUrl: cleanBrandString(profile.logoDarkUrl) ?? null,
    faviconUrl:
      cleanBrandString(profile.faviconUrl) ?? PLATFORM_BRANDING_PROFILE.faviconUrl,
    faviconDarkUrl: cleanBrandString(profile.faviconDarkUrl) ?? null,
    primaryColor:
      cleanBrandString(profile.primaryColor) ??
      PLATFORM_BRANDING_PROFILE.primaryColor,
    // NO FALLBACK, and this is the one field that gets none (AGL-2428).
    //
    // Only a white-label org reaches this line — the guard above hands every
    // other org the platform profile whole. So a blank here belongs to an
    // organization whose transactional mail reads as *theirs* to a recipient
    // who is *their* customer, and filling the gap with Aglyn's desk sends
    // that person to a support team that cannot help them, while disclosing
    // a vendor they were never told about. `platform-brand.ts` calls the
    // same substitution in the self-host case "a trap dressed as a default";
    // this is that trap with a different victim.
    //
    // Blank therefore means NO LINK, exactly as `emailLogoUrl` one field
    // down already means no logo. An email with a gap where a link should be
    // reads as broken; an email with no support line reads as plain, which
    // is the right appearance for an organization that has not set one.
    supportUrl: cleanBrandString(profile.supportUrl) ?? null,
    // A white-label org has no separate "home" field to store, and the same
    // no-fallback rule applies for the same reason: `PLATFORM_HOME_URL` here
    // would put a link to the platform's marketing site on the badge of an
    // org that pays to conceal the platform exists. Their support URL is the
    // only destination they have told us about, which is exactly what this
    // badge already linked to before the split — so white-label badges are
    // unchanged, and only the platform's own badge stops opening a mail
    // client.
    homeUrl: cleanBrandString(profile.supportUrl) ?? null,
    fromName:
      cleanBrandString(profile.fromName) ?? PLATFORM_BRANDING_PROFILE.fromName,
    emailLogoUrl:
      cleanBrandString(profile.emailLogoUrl) ??
      PLATFORM_BRANDING_PROFILE.emailLogoUrl,
    customConsoleDomain:
      cleanBrandString(profile.customConsoleDomain) ??
      PLATFORM_BRANDING_PROFILE.customConsoleDomain,
  }
}

/**
 * The brand as MERGE TOKENS, for a staff-designed system-email template
 * (AGL-2139).
 *
 * White-label inverted precisely when staff published a template, which is
 * that feature's normal steady state. Every org-context sender has the shape
 * `subject: designed?.subject ?? <branded fallback>` — the DESIGNED template
 * wins — and the catalog copy hard-coded "Aglyn" throughout. There was no
 * brand token to design against either: the merge maps carried `org.name`,
 * `invite.role`, `signInUrl` and friends and nothing about the brand, and
 * `blankUnresolvedTokens` BLANKS any token the caller did not supply. So a
 * designer who reached for `{{brand.productName}}` would have shipped an
 * email with a hole in the sentence.
 *
 * Token bodies are namespaced `brand.*` so a designer can tell them from the
 * per-send values, and they resolve for EVERY org — Aglyn's own included,
 * since `PLATFORM_BRANDING_PROFILE.productName` is `'Aglyn'`. That is what makes
 * a single template correct for both populations instead of one hard-coded
 * for the majority.
 *
 * The email LOGO is deliberately absent. It is structural — `renderEmailHtml`
 * emits it as a header row above the designed body, or emits nothing — so it
 * travels as an option rather than as a token a template could forget to
 * place, and there is no sample URL a staff test-send could render without
 * showing a broken image.
 */
export function brandMergeTokens(
  branding: ResolvedBrandingProfile,
): Record<string, string> {
  return {
    'brand.productName': branding.productName,
    'brand.fromName': branding.fromName,
    // EMPTY, never omitted, when the org has no Support URL (AGL-2428).
    //
    // `renderLoadedSystemEmail` merges these OVER `DEFAULT_BRAND_TOKENS`,
    // which carries the platform's own support URL for the genuinely
    // org-less senders. Dropping the key here would let that default show
    // through and mail a white-label org's customer a link to Aglyn — the
    // exact leak this closes — so the key must be present and empty in order
    // to overwrite it. A designed template that interpolates the token
    // renders nothing where it sits.
    'brand.supportUrl': branding.supportUrl ?? '',
  }
}

/**
 * The value of the `<meta name="generator">` tag and of the `x-powered-by`
 * header on published sites (AGL-2088) — the canonical CMS signal WordPress,
 * Squarespace, Drupal and Ghost all ship, and the thing tech-stack detectors
 * key on.
 *
 * Deliberately VERSIONLESS. The accidental headers this replaced carried
 * `1.0.0-alpha.0` and a Node version, which dated every deployment and named
 * our runtime to no consumer. A detector needs the product name and nothing
 * else.
 */
export const PLATFORM_GENERATOR_NAME = PLATFORM_BRAND_NAME

/**
 * May this org's published pages say they were built with Aglyn? (AGL-2088)
 *
 * The gate for the `<meta name="generator">` tag and the `x-powered-by`
 * header. It reads the SAME entitlement `resolveBrandingProfile` reads —
 * `whiteLabel` — rather than inventing a parallel one, so an agency's
 * concealment promise has exactly one definition.
 *
 * WHY `whiteLabel` AND NOT `showBranding`. The "Made with Aglyn" badge is
 * gated on `removeBranding`, which every plan above free grants, so reusing
 * that gate would confine the signal to free-tier sites — the corpus this
 * exists to build would be almost empty on day one. The two promises are also
 * different in kind: `removeBranding` buys a page with no Aglyn credit on it,
 * an aesthetic commitment about what a VISITOR sees; `whiteLabel` buys
 * concealment of who built the site, which is the promise a machine-readable
 * fingerprint actually breaks. A paying customer on a mid plan has bought a
 * clean page, not anonymity.
 *
 * ⚠️ AN UNRESOLVED ORG SUPPRESSES, and this is the whole reason the function
 * exists instead of a bare `!checkEntitlement(org, 'whiteLabel')` at each call
 * site. `resolveOrgEntitlements(null)` resolves to the FREE plan — a loading
 * default answering a question it was never asked — so a null org reads as
 * "no white-label" and would EMIT. That null is not hypothetical: the tenant's
 * `getOrgBilling` fails open with `org: null` on a Firestore error, so a
 * transient read failure on an Agency site would have stamped the generator
 * tag onto it. The asymmetry decides the direction: suppressing wrongly costs
 * us one detection sample, emitting wrongly breaks a paid promise on a
 * customer's own domain. So the org must be PRESENT and lack the entitlement;
 * absent is not the same as free here.
 */
export function showsPlatformAttribution(
  org: Partial<AglynOrgBilling> | null | undefined,
): boolean {
  // `{}` is treated as unresolved alongside `null`, and that is not
  // pedantry: no org doc that was actually READ is empty — the converter
  // yields `$id` at minimum — so an empty object only ever arrives from a
  // placeholder or a loading default, the same shape `useBranding` hands back
  // before its read lands. It resolves to the free plan like every other
  // absent org, which is the right default for a quota and the wrong one for
  // a disclosure.
  if (!org || Object.keys(org).length === 0) return false
  return !checkEntitlement(org, 'whiteLabel')
}

/**
 * Whether the org has actually **configured** a white-label brand (AGL-1110) —
 * a non-empty product name or logo. White-label chrome should activate only
 * when this is true, not merely because the plan grants the `whiteLabel`
 * entitlement: an Agency org (or a comped Enterprise org) that never set up a
 * brand keeps the plain Aglyn wordmark — no product-name-text render, no
 * flash — until it opts in on the branding page.
 */
export function hasBrandingProfile(
  org: Partial<AglynOrgBilling> | null | undefined,
): boolean {
  const profile = org?.brandingProfile
  if (!profile) return false
  return !!(
    cleanBrandString(profile.productName) || cleanBrandString(profile.logoUrl)
  )
}

/**
 * Quota check: `allowed` is false once `currentUsage` meets the limit —
 * call before creating the next resource (e.g. usage=hostCount before
 * creating another host). `remaining` never goes negative.
 */
/**
 * `members` = per-site collaborator seats (legacy key, AGL-888): the key
 * matches persisted `seatAddons.members` and must not be renamed.
 */
export type SeatKind = 'managers' | 'members'

export interface SeatQuotaResult {
  /** False once usage meets the effective limit (included + purchased). */
  allowed: boolean
  /** Effective seat limit: included + purchased addons, clamped to the max. */
  limit: number
  remaining: number
  /** Included seats on the plan before addons. */
  included: number
  /** Purchased addon seats currently applied. */
  purchased: number
  /** Hard cap incl. addons; reaching it requires a plan upgrade. */
  maxSeats: number
  /**
   * True when buying more addon seats cannot raise the limit — either the
   * plan sells no addons or the hard cap is reached. UI should prompt an
   * upgrade instead of an addon purchase.
   */
  upgradeRequired: boolean
  /** Monthly price per addon seat; null when the plan sells none. */
  addonPriceUsd: number | null
}

/**
 * Seat quota check (AGL-112): seats differ from plain quotas because orgs
 * can buy addon seats (`org.seatAddons`) up to a per-plan hard max —
 * beyond the max the only path is upgrading the plan. `managers` counts
 * org-manager seats org-wide; `members` counts per-site COLLABORATORS
 * (`hosts/{id}/members`, viewer/editor/admin — legacy key name, AGL-888).
 * End-user member accounts (`siteMembers`) are unlimited and never pass
 * through here.
 *
 * SINCE AGL-2439 THIS IS THE PLAN'S CAP FOR `members`, with no purchased
 * seats in it — see the comment at the add below. A per-site answer must come
 * from `checkHostCollaboratorQuota(org, hostId, used)`, which is the only
 * thing that knows which site holds which pool seat. Every caller that has a
 * `hostId` must use that one; this stays correct for a plan-level readout
 * (what the plan includes per site, before anything is assigned).
 */
export function checkSeatQuota(
  org: Partial<AglynOrgBilling> | null | undefined,
  kind: SeatKind,
  currentUsage: number,
): SeatQuotaResult {
  const entitlements = resolveOrgEntitlements(org)
  const pricing = resolvePlanPricing(org)
  const included =
    kind === 'managers'
      ? entitlements.managersPerOrg
      : entitlements.membersPerHost
  const maxSeats =
    kind === 'managers'
      ? entitlements.maxManagersPerOrg
      : entitlements.maxMembersPerHost
  const addonPriceUsd =
    kind === 'managers'
      ? pricing.extraSeatMonthlyUsd
      : pricing.extraCollaboratorMonthlyUsd
  // `members` IS NOT ADDED HERE, and this is the fix, not an omission
  // (AGL-2439) — the AGL-1775 shape, applied to
  // the key that never got it. `seatAddons.members` is an ORG-LEVEL purchased
  // quantity and `membersPerHost` is enforced PER SITE, so folding one into
  // the other handed every site the whole purchase: an org with 20 sites that
  // bought ONE extra collaborator seat received 20 seats for one seat's price.
  // The quantity is now a POOL and `org.collaboratorAllocations` says which
  // site holds each seat. What this function returns for `members` is
  // therefore **the plan's cap alone** — exactly what an unallocated site must
  // resolve to. Per-site capacity comes from `checkHostCollaboratorQuota`;
  // nothing else may add the pool back in.
  //
  // `managers` is unaffected and still adds: `managersPerOrg` really is
  // org-level, so an org-level purchase raising an org-level cap is correct
  // there. That difference is the whole reason this is a `kind` switch rather
  // than one rule.
  const purchased =
    kind === 'managers'
      ? Math.max(0, resolvePurchasedAddons(org)[kind] ?? 0)
      : 0
  const limit = Math.min(included + purchased, maxSeats)
  return {
    allowed: currentUsage < limit,
    limit,
    remaining: Math.max(0, limit - currentUsage),
    included,
    purchased,
    maxSeats,
    upgradeRequired: addonPriceUsd === null || limit >= maxSeats,
    addonPriceUsd,
  }
}

export function checkQuota(
  org: Partial<AglynOrgBilling> | null | undefined,
  quota: keyof Omit<ResolvedOrgEntitlements, 'features'>,
  currentUsage: number,
): { allowed: boolean; limit: number; remaining: number } {
  const limit = resolveOrgEntitlements(org)[quota]
  return {
    allowed: currentUsage < limit,
    limit,
    remaining: Math.max(0, limit - currentUsage),
  }
}

export interface DatasetQuotaResult {
  /** False once usage meets the effective limit (included + purchased). */
  allowed: boolean
  /** Effective dataset limit: included + purchased, clamped to the max. */
  limit: number
  remaining: number
  included: number
  purchased: number
  maxDatasets: number
  /** True when addons cannot raise the limit — upgrade instead. */
  upgradeRequired: boolean
  /** Monthly price per extra dataset; null when the plan sells none. */
  addonPriceUsd: number | null
}

/**
 * Dataset quota check (AGL-132/240), mirroring `checkSeatQuota`: orgs can
 * buy addon datasets (`org.seatAddons.datasets`, org-wide) up to the
 * plan's hard max; beyond the max the only path is upgrading.
 */
export function checkDatasetQuota(
  org: Partial<AglynOrgBilling> | null | undefined,
  currentUsage: number,
): DatasetQuotaResult {
  const entitlements = resolveOrgEntitlements(org)
  const pricing = resolvePlanPricing(org)
  const included = entitlements.datasetsPerOrg
  const maxDatasets = entitlements.maxDatasetsPerOrg
  const addonPriceUsd = pricing.extraDatasetMonthlyUsd
  const purchased = Math.max(0, resolvePurchasedAddons(org).datasets ?? 0)
  const limit = Math.min(included + purchased, maxDatasets)
  return {
    allowed: currentUsage < limit,
    limit,
    remaining: Math.max(0, limit - currentUsage),
    included,
    purchased,
    maxDatasets,
    upgradeRequired: addonPriceUsd === null || limit >= maxDatasets,
    addonPriceUsd,
  }
}

export interface PluginOrgCapacityQuotaResult {
  /** False once usage meets the effective limit. */
  allowed: boolean
  /** Included + purchased, clamped to the plan's ceiling for this capacity. */
  limit: number
  remaining: number
  /** What the plan includes — the figure a downgrade warning quotes. */
  included: number
  /** What the org bought on top, 0 on a dead subscription. */
  purchased: number
  /** How far buying can raise it on this plan. */
  ceiling: number
}

/**
 * The quota for a capacity a PLUGIN backs (AGL-3080), from its declaration.
 *
 * `checkDatasetQuota` is this arithmetic written out for the one capacity
 * core happened to know the name of. It has a dozen callers on money paths
 * and keeps its own shape; what moves here is the ability to ask the same
 * question about a capacity core does not name, which is what a console meter
 * needs — it draws a row per declared capacity and cannot switch on `datasets`.
 *
 * The two must agree, and `plan-entitlements-plugin-capacity.spec.ts` is what
 * holds that: for every plan, this answers exactly what `checkDatasetQuota`
 * answers for the declared dataset capacity. A second implementation of a
 * limit is worth having only while something refuses to let the two drift.
 *
 * ⚑ An undeclared kind, or a declaration naming a field no plan carries, is
 * ZERO — included, ceiling and limit all — which reads as "you are over" and
 * shows a meter full. That is the same direction `overLimitRows` and
 * `includedCapacity` already fail in, and for the same reason: the reassuring
 * answer here would be a meter promising room the create is refused at.
 */
export function checkPluginOrgCapacityQuota(
  org: Partial<AglynOrgBilling> | null | undefined,
  kind: string,
  currentUsage: number,
): PluginOrgCapacityQuotaResult {
  const capacity = pluginOrgCapacity(kind)
  const entitlements = resolveOrgEntitlements(org) as unknown as Record<
    string,
    unknown
  >
  const included = capacity
    ? Number(entitlements[capacity.includedEntitlement] ?? 0)
    : 0
  const ceiling = capacity
    ? Number(entitlements[capacity.purchaseCeilingEntitlement] ?? 0)
    : 0
  // The add-on kind IS the `seatAddons` key it is stored under — the
  // generator refuses two capacities on one add-on kind, so one purchased
  // quantity can only ever raise the capacity that sells it.
  const purchased = capacity
    ? Math.max(
        0,
        Number(
          (resolvePurchasedAddons(org) as unknown as Record<string, unknown>)[
            capacity.addonKind
          ] ?? 0,
        ),
      )
    : 0
  const limit = Math.min(included + purchased, ceiling)
  return {
    allowed: currentUsage < limit,
    limit,
    remaining: Math.max(0, limit - currentUsage),
    included,
    purchased,
    ceiling,
  }
}

export interface DataStorageQuotaResult {
  /**
   * False only when the plan hard-blocks (no overage pricing) and usage
   * meets the included size; metered plans always allow and bill overage.
   */
  allowed: boolean
  /** Included dataset storage on the plan, MB. */
  includedMb: number
  usedMb: number
  /** Remaining included storage, MB; 0 once into overage. */
  remainingMb: number
  /** Usage beyond the included size, GB (0 when within the plan). */
  overageGb: number
  /** Estimated overage this month at the plan's per-GB rate. */
  overageMonthlyUsd: number
  /** Per-GB-month overage rate; null when the plan meters nothing. */
  overageRateUsd: number | null
}

/**
 * Org dataset-storage meter (AGL-240): aggregate stored document bytes
 * across `orgs/{orgId}/datasets`. Plans with an `extraDataGbMonthlyUsd`
 * rate meter the overage onto the monthly invoice (cost-plus, AGL-41);
 * plans without one (free) hard-block at the included size.
 */
export function checkDataStorageQuota(
  org: Partial<AglynOrgBilling> | null | undefined,
  usedMb: number,
): DataStorageQuotaResult {
  const entitlements = resolveOrgEntitlements(org)
  const pricing = resolvePlanPricing(org)
  const includedMb = entitlements.dataStorageMbPerOrg
  const overageRateUsd = pricing.extraDataGbMonthlyUsd
  const used = Math.max(0, usedMb)
  const overageMb = Math.max(0, used - includedMb)
  const overageGb = overageMb / 1024
  return {
    allowed: overageRateUsd !== null ? true : used < includedMb,
    includedMb,
    usedMb: used,
    remainingMb: Math.max(0, includedMb - used),
    overageGb,
    overageMonthlyUsd:
      overageRateUsd === null
        ? 0
        : Math.round(overageGb * overageRateUsd * 100) / 100,
    overageRateUsd,
  }
}

export interface ApiRequestQuotaResult {
  /** Metered plans always allow; plans without API access block. */
  allowed: boolean
  /** Included requests this month at the plan. */
  included: number
  used: number
  /** Remaining included requests; 0 once into overage. */
  remaining: number
  /** Requests beyond the included quota (0 within the plan). */
  overageRequests: number
  /** Estimated overage this month at the plan's per-1,000 rate. */
  overageMonthlyUsd: number
  /** Per-1,000 overage rate; null when the plan has no API. */
  overageRateUsd: number | null
}

/**
 * Customer REST API request meter (AGL-634): the monthly request count for an
 * org. Business and every tier above it carry `apiAccess` and an
 * `extraApiRequestsUsdPer1k` rate, so requests past the included quota meter
 * onto the monthly invoice
 * (cost-plus, like storage overage — never a hard wall mid-integration); plans
 * without API access have `included: 0` and always block.
 */
export function checkApiRequestQuota(
  org: Partial<AglynOrgBilling> | null | undefined,
  usedRequests: number,
): ApiRequestQuotaResult {
  const entitlements = resolveOrgEntitlements(org)
  const pricing = resolvePlanPricing(org)
  const included = entitlements.apiRequestsPerMonth
  const overageRateUsd = pricing.extraApiRequestsUsdPer1k
  const used = Math.max(0, usedRequests)
  const overageRequests = Math.max(0, used - included)
  return {
    allowed: overageRateUsd !== null ? true : used < included,
    included,
    used,
    remaining: Math.max(0, included - used),
    overageRequests,
    overageMonthlyUsd:
      overageRateUsd === null
        ? 0
        : Math.round((overageRequests / 1000) * overageRateUsd * 100) / 100,
    overageRateUsd,
  }
}

export interface CrmRecordsQuotaResult {
  /** Metered plans always allow; free hard-bands at the included count. */
  allowed: boolean
  /** Included CRM records on the plan — the `contactsPerHost` band. */
  included: number
  /** Contacts + companies + deals, as the caller counted them. */
  used: number
  /** Remaining included records; 0 once into overage. */
  remaining: number
  /** Records beyond the included band (0 within the plan). */
  overageRecords: number
  /** Estimated overage this month at the plan's per-1,000 rate. */
  overageMonthlyUsd: number
  /** Per-1,000 overage rate; null when the plan hard-bands (free). */
  overageRateUsd: number | null
}

/**
 * The name this result carried while the band counted contacts alone.
 * @deprecated Read `CrmRecordsQuotaResult`; the shape is the same.
 */
export type ContactQuotaResult = CrmRecordsQuotaResult

/**
 * CRM records band meter (AGL-890, widened in AGL-2611): contacts, companies
 * and deals, counted together across the org and measured against the ONE
 * band `contactsPerHost` names. Paid plans carry an `extraContactsUsdPer1k`
 * rate, so records past the included band meter onto the monthly invoice
 * (cost-plus, like storage and API overage — a growing audience is never
 * dropped); free has no rate and hard-bands at the included count, refusing
 * the next record of any of the three kinds.
 *
 * ONE band for three collections, and not three bands, because a company or
 * a deal costs less to hold than a contact — a ~2 KB document with few index
 * entries and no dynamic-list scan — so counting them at the contacts rate
 * over-covers them, and a second and third number to explain would buy
 * nothing a customer could act on. The caller passes the SUM; the server
 * helper that takes the three aggregate reads is `countCrmRecords`, and the
 * billing meter shows the three components in its caption so the sum is
 * legible rather than opaque. Tasks and activities are not counted: they
 * are bounded by human effort, and the activity log is capped per record by
 * `CRM_ACTIVITIES_PER_RECORD_CEILING` instead.
 *
 * End-user member ACCOUNTS are unlimited on every plan (AGL-889) — this
 * meters only the CRM record.
 */
export function checkCrmRecordsQuota(
  org: Partial<AglynOrgBilling> | null | undefined,
  usedRecords: number,
): CrmRecordsQuotaResult {
  const entitlements = resolveOrgEntitlements(org)
  const pricing = resolvePlanPricing(org)
  const included = entitlements.contactsPerHost
  const overageRateUsd = pricing.extraContactsUsdPer1k
  const used = Math.max(0, usedRecords)
  const overageRecords = Math.max(0, used - included)
  return {
    allowed: overageRateUsd !== null ? true : used < included,
    included,
    used,
    remaining: Math.max(0, included - used),
    overageRecords,
    overageMonthlyUsd:
      overageRateUsd === null
        ? 0
        : Math.round((overageRecords / 1000) * overageRateUsd * 100) / 100,
    overageRateUsd,
  }
}

/**
 * The name the band's gate carried while it counted contacts alone. The SAME
 * function — every caller that still says "contact" is measuring the records
 * band and must hand it the summed count, not the contacts aggregate.
 * @deprecated Call `checkCrmRecordsQuota`.
 */
export const checkContactQuota = checkCrmRecordsQuota

/**
 * `orgs/{orgId}/crmEmailUsage/{YYYY-MM-DD}` — the durable per-day counter of
 * one-to-one CRM emails, `{ count, day, updatedAt }`, server-written only and
 * member-readable, exactly as `apiUsage/{month}` is for API requests.
 */
export const CRM_EMAIL_USAGE_COLLECTION = 'crmEmailUsage'

/**
 * The document id of today's one-to-one email counter, `YYYY-MM-DD` in UTC.
 *
 * UTC rather than the workspace's zone, for the reason `apiUsageMonth` keys
 * the API counter in UTC: the cap is enforced on a server that has no zone
 * of the customer's to read, and a boundary that moved with a browser's
 * clock would let one day's sends be counted against two.
 */
export function crmEmailUsageDayKey(now: Date = new Date()): string {
  return now.toISOString().slice(0, 10)
}

export interface CrmEmailQuotaResult {
  /** Whether ONE MORE send is inside today's cap. Enterprise always allows. */
  allowed: boolean
  /** The plan's daily cap; 0 where the tier has no CRM. */
  included: number
  /** Sends already counted today. */
  used: number
  /** Sends left today; 0 at the cap. */
  remaining: number
  /** When the counter rolls over — the next UTC midnight. */
  resetsAt: Date
}

/**
 * One-to-one CRM email cap (AGL-2611): how many messages a workspace may
 * still send from CRM records today, mirroring `checkApiRequestQuota`.
 *
 * A HARD cap with no overage rate, on every tier — the one respect in which
 * it differs from the API meter it is shaped after. `allowed` is therefore
 * `used < included` outright. Every paid tier's cap is finite, Enterprise's
 * 2,000 included, and 0 on Free makes it false at zero, so a per-org
 * `features.crm` grant does not send until `crmEmailsPerDay` is raised
 * beside it.
 *
 * ## The contract for the send route
 *
 * The route that sends a one-to-one email takes the send as a SLOT on
 * `orgs/{orgId}/crmEmailUsage/{crmEmailUsageDayKey()}` inside one Firestore
 * transaction — `reserveCrmEmailSend` in `@aglyn/tenant-data-admin` reads
 * `count`, calls this with that figure, REFUSES with the plan reason when
 * `allowed` is false and otherwise writes `used + 1` in the same transaction
 * (AGL-2645). A read-then-write cap let two reps at the last slot both pass
 * one reading of it; the transaction re-runs the second on the raised
 * figure. The slot is taken before the message leaves and handed back by
 * `releaseCrmEmailSend` when the provider refuses it. A delivered send is
 * then recorded on the org's `emailSends` cost meter through
 * `recordEmailSends` as `transactional`, because every message the provider
 * charged for is priced into COGS and the campaign cap does not govern it.
 * The count enforced is the count billed, from one document, the way the
 * API meter works.
 *
 * Read the counter with `used` from the server: the console meter on the
 * billing page reads the same document, member-readable, so a rep is never
 * told their limit by being refused.
 */
export function checkCrmEmailQuota(
  org: Partial<AglynOrgBilling> | null | undefined,
  usedToday: number,
  now: Date = new Date(),
): CrmEmailQuotaResult {
  const included = planQuotaOf(resolveOrgEntitlements(org), 'crmEmailsPerDay')
  const used = Math.max(0, Math.floor(Number(usedToday) || 0))
  const resetsAt = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1),
  )
  return {
    allowed: used < included,
    included,
    used,
    remaining: Math.max(0, included - used),
    resetsAt,
  }
}

/**
 * Volume above the plan's included band, in emails.
 *
 * Transactional mail is never refused, so an org CAN and will finish a month
 * over its included allowance. That is not an error and not a failed send —
 * it is the overage the cap chose not to enforce, and recording it is what
 * keeps it from being a surprise at invoicing. `UNLIMITED` (and any
 * non-positive limit, which is how "no included allowance" is expressed)
 * yields 0 rather than the whole month's volume.
 *
 * DEFINED HERE and re-exported from `email-metering.ts` (AGL-2155's move):
 * the billing page renders this figure before anything is invoiced, and it
 * is a client component that cannot pull in the Admin SDK. One subtraction,
 * so the readout and the invoice cannot disagree.
 */
export function emailSendsOverage(total: number, limit: number): number {
  const sends = Number(total)
  const included = Number(limit)
  if (!Number.isFinite(sends) || sends <= 0) return 0
  if (!Number.isFinite(included) || included <= 0) return 0
  return Math.max(0, sends - included)
}

export interface EmailSendOveragePrice {
  /** Emails past the plan's included band, as handed in. */
  overageSends: number
  /** Estimated charge this month at the plan's per-1,000 rate. */
  overageMonthlyUsd: number
  /** Per-1,000 rate; null when the plan carries no email overage price. */
  overageRateUsd: number | null
}

/**
 * Prices email volume past a plan's included band.
 *
 * ## No `allowed`, deliberately
 *
 * Every other meter's helper here answers "may this proceed" alongside "what
 * does it cost", because on those axes one gate decides both. Email has two
 * gates that are not the same gate: `reserveCampaignEmailSends` refuses a
 * CAMPAIGN at the band inside a transaction, and nothing at all refuses
 * transactional mail at any tier. A field named `allowed` on this result
 * would be a plausible-looking thing for a sender to consult, and a
 * transactional sender consulting it is how a password reset stops going out.
 * So the result carries money and nothing else.
 *
 * ## Takes the overage, does not recompute it
 *
 * `emailSendsOverage` already derives the excess from the cost meter and the
 * resolved band, on the path that records it. Re-deriving it here from a raw
 * total would be a second overage model to drift from the first — which is
 * the shape AGL-1402 documented, where two figures that should have been one
 * disagreed by 20-45% for years.
 *
 * A null rate yields zero, structurally rather than by a check, which is what
 * keeps free at zero on this axis for the same reason it is zero on the other
 * three.
 */
export function priceEmailSendOverage(
  org: Partial<AglynOrgBilling> | null | undefined,
  overageSends: number,
): EmailSendOveragePrice {
  const overageRateUsd = resolvePlanPricing(org).extraEmailSendsUsdPer1k
  const sends = Number(overageSends)
  const over = Number.isFinite(sends) && sends > 0 ? sends : 0
  return {
    overageSends: over,
    overageMonthlyUsd:
      overageRateUsd === null
        ? 0
        : Math.round((over / 1000) * overageRateUsd * 100) / 100,
    overageRateUsd,
  }
}

/**
 * Is this org's plan subject to the metered infrastructure pass-through
 * (AGL-1280)? The single answer behind three otherwise-unrelated behaviours:
 * whether storage/bandwidth/form usage past the included band is BILLED, and
 * whether the form-submission cap is a wall or a meter.
 *
 * Deliberately a plan-level question, not an org-level one — an org with no
 * plan resolves as free, so an unknown org meters nothing. Billing usage to
 * someone with no subscription is the one error direction with no recovery.
 * A staff comp is that someone too (AGL-3034): it grants a paid plan with no
 * subscription behind it, so `resolvePlanPricing` answers `false` for it and
 * its bands are walls.
 */
export function planMetersInfraOverage(
  org: Partial<AglynOrgBilling> | null | undefined,
): boolean {
  return resolvePlanPricing(org).meteredInfraPassThrough
}

export interface FormSubmissionQuotaResult {
  /**
   * False only when the plan hard-caps (no metered pass-through) and usage
   * has reached the included count; metered plans always accept and bill
   * the excess.
   */
  allowed: boolean
  /** Included submissions this month for this site. */
  included: number
  used: number
  /** Remaining included submissions; 0 once into overage. */
  remaining: number
  /** Submissions beyond the included count (0 within the plan). */
  overageSubmissions: number
  /** True when overage meters instead of blocking. */
  metered: boolean
}

/**
 * Monthly form-submission meter (AGL-76, made coherent in AGL-1280).
 *
 * Submissions were BOTH hard-capped at `formSubmissionsPerMonth` (429) and
 * metered — which made the published promise that usage past the
 * included band is billed rather than cut off false for the one meter where
 * being cut off costs the customer a lead. Now it mirrors the other three
 * overage meters exactly: a plan with the metered pass-through always accepts
 * and prices the excess (`estimateMonthlyUsageCost`); free has no
 * subscription to bill, so its included count stays a hard wall.
 *
 * Priced per ORG at cost-plus, but capped per SITE, because that is where the
 * counter lives (`hosts/{id}/counters/formSubmissions`). Callers gating a
 * single submission pass that site's month count.
 */
export function checkFormSubmissionQuota(
  org: Partial<AglynOrgBilling> | null | undefined,
  usedThisMonth: number,
): FormSubmissionQuotaResult {
  const included = planQuotaOf(
    resolveOrgEntitlements(org),
    'formSubmissionsPerMonth',
  )
  const metered = planMetersInfraOverage(org)
  const used = Math.max(0, usedThisMonth)
  return {
    allowed: metered ? true : used < included,
    included,
    used,
    remaining: Math.max(0, included - used),
    overageSubmissions: Math.max(0, used - included),
    metered,
  }
}

/**
 * How far past a plan's own included band the abuse ceiling sits (AGL-1655).
 * Ten times what the customer bought is not growth — a site that legitimately
 * outgrows its band by an order of magnitude has an upgrade conversation
 * waiting, not a silent invoice.
 */
export const FORM_ABUSE_CEILING_MULTIPLE = 10

/**
 * Absolute floor for the abuse ceiling, so the small plans get real headroom
 * rather than a second, tighter plan limit wearing a different name. Starter
 * includes 200/month; 10× would be 2,000, which a genuinely busy site could
 * reach. 5,000 is ~167 submissions a day on ONE site — no legitimate site on
 * a small plan reaches that, and no small customer can be billed past
 * 5,000 × $0.000065 ≈ $0.33 of abuse.
 */
export const FORM_ABUSE_CEILING_FLOOR = 5_000

/**
 * Ceiling for plans whose included count is UNLIMITED (Agency, Enterprise),
 * where a multiple has nothing to multiply. Kept at or above the ceiling of
 * every finite plan below it (Advanced includes 100,000 → 1,000,000) so the
 * ladder never inverts and a bigger plan never gets the smaller ceiling.
 */
export const FORM_ABUSE_CEILING_UNLIMITED = 1_000_000

export interface FormAbuseCeilingResult {
  /** True once this month's count for the SITE has reached the ceiling. */
  exceeded: boolean
  /** The ceiling this site's plan resolves to. Always finite. */
  ceiling: number
  used: number
}

/**
 * Per-site monthly abuse ceiling for form submissions (AGL-1655).
 *
 * Deliberately NOT part of `checkFormSubmissionQuota`. That function answers
 * a plan question — "did the customer buy this?" — and AGL-1280 correctly
 * made its answer on a metered plan always yes, because usage past the band
 * bills rather than blocks. This one answers a different question: "is this
 * still a customer's traffic at all?" Conflating them is how the plan gate
 * ended up as the anti-abuse control it was never designed to be, and how
 * `allowed: metered ? true : …` came to mean an unbounded bill.
 *
 * So the ceiling is containment, not capacity. It is far above anything a
 * legitimate site reaches, it is the same shape on every plan, and crossing
 * it is an INCIDENT: the caller is expected to refuse the write, leave the
 * billable counter alone, and make the trip visible to the site's managers
 * and to staff. A ceiling that silently absorbed submissions would only move
 * the free plan's lost-lead failure up to a bigger number.
 *
 * Evaluated against the same month counter the plan gate reads — which is
 * Admin-SDK-only (`counters` is excluded from every client write in
 * `cloud/firebase-firestore.rules`), so the count behind the ceiling cannot
 * be lowered to launder past it.
 */
export function checkFormSubmissionAbuseCeiling(
  org: Partial<AglynOrgBilling> | null | undefined,
  usedThisMonth: number,
): FormAbuseCeilingResult {
  const included = planQuotaOf(
    resolveOrgEntitlements(org),
    'formSubmissionsPerMonth',
  )
  const ceiling = Number.isFinite(included)
    ? Math.max(FORM_ABUSE_CEILING_FLOOR, included * FORM_ABUSE_CEILING_MULTIPLE)
    : FORM_ABUSE_CEILING_UNLIMITED
  const used = Math.max(0, usedThisMonth)
  return { exceeded: used >= ceiling, ceiling, used }
}

/**
 * Average transfer per page view (HTML + JS + a few images), the constant
 * that turns the analytics view counter into a bandwidth number and back.
 *
 * ## ⛔ HALF OF A PAIR. It moves with `perPageView`, or neither moves.
 *
 * This is the BYTES one page view weighs;
 * {@link ORG_COGS_UNIT_RATES_USD}`.perPageView` is the DOLLARS the same page
 * view costs. They are one physical measurement written in two units, and
 * what every `bandwidthGb` band is actually sized against is neither of them
 * alone but their quotient — `(1 GB ÷ this) × perPageView`, the cost of a
 * gigabyte's WEIGHT, to which the band sizing adds the CDN requests the same
 * views make ({@link PAGE_VIEW_CDN_REQUEST_COST_USD}). That quotient is
 * stable even while the page's weight is not,
 * which is precisely why moving one half re-prices every band on the ladder
 * without touching a band: a heavier page costs more per view and buys fewer
 * views per gigabyte, and the two cancel.
 *
 * Both are therefore stated against the SAME weight — **1012.8 KB**, the
 * basis recorded in `tools/tenant-page-budget.json` and recoverable from the
 * rate itself — and `npm run check:page-view-rate` compares the two and
 * refuses a mismatch. There is no version of this constant that is correct
 * against a `perPageView` calibrated for a different page.
 *
 * The basis is FIRST-PARTY encoded bytes at settle, because that is what an
 * egress bill is denominated in: third-party tags leave someone else's
 * network and cost the platform nothing, so counting them here would inflate
 * a customer's measured bandwidth with bytes nobody pays us for.
 *
 * Lives HERE rather than beside the metering rates (AGL-2155). It used to sit
 * in `apps/console/utils/usage-metering.ts`, which the console imports and the
 * tenant app cannot — and the bandwidth ceiling below has to be evaluated in
 * the tenant app, at the beacon that writes the counter. Re-deriving the
 * conversion there would have been a fourth hand-rolled copy of
 * `× 1024³ / page weight`, which is the exact drift AGL-1371 collapsed into
 * one function. `usage-metering` now re-exports these three, so every existing
 * importer is unchanged and there is still one definition.
 */
export const ESTIMATED_PAGE_TRANSFER_BYTES = 1012.8 * 1024

/**
 * Bandwidth ⇄ page views: the one conversion three surfaces used to each
 * write out by hand (AGL-1371).
 *
 * `bandwidthGb` is not a second cap next to metered page views — it IS the
 * included band of the page-view meter, expressed in the unit customers
 * understand. It is therefore used in both directions: forward by
 * `meteredIncludedAllowance` to size the band the invoice subtracts, backward
 * by the usage-alerts cron and the console meter to render live page views as
 * GB.
 */
export function pageViewsFromBandwidthGb(bandwidthGb: number): number {
  return (bandwidthGb * 1024 * 1024 * 1024) / ESTIMATED_PAGE_TRANSFER_BYTES
}

/** @see pageViewsFromBandwidthGb — the same constant, the other way. */
export function bandwidthGbFromPageViews(pageViews: number): number {
  return (
    (Math.max(0, Number(pageViews) || 0) * ESTIMATED_PAGE_TRANSFER_BYTES) /
    (1024 * 1024 * 1024)
  )
}

/** The decimal GB Vercel bills transfer in (AGL-3444). */
const DECIMAL_GB_BYTES = 1_000_000_000

/** The binary GB `bandwidthGb` and GCS egress are counted in. */
const BINARY_GB_BYTES = 1024 * 1024 * 1024

/**
 * How long one decimal GB keeps a streaming function open: a viewer draws a
 * 720p film at about 5 Mbit/s, and the media route reads Storage only as fast
 * as the viewer takes the bytes (AGL-2810), so the function lives for the
 * film, not for the transfer — 1,600 seconds a GB.
 */
const ORIGIN_MEDIA_STREAM_SECONDS_PER_GB = (DECIMAL_GB_BYTES * 8) / 5_000_000

/**
 * The range requests one decimal GB arrives in: a player asks for a film a
 * slice at a time, and a slice of about a megabyte is the small end of what
 * players ask for, so per-request costs are counted a thousand times a GB.
 */
const ORIGIN_MEDIA_REQUESTS_PER_GB = 1_000

/**
 * What ONE DECIMAL GB of video, audio or a file costs to SERVE FROM ORIGIN, by
 * term, at the dearest region every Vercel term is billed in (AGL-3474).
 *
 * The media CDN serves every type the edge must never hold (AGL-1515) on
 * every request, from origin, so a GB of it pays both of Vercel's transfer
 * meters and Storage's egress — where a page's GB is mostly answered by the
 * edge. This is the basis {@link ORIGIN_MEDIA_BANDWIDTH_WEIGHT} is derived
 * from, and the figure the published "at cost + 30%" has to be true of.
 *
 * | term | basis | per decimal GB |
 * |---|---|---|
 * | `fastDataTransfer` | Vercel Fast Data Transfer, dearest region, decimal GB | $0.35 |
 * | `fastOriginTransfer` | Vercel Fast Origin Transfer, dearest region, decimal GB | $0.43 |
 * | `storageEgress` | GCS internet egress at list, $0.12 per GiB (0–1 TiB) | $0.111759 |
 * | `streamingMemory` | 2 GB (the function default) at $0.0183/GB-hour, held 1,600 s | $0.016267 |
 * | `streamingCpu` | 60 s of active CPU at $0.221/hour — an allowance, see below | $0.003683 |
 * | `requests` | 1,000 range requests × $0.0000070 | $0.007 |
 * | **total** | | **$0.918709** |
 *
 * Each request is a CDN request ($3.20 per million), a function invocation
 * ($0.60 per million), the media document's read ($0.0000006 at `nam5`), the
 * day document's counter write ($0.0000018) and two Storage Class B
 * operations, the metadata read and the object read ($0.0000004 each). The
 * lockdown, quarantine and rate-limit reads are cached across requests and
 * cost nothing a request.
 *
 * `streamingCpu` is the one unmeasured term. The bytes are copied, not
 * computed — piping a GB through Node is a few seconds of CPU — and the
 * minute allowed here is chosen to err high, as every other basis in this
 * file does.
 *
 * ## Storing the bytes is NOT here
 *
 * A file in the library is already metered as storage (`storagePerGbMonth`)
 * and billed past the plan's storage at cost + 30% after card fees; serving
 * it does not store it again. Adding storage here would charge for the same
 * bytes twice.
 */
export const ORIGIN_MEDIA_SERVE_COST_USD_PER_GB = {
  fastDataTransfer: 0.35,
  fastOriginTransfer: 0.43,
  storageEgress: (0.12 * DECIMAL_GB_BYTES) / BINARY_GB_BYTES,
  streamingMemory: (2 * 0.0183 * ORIGIN_MEDIA_STREAM_SECONDS_PER_GB) / 3600,
  streamingCpu: (60 * 0.221) / 3600,
  requests:
    ORIGIN_MEDIA_REQUESTS_PER_GB *
    (3.2 / 1_000_000 + 0.6 / 1_000_000 + 0.0000006 + 0.0000018 + 2 * 0.0000004),
} as const

/** {@link ORIGIN_MEDIA_SERVE_COST_USD_PER_GB}, summed: about $0.9187 a decimal GB. */
export const ORIGIN_MEDIA_COST_USD_PER_GB = Object.values(
  ORIGIN_MEDIA_SERVE_COST_USD_PER_GB,
).reduce((sum, term) => sum + term, 0)

/**
 * The published page-view overage, per view: what a billed view costs us
 * (`perPageView` and the request term) at {@link METERED_PRICE_MULTIPLE},
 * rounded up to the cent per 1,000 it is quoted in — $0.83 per 1,000. The
 * same expression as the console's `METERED_BILLED_RATES_USD.perPageView`,
 * computed here because the weight below is, and a spec holds the two equal.
 */
export const PAGE_VIEW_PUBLISHED_PRICE_USD =
  publishedMeteredPrice(
    (ORG_COGS_UNIT_RATES_USD.perPageView + PAGE_VIEW_CDN_REQUEST_COST_USD) * 1000,
    2,
  ) / 1000

/**
 * (a) THE OVERAGE: does counting origin media at `weight` bill a GB of it past
 * the band at cost + 30%, kept after Stripe's percentage fee?
 *
 * A counted GB is `weight` GB of page views at {@link PAGE_VIEW_PUBLISHED_PRICE_USD}.
 */
export function originMediaOverageHolds(weight: number): boolean {
  const billedPerGbUsd =
    weight * PAGE_VIEW_PUBLISHED_PRICE_USD * (DECIMAL_GB_BYTES / ESTIMATED_PAGE_TRANSFER_BYTES)
  return (
    billedPerGbUsd * (1 - STRIPE_PROCESSOR_FEE_PCT) >=
    METERED_MARKUP * ORIGIN_MEDIA_COST_USD_PER_GB
  )
}

/**
 * (b) THE BAND: does counting origin media at `weight` keep a plan that spends
 * its whole bandwidth band on it inside what the band was sized on?
 *
 * Every `bandwidthGb` band is sized at the cost of the views one GiB buys —
 * their weight (`perPageView`) and their requests
 * (`PAGE_VIEW_CDN_REQUEST_COST_USD`), $0.63712 a GiB — and
 * `tier-margin-floor.spec.ts` holds every tier at 1.3× its full-use cost on
 * that figure. A GiB of origin media spends `weight` GiB of the band, so the
 * band holds while `weight` GiB of band costs at least one GiB of media.
 */
export function originMediaBandHolds(weight: number): boolean {
  const bandCostPerGibUsd =
    (ORG_COGS_UNIT_RATES_USD.perPageView + PAGE_VIEW_CDN_REQUEST_COST_USD) *
    pageViewsFromBandwidthGb(1)
  const originCostPerGibUsd =
    (ORIGIN_MEDIA_COST_USD_PER_GB * BINARY_GB_BYTES) / DECIMAL_GB_BYTES
  return weight * bandCostPerGibUsd >= originCostPerGibUsd
}

/**
 * How many GB of bandwidth one GB of video, audio or a file served from origin
 * counts as (AGL-3474, decided by the account owner 2026-10-02): the smallest
 * weight, in tenths and never below 1, that satisfies BOTH
 * {@link originMediaOverageHolds} and {@link originMediaBandHolds}.
 *
 * One weight rather than a second meter or a second rate, because it moves
 * the band and the overage together: the Free wall, the abuse ceiling, the
 * meters, the alerts and the invoice all read media through
 * `pageViewsFromMediaBytes`, which applies it. At today's bases the overage
 * needs 1.54 and the band 1.55, so it is 1.6 — the published sentence names
 * it, and the docs and `/pricing` have to change before it does.
 *
 * Derived, never written down: a rate or a band basis that moves re-derives
 * it, and `media-bandwidth.spec.ts` pins the figure so that a move is a
 * decision with a red test attached rather than a silent re-price.
 */
export const ORIGIN_MEDIA_BANDWIDTH_WEIGHT = ((): number => {
  for (let tenths = 10; tenths <= 1000; tenths += 1) {
    const weight = tenths / 10
    if (originMediaOverageHolds(weight) && originMediaBandHolds(weight)) return weight
  }
  throw new Error('no origin-media weight up to 100 covers its cost')
})()

/**
 * The sentence every customer surface states the weight in — `/pricing`
 * beside the metered rates (`tools/marketing/build-pricing-tables.mts`), and
 * the bandwidth, billing and media docs, which
 * `media-bandwidth-weight-copy.spec.ts` holds to this exact text. Built from
 * the derived figure, so a weight that moves turns those red before it can
 * bill anyone.
 */
export const ORIGIN_MEDIA_BANDWIDTH_SENTENCE =
  'Video, audio, file downloads and other media served from our servers count ' +
  `${ORIGIN_MEDIA_BANDWIDTH_WEIGHT}× toward bandwidth, because serving them ` +
  'costs more than serving pages.'

/**
 * How far past a plan's own included bandwidth the abuse ceiling sits
 * (AGL-2155). Three times the bandwidth the customer bought is not growth; it
 * is an upgrade conversation, or it is not the customer's traffic at all.
 *
 * It is a CAP, not a price, and it is lower than {@link
 * FORM_ABUSE_CEILING_MULTIPLE} because bandwidth is the largest cost line the
 * platform carries and the one a stranger can spend on the customer's behalf.
 * Past the band every 1,000 views bills $0.80 against at most $0.62 of real
 * cost — the dearest region's — once the CDN's request allowance is spent
 * ({@link PAGE_VIEW_CDN_REQUEST_COST_USD}), so the tail is not a loss — but a
 * scraper, a hotlinked asset or a botnet still bills the account holder for
 * traffic they did not ask for, and the ceiling is what bounds that before
 * staff look at it. At 10× the band that tail was open-ended on Agency; at 3×
 * it is bounded.
 */
export const BANDWIDTH_ABUSE_CEILING_MULTIPLE = 3

/**
 * Absolute floor for the bandwidth ceiling, in PAGE VIEWS per month, so the
 * small plans get real headroom instead of a second, tighter plan limit
 * wearing a different name.
 *
 * Free includes 2 GB ≈ 2,071 views; 3× would be ~6,212, which a genuinely
 * successful hobby site (a post that lands on Hacker News) reaches in an
 * afternoon and would be a miserable first experience of the platform.
 * 100,000 views/month ≈ 96.6 GB ≈ **$62 of real COGS** at the dearest
 * region (`METERED_UNIT_RATES_USD.perPageView` plus
 * `PAGE_VIEW_CDN_REQUEST_COST_USD`) — well above the free band, and an order
 * of magnitude below the $615 a million views costs. It is the number
 * that makes "free stays free" true without making it stingy.
 */
export const BANDWIDTH_ABUSE_CEILING_FLOOR = 100_000

/**
 * Ceiling for an org whose bandwidth band resolves to UNLIMITED — a
 * contracted per-org override, since no plan row carries the sentinel any
 * more — where a multiple has nothing to multiply. Kept at or above the
 * ceiling of every finite band below it so the ladder never inverts and a
 * bigger plan never inherits a smaller ceiling.
 */
export const BANDWIDTH_ABUSE_CEILING_UNLIMITED = 500_000_000

/**
 * Machine-readable marker for a bandwidth containment, so a capped response
 * is distinguishable from the maintenance/lockdown notices it shares a shell
 * with. Deliberately NOT a {@link LockdownReasonCode}: a lockdown is a staff
 * takedown, this is a billing containment that clears itself at the month
 * boundary with nobody doing anything.
 */
export const BANDWIDTH_CEILING_CODE = 'bandwidth_ceiling'

export interface BandwidthAbuseCeilingResult {
  /** True once this month's page views for the SITE reached the ceiling. */
  exceeded: boolean
  /** The ceiling this site's plan resolves to, in page views. Finite. */
  ceiling: number
  used: number
}

/**
 * Per-site monthly bandwidth abuse ceiling (AGL-2155).
 *
 * ## The hole this closes
 *
 * Every other free dimension had a runtime brace — `mediaStorageGate`,
 * `checkFormSubmissionQuota`, a zero band for dataset storage and API, a
 * hard band for contacts. Bandwidth had **none**: nothing anywhere refused a
 * page view, so a free site that went viral served a million views — roughly
 * **$162 of real COGS** — with no wall, no throttle and no alert. It was
 * protected only by the structural zero on the billing side (free carries no
 * rate, so the invoice stays $0), which protects the *bill* and not the
 * *bleeding*. `free-tier-never-billed.spec.ts` said so in its own table.
 *
 * ## Why an abuse ceiling and not a plan gate
 *
 * Exactly the distinction {@link checkFormSubmissionAbuseCeiling} draws, and
 * for the same reason. A plan gate answers "did the customer buy this?" — and
 * for a metered plan the answer past the band is *yes, and we bill it*. This
 * answers "is this still a customer's traffic at all?", and crossing it is an
 * INCIDENT rather than a quota: the site is flagged, staff are told, and the
 * render path degrades to a static notice instead of paying ~40 Firestore
 * reads and ~1,013 KB of egress per view.
 *
 * ## Where it is evaluated
 *
 * NOT in the render path. The page-view counter is written *after* the render
 * by `/api/analytics/collect`; that route evaluates this on a sampled cadence
 * and stamps `bandwidthCeiling` onto the host document. The render path reads
 * that field off a host doc **it already loads** (`load-page-data.ts` reads
 * `hostRes.host` for the lockdown branch), so the containment costs the happy
 * path exactly zero extra reads.
 *
 * An edge/CDN rule would be the other natural home and is deliberately not
 * the answer here: it is not a repo change, nothing in this suite could
 * verify it, and it cannot see which plan a host is on.
 */
export function checkBandwidthAbuseCeiling(
  org: Partial<AglynOrgBilling> | null | undefined,
  usedPageViewsThisMonth: number,
): BandwidthAbuseCeilingResult {
  const includedGb = resolveOrgEntitlements(org).bandwidthGb ?? 0
  const includedViews = pageViewsFromBandwidthGb(includedGb)
  const ceiling = Number.isFinite(includedViews)
    ? Math.max(
        BANDWIDTH_ABUSE_CEILING_FLOOR,
        Math.round(includedViews * BANDWIDTH_ABUSE_CEILING_MULTIPLE),
      )
    : BANDWIDTH_ABUSE_CEILING_UNLIMITED
  const used = Math.max(0, Number(usedPageViewsThisMonth) || 0)
  return { exceeded: used >= ceiling, ceiling, used }
}

/**
 * Does crossing the ceiling **degrade what visitors see**, or only raise the
 * incident? (AGL-2155)
 *
 * Only on a plan that does NOT meter the infra pass-through — which today is
 * free/hobby, where the requirement is that free always actually stays free.
 * On free the traffic is uncompensated, so serving it is a
 * straight loss and the least destructive way to stop the bleeding is to stop
 * paying for the expensive render.
 *
 * On a metered plan the same traffic is *invoiced*, so taking a paying
 * customer's site off the air would trade a bill they agreed to for an outage
 * they did not. Their ceiling still trips, still flags the host and still
 * escalates to staff — it just does not change what a visitor gets. Whether a
 * paying host past its OWN ceiling should also be degraded is a product call
 * and is left open on purpose; flipping it is this one function.
 *
 * Enterprise does not meter either, and is NOT degraded, for the same reason
 * a metered plan is not: its traffic is paid for, by agreement rather than by
 * invoice. Since 2026-09-07 its bandwidth band is a finite fallback (twice
 * Agency's), so without this exclusion the ceiling an agreement never named
 * would have taken a contracted customer's site off the air three times past
 * a default nobody chose for them. It still trips, flags and pages.
 */
export function bandwidthCeilingDegradesRender(
  org: Partial<AglynOrgBilling> | null | undefined,
): boolean {
  return !planMetersInfraOverage(org) && !isCustomPricedPlan(resolvePlan(org))
}

/**
 * `hosts/{id}.bandwidthCeiling` — the flag the beacon stamps and the render
 * path reads. Month-scoped so it **self-clears at the month boundary** with
 * no staff action and no write, the same property `LockdownState.untilMs`
 * has: a site contained in August serves normally on September 1.
 *
 * Admin-SDK-only. It is frozen against client writes in
 * `cloud/firebase-firestore.rules` alongside the `suspendedAt` family, for
 * the identical reason: a site that could clear its own containment does not
 * have one.
 */
export interface HostBandwidthCeiling {
  /** `YYYY-MM` (UTC) the trip belongs to. */
  month: string
  /** The ceiling that was crossed, in page views. */
  ceiling: number
  /** The month count observed when it tripped. */
  used: number
  trippedAtMs: number
  /** False when the plan meters the overage — flagged, not degraded. */
  degraded: boolean
}

/**
 * Read the host document's containment flag, tolerantly. Returns null unless
 * the flag is present, well-formed AND belongs to `month` — an August trip
 * must not silence a September visitor.
 */
export function normalizeHostBandwidthCeiling(
  host: Record<string, any> | null | undefined,
  month: string,
): HostBandwidthCeiling | null {
  const raw = host?.['bandwidthCeiling']
  if (!raw || typeof raw !== 'object') return null
  if (String(raw.month ?? '') !== month) return null
  const ceiling = Number(raw.ceiling ?? 0)
  if (!Number.isFinite(ceiling) || ceiling <= 0) return null
  return {
    month,
    ceiling,
    used: Math.max(0, Number(raw.used ?? 0) || 0),
    trippedAtMs: Math.max(0, Number(raw.trippedAtMs ?? 0) || 0),
    degraded: raw.degraded === true,
  }
}

/**
 * Is the render path required to serve the capped notice instead of the page?
 *
 * Three things must hold: the flag is for THIS month, the trip was recorded
 * as one that degrades, and the org's CURRENT plan still degrades.
 *
 * The stored `degraded` alone cannot answer the last one. It is written once,
 * at trip time, and nothing rewrites it: the beacon stops evaluating a host
 * once it has tripped this month, and a contained site serves no pages to
 * beacon from. So a host that upgraded mid-month stayed on the notice until
 * the 1st, while the owner notice and the visitor notice both promised an
 * upgrade would bring it back. Re-deriving from `org` on every read releases
 * it on the next org-doc TTL with no write, the same asymmetry
 * `bandwidthCapEngaged` keeps for the plan band.
 *
 * Both halves still hold in the other direction: a flag written for a paying
 * host (`degraded: false`) can never take that host down, whatever its plan
 * becomes later. An unknown org (`null`) resolves as unmetered, so a lookup
 * failure keeps the containment rather than lifting it.
 */
export function bandwidthCeilingDegradesHost(
  host: Record<string, any> | null | undefined,
  month: string,
  org: Partial<AglynOrgBilling> | null | undefined,
): boolean {
  return (
    normalizeHostBandwidthCeiling(host, month)?.degraded === true &&
    bandwidthCeilingDegradesRender(org)
  )
}

/** UTC `YYYY-MM`, the key both the beacon and the render path agree on. */
export function bandwidthCeilingMonthKey(now: Date = new Date()): string {
  return now.toISOString().slice(0, 7)
}

/**
 * Visitor-facing copy for a contained site. Says what happened and that it
 * ends, and names nothing about the plan, the count or the owner — this is
 * read by a stranger to the site, exactly like the form ceiling's refusal
 * body (AGL-1666).
 */
export function bandwidthCeilingNotice(): { title: string; body: string } {
  return {
    title: 'This site is temporarily unavailable',
    body: 'It has served more traffic this month than its plan includes. It will be back at the start of next month, or sooner if the owner upgrades.',
  }
}

/**
 * What a quota check costs a caller that intends to ENFORCE it (AGL-2163).
 *
 * `checkDataStorageQuota` and `checkApiRequestQuota` each carry an `allowed`
 * field, and their docblocks promise it refuses — "plans without one (free)
 * hard-block at the included size", "plans without API access have
 * `included: 0` and always block". Nothing read it. Both functions appeared
 * only in `/api/billing/report-usage`, which reads `overageMonthlyUsd` and
 * ignores `allowed`, so the promise was documentation and the free tier's
 * "runtime braces" on those two dimensions did not exist.
 *
 * Enforcing them naively means measuring first, and for dataset storage the
 * measurement is O(datasets) reads — far too much for a per-record write.
 * This makes the cheap answer available: for every plan shipping today the
 * verdict does not depend on the measurement at all, because a plan either
 * has an overage RATE (then it always allows and meters) or a band of zero
 * (then it always refuses). Measuring is required only for the shape a staff
 * `entitlementOverrides` can create — a finite, non-zero band on a plan with
 * no rate — and only then does the caller pay a read.
 *
 * - `'always-blocks'` — refuse now; no measurement, no read.
 * - `'never-blocks'` — proceed; no measurement, no read. **This is every
 *   metered/paid plan**, so enforcement costs a paying customer nothing and
 *   can never refuse them.
 * - `'measure'` — the verdict depends on usage; measure and call the check.
 */
export type QuotaEnforcementShape =
  | 'always-blocks'
  | 'never-blocks'
  | 'measure'

function enforcementShape(
  atZero: boolean,
  atCeiling: boolean,
): QuotaEnforcementShape {
  if (!atZero && !atCeiling) return 'always-blocks'
  if (atZero && atCeiling) return 'never-blocks'
  return 'measure'
}

/**
 * Enforcement shape of {@link checkDataStorageQuota} for this org, before
 * anything has been measured. @see QuotaEnforcementShape
 */
export function dataStorageEnforcementShape(
  org: Partial<AglynOrgBilling> | null | undefined,
): QuotaEnforcementShape {
  return enforcementShape(
    checkDataStorageQuota(org, 0).allowed,
    checkDataStorageQuota(org, Number.MAX_SAFE_INTEGER).allowed,
  )
}

/**
 * Enforcement shape of {@link checkApiRequestQuota} for this org, before
 * anything has been measured. @see QuotaEnforcementShape
 */
export function apiRequestEnforcementShape(
  org: Partial<AglynOrgBilling> | null | undefined,
): QuotaEnforcementShape {
  return enforcementShape(
    checkApiRequestQuota(org, 0).allowed,
    checkApiRequestQuota(org, Number.MAX_SAFE_INTEGER).allowed,
  )
}
